/*
Created-by: XtroEdge AI
Updated-by: XtroEdge AI
Last modified: 2026-11-04T19:30:00+05:00
Intent: The Link-level timer takes whole seconds only; "12.5" and "1e3" are blocked or flagged (QA #340).
A Link level picks where its link opens, in-app browser or external, as the admin portal does (QA #339).
A Link level's timer, URL and instruction also go out as timer_duration / task_url / task_instruction, the names the participant app reads; it showed a 10-second default instead of the client's timer.
A Link level needs a real URL (no "https://.com" or "https://example..com") and both instructions, Arabic included; the invalid-form popup names what is missing.
A receipt level's products take an Arabic name, shown to Arabic participants on Web QR.
Restored the currency and location-mode imports the merge dropped.
A non-integrated campaign, edited copies included, can't keep or submit integrated (webhook) levels.
A receipt level picks its currencies from the campaign countries' (all of them by default), checks its purchase window against the level dates, and can't count spend on listed products once none is left; a renamed product no longer sends its old SKU.
No Treasure Hunt (AR) level is picked or submitted once the cycle's AR campaigns are used (a draft still saves).
With no campaigns left in the plan, neither a new campaign nor a saved draft can be submitted (both still save as drafts).
An open cascader closes when the page scrolls or Esc is pressed; the validation popup names a missing level date.
*/
import { CommonModule, Location, formatDate } from '@angular/common';
import { environment } from 'src/environments/environment';
import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  HostListener,
  inject,
  NgZone,
  OnDestroy,
  QueryList,
  ViewChild,
  ViewChildren,
} from '@angular/core';
import {
  AbstractControl,
  FormArray,
  FormBuilder,
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { PagesService } from '@services/pages.service';
import { SharedService } from '@services/shared.service';
import { DataService } from '@services/contests-data.service';
import { CountryService } from '@services/country.service';
import { AiInsightService } from '@services/ai-insight.service';
import { LUQTA_WEBHOOK_URL } from 'src/app/constants/micro-api.service';
import { NzCascaderComponent } from 'ng-zorro-antd/cascader';
import { NzDatePickerModule } from 'ng-zorro-antd/date-picker';
import { NzModalRef, NzModalService } from 'ng-zorro-antd/modal';
import { NzSelectModule } from 'ng-zorro-antd/select';
import { firstValueFrom, timeout, catchError, throwError, Subscription, Observable, forkJoin, of } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { AuthService } from 'src/app/Auth/AuthService';
import { HasUnsavedChanges } from 'src/app/Auth/unsaved-changes.guard';
import { canEditCampaign, cannotEditMessageKey, isWebQrOnlyApproved, webQrApprovalStatus } from '../contests/campaign-publication';
import { formatPlanLimitMessage } from '../../../utils/plan-limit-message.util';
import { webUrlValidator } from '../../../utils/web-url.util';
import { DEFAULT_LINK_OPEN_MODE, linkTaskPayload, readLinkOpenMode, readLinkTaskInstruction, readLinkTaskTimer, readLinkTaskUrl } from '../../../utils/link-task-fields.util';
import { blockNonDigitKey, blockNonDigitPaste } from '../../../utils/whole-number.util';
import { toKycTier } from '../../../utils/kyc-tier.util';
import { countsTowardArUsage, effectiveArCampaignsUsed } from '../../../utils/ar-usage.util';
import { NgZorroAntdModule } from 'src/app/ng-zorro-antd.module';
import { QRCodeComponent } from 'angularx-qrcode';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { GeoMapPickerComponent } from './geo-map-picker/geo-map-picker.component';
import { IsoDatePickerComponent } from './iso-date-picker.component';
import { Model3dService, ModelRecord } from '@services/model-3d.service';
import { ModelPreviewComponent } from '@shared/model-preview/model-preview.component';
import { VoucherFieldsComponent } from '../vouchers/voucher-fields.component';
import {
  DEFAULT_VOUCHER_CURRENCIES,
  VoucherCurrency,
  mergeVoucherCurrencies,
  parseServerDate,
  startOfToday,
  toDateParam,
  validateVoucherRules,
} from '../vouchers/voucher.util';
import {
  NgxIntlTelInputModule,
  CountryISO,
  SearchCountryField,
} from 'ngx-intl-tel-input';

import { campaignAllowance, hasNoCampaignsLeft, NO_CAMPAIGNS_LEFT_ACKNOWLEDGED, openNoCampaignsLeftModal } from './no-campaigns-left';
import { ageGroupRequired, hasAgeGroup, isSupportedCampaignImage, normalizeAgeSelection, prizeAfterEndDate, wholeNumberValidator, winnersWithinParticipants, noWhitespaceValidator } from './campaign-validation';
import { GiftCardCode, GiftCardCodeStatus, readGiftCardPool } from 'src/app/utils/gift-card-codes.util';
import { ageGroupsFrom } from 'src/app/services/ai-age-groups';
export { noWhitespaceValidator };
import {
  ReceiptRulesFormValue,
  ReceiptRulesPayload,
  buildReceiptRules,
  cleanCurrencies,
  defaultReceiptRulesForm,
  receiptProductCount,
  receiptRulesHaveData,
  receiptRulesToForm,
  receiptRulesValidator,
} from './receipt-rules';
import {
  allCountryCurrencies,
  countryCodeForName,
  currencyForCountry,
  currencyName,
} from 'src/app/utils/country-currency.util';
import { isFixedLocation, pointAreasOf } from '@services/ai-location-mode';

const RECEIPT_MAX_PRODUCTS = 100;

/** Level types only an integrated campaign may use. */
export const INTEGRATED_LEVEL_TYPES = ['luqta_weebhook', 'client_webhook'];

export type ArType = 'scan_find' | 'find_gift' | 'catch_logos';

// Keys the client reads for each mini-game. Only the common keys plus the
// selected ar_type's own keys are sent — a foreign game's keys never ship.
const AR_COMMON_KEYS = [
  'objects_total', 'objects_required', 'duration_sec', 'tap_range_m',
  'model_url', 'model_scale', 'scene_url', 'spawn_min_m', 'spawn_max_m',
] as const;

const AR_TYPE_KEYS: Record<ArType, string[]> = {
  scan_find: ['height_min_m', 'height_max_m'],
  find_gift: [
    'gift_height_m', 'gift_scale', 'move_ms', 'move_gap_ms',
    'move_height_m', 'move_angle_rad', 'move_radius_m', 'spin_rate',
  ],
  catch_logos: [
    'drop_from_m', 'drop_to_m', 'drop_ms', 'fall_scale', 'lanes',
    'drop_overlap', 'respawn_ms', 'lane_jitter_rad', 'lane_jitter_m',
  ],
};

// Per-ar_type defaults. These mirror the client's own fallbacks so the admin
// always shows the value the player would actually get for an omitted field.
const AR_DEFAULTS: Record<ArType, Record<string, any>> = {
  scan_find: {
    objects_total: 7, objects_required: 5, duration_sec: 120, tap_range_m: 3.5,
    model_url: null, model_scale: 0.28, scene_url: null,
    spawn_min_m: 1.5, spawn_max_m: 7,
    height_min_m: 0.9, height_max_m: 1.7,
  },
  find_gift: {
    objects_total: 1, objects_required: 1, duration_sec: 0, tap_range_m: 2.2,
    model_url: null, model_scale: 0.28, scene_url: null,
    spawn_min_m: 2, spawn_max_m: 6,
    gift_height_m: 0.8, gift_scale: 1.6, move_ms: 900, move_gap_ms: 110,
    move_height_m: 1.2, move_angle_rad: 0.9, move_radius_m: 1.6, spin_rate: 0.34,
  },
  catch_logos: {
    objects_total: 14, objects_required: 5, duration_sec: 120, tap_range_m: 5,
    model_url: null, model_scale: 0.28, scene_url: null,
    spawn_min_m: 1, spawn_max_m: 2.6,
    drop_from_m: 3.4, drop_to_m: -0.6, drop_ms: 3200, fall_scale: 0.85, lanes: 5,
    drop_overlap: 0.55, respawn_ms: 900, lane_jitter_rad: 0.35, lane_jitter_m: 0.4,
  },
};

const DEFAULT_AR_TYPE: ArType = 'catch_logos';

// Keys a mini-game doesn't let the admin author. Find the Gift always spawns exactly one
// gift, so its object counts are meaningless to tune — the controls stay in the group (and
// in the payload, at their defaults) so the client contract is unchanged; they're just not
// rendered, and the total/required invariant is skipped for them.
const AR_HIDDEN_KEYS: Partial<Record<ArType, string[]>> = {
  find_gift: ['objects_total', 'objects_required'],
};

// The only ar_config values that may go below zero: a logo falls from above the
// player's head to below the frame, so both heights can legitimately be negative.
const AR_SIGNED_KEYS = ['drop_from_m', 'drop_to_m'];

// Object counts: a game with no objects to find, or none required, can't be played.
// Whole numbers too, since the payload rounds them and 0.4 would go out as 0.
const AR_MIN_ONE_KEYS = ['objects_total', 'objects_required'];

// The prize zone has a hard floor; the admin cannot save anything tighter.
const GEO_MIN_PRIZE_RADIUS_M = 5;

// Only sent when direction.enabled is true.
const GEO_DIRECTION_DEFAULTS = { angle_tolerance: 30, distance_check: 20 };

// Saves upload every prize/level image server-side, so they can legitimately run long.
// A timeout here does not cancel the save, so the message warns against blind retries.
const SAVE_TIMEOUT_MS = 120000;
const SAVE_TIMEOUT_MESSAGE = 'The save is taking longer than expected and may still complete. Check the Campaigns list before trying again.';

// Longest a level name may be, in English and in Arabic.
export const LEVEL_NAME_MAX = 35;

@Component({
  selector: 'app-add-contents',
  standalone: true,
  imports: [
    CommonModule,
    NzSelectModule,
    FormsModule,
    ReactiveFormsModule,
    TranslateModule,
    NgZorroAntdModule,
    NzDatePickerModule,
    QRCodeComponent,
    GeoMapPickerComponent,
    NgxIntlTelInputModule,
    ModelPreviewComponent,
    VoucherFieldsComponent,
    IsoDatePickerComponent,
  ],
  templateUrl: './add-contents.component.html',
  styleUrls: ['./add-content.scss'],
})
export class AddContentsComponent implements HasUnsavedChanges, OnDestroy {
  private readonly leavePageWarningMessage =
    'Are you sure you want to leave this page? Unsaved changes will be lost.';

  ageRanges: string[] = [];
  bannerPreviews: string[] = [];
  originalBannerObjects: any[] = [];
  originalPrizeImageObjects: any[] = [];
  activeBannerIndex: number = 0;
  imagePreview: any[] = [];
  @ViewChildren('qrcode') qrCodeElements!: QueryList<ElementRef>;
  addContentForm: FormGroup;
  sharedService = inject(SharedService);
  countryRecords: any;
  countriesLoaded: boolean = false;
  allCountriesSelected: boolean = false;
  readonly ALL_COUNTRIES_VALUE = '__ALL_COUNTRIES__';
  readonly LEVEL_NAME_MAX = LEVEL_NAME_MAX;
  private langChangeSub?: Subscription;
  public locationOptions: any[] = [];
  public locationCascaderValue: any[] = [];
  // Case-insensitive search for the cascaders (ng-zorro's own filter matches case exactly).
  public cascaderSearchOption = {
    filter: (inputValue: string, path: any[]) => {
      const term = (inputValue || '').trim().toLowerCase();
      return path.some(option => String(option?.label ?? '').toLowerCase().includes(term));
    }
  };
  public interestOptions: any[] = [];
  // The page scrolls inside the dashboard container, which the dropdown overlays don't follow,
  // so an open cascader would float over other fields. It is closed on that scroll instead,
  // and on Esc wherever the focus is.
  @ViewChildren(NzCascaderComponent) private cascaders?: QueryList<NzCascaderComponent>;
  private readonly ngZone = inject(NgZone);
  private readonly closeCascadersOnScroll = (event: Event): void => {
    const target = event.target;
    // Scrolling the menu itself, or the picked tags inside the field, keeps it open.
    if (target instanceof Element && target.closest('.cdk-overlay-container, nz-cascader')) return;
    this.closeOpenCascaders();
  };
  private businessSubjects: any[] = [];
  globalLevelSettings: any = null;
  /** Set once the campaign is saved: from then on the form is only being torn down. */
  private campaignSaved = false;
  /** The open "Change the level type?" prompt. NG-Zorro modals outlive router navigation,
   *  so it is closed explicitly when the form is saved or left. */
  private levelTypeSwitchPrompt: NzModalRef | null = null;
  public interestCascaderValue: any[] = [];
  hasSdkAccess = false;
  hasAiAccess = false;
  hasWebAccess = false;
  hasIntegratedAccess = false;
  integratedCampaignsAllowed = 0;
  maxParticipants = 0;
  remainingPoints = 0;
  hasArAccess = false;
  arUnlimited = false;
  arCampaignsUsed = 0;
  arCampaignsAllowed = 0;
  arUsageResetDate: string | null = null;
  arUsageResetType: 'auto' | 'manual' = 'manual';
  // The campaign being edited already took its AR slot (sent, with an AR level), so keeping or
  // adding AR levels to it uses nothing more.
  campaignAlreadyCountsForAr = false;
  drawTypeOptions = [
    { label: 'Random Draw', value: 'random_draw' },
    { label: 'First To Complete', value: 'first_to_complete' },
    { label: 'No Draw', value: 'no_draw' },
    { label: 'Instant Result', value: 'instant_result' },
  ];
  categoriesList: any[] = [];
  categoriesTree: any[] = [];
  prizeCategoryOptions: any[][] = [];
  categoriesLoaded: boolean = false;
  // Contest Category (distinct taxonomy from prize categories — sourced from /user_interest, same as Admin).
  contestCategoryTree: any[] = [];
  contestCategoryList: any[] = [];
  contestCategoriesLoaded: boolean = false;
  quizzesList: any[] = [];
  quizzesLoaded: boolean = false;
  isVisible = false;
  singleContestData: any;
  isVisiblesubmit = false;
  @ViewChild('profileFileInput')
  profileFileInput!: ElementRef<HTMLInputElement>;
  @ViewChild('prizeFileInput') prizeFileInput!: ElementRef<HTMLInputElement>;
  timelineDays: number = 1;
  progressWidth: number = 14.3;
  modalForm!: FormGroup;
  isEditMode: boolean = false;
  private editDetailsLoaded = false;
  isDuplicateMode: boolean = false;
  contestID: any;
  levelQrCodes: (string | null)[] = [];
  levelQrCodesOriginal: (string | null)[] = []; // Track original QR data to detect changes
  levelImagePreviews: (string | ArrayBuffer | null)[] = [];
  // Only codes the backend's currencies table holds: EUR and GBP were offered
  // here and every save with them failed with CURRENCY_NOT_SUPPORTED.
  // Note: OMR uses 'ر.ع' (without trailing dot) or unicode bidi isolation so
  // browser bidi engines render 'ر.ع OMR' correctly rather than reversing to 'ع.ر. OMR'.
  currencies = [
    { code: 'USD', symbol: '$' },
    { code: 'SAR', symbol: 'ر.س' },
    { code: 'AED', symbol: 'د.إ' },
    { code: 'QAR', symbol: 'ر.ق' },
    { code: 'KWD', symbol: 'د.ك' },
    { code: 'BHD', symbol: 'د.ب' },
    { code: 'OMR', symbol: 'ر.ع' },
    { code: 'EGP', symbol: 'E£' },
    { code: 'JOD', symbol: 'د.ا' },
  ];
  isAccessCodeModalVisible = false;
  accessCode: string | null = null;
  isCodeCopied = false;
  isImagePreviewVisible = false;
  imagePreviewUrl = '';
  imagePreviewTitle = '';
  aiRegeneratingBanner = false;
  isAiRegenModalVisible = false;
  aiRegenPrompt = '';
  aiRegenPreviewUrl = '';
  aiRegenContext: 'banner' | 'prize' | 'level' = 'banner';
  aiRegenTargetIndex: number = 0;
  isQrVisible = false;
  /** Which level's QR modal is open. The modal template sits inside the levels
   *  *ngFor, so without this every level renders its own instance bound to the
   *  same isQrVisible flag — they all open at once and the last one (usually a
   *  non-QR level, so blank) ends up on top. */
  qrModalIndex: number | null = null;
  isImageVisible: boolean[] = []; // Array to track image modal visibility for each level
  isCustomInput: boolean = false;
  isCustomParticipants: boolean = false;
  isSavingDraft: boolean = false;
  showToAdmin: boolean = false;
  contestLimitReached: boolean = false;
  private contestLimitWarningShown = false;
  isSubmitting: boolean = false; // Guards against double-submit creating duplicate contests
  private contestAllowed: number = 0;
  private contestCreated: number = 0;
  currentStep: number = 1; // Stepper current step
  selectedLevelCount: number | null = null; // Selected level count from buttons
  customLevelCount: number | null = null; // Custom level count from input
  clientLogo: string = 'assets/images/luqta_favicon2.png'; // Client logo for preview
  contestTimeLeft: string = '0d 0h 0m 0s';
  private contestCountdownIntervalId: ReturnType<typeof setInterval> | null =
    null;
  private businessObjectiveObserver?: MutationObserver;
  private levelDateOrderValidator: ValidatorFn = (
    control: AbstractControl
  ): ValidationErrors | null => {
    const errors: ValidationErrors = {};

    const start = control.get('level_start_date')?.value;
    const expiry = control.get('level_timeline')?.value;
    if (start && expiry) {
      const startTime = new Date(start).getTime();
      const expiryTime = new Date(expiry).getTime();
      if (startTime >= expiryTime) {
        errors['levelDateOrder'] = true;
      }
    }

    const type = control.get('type')?.value;
    const clientVariables = control.get('client_variables') as FormArray | null;
    if (type === 'client_webhook') {
      if (!clientVariables || clientVariables.length === 0) {
        errors['clientVariablesRequired'] = true;
      } else {
        const hasIncompleteVariable = clientVariables.controls.some((variableCtrl) => {
          const variableGroup = variableCtrl as FormGroup;
          const variableName = String(variableGroup.get('variable_name')?.value ?? '').trim();
          const displayName = String(variableGroup.get('display_name')?.value ?? '').trim();
          const dataType = String(variableGroup.get('datatype')?.value ?? '').trim();
          return !variableName || !displayName || !dataType;
        });

        if (hasIncompleteVariable) {
          errors['clientVariablesIncomplete'] = true;
        }
      }
    }

    if (type === 'survey') {
      const minAnswers = Number(control.get('min_answers_required')?.value) || 0;
      const questions = control.get('survey_questions') as FormArray | null;
      if (questions && minAnswers > questions.length) {
        errors['minAnswersExceedsQuestions'] = true;
      }
      const requiredCount = questions
        ? questions.controls.filter(q => q.get('is_required')?.value === true).length
        : 0;
      if (minAnswers < requiredCount) {
        errors['minAnswersBelowRequired'] = requiredCount;
      }
    }

    return Object.keys(errors).length ? errors : null;
  };

  get isArabic(): boolean {
    return (localStorage.getItem('lang') || this.translate?.currentLang) === 'ar';
  }

  trimControl(controlName: string): void {
    const ctrl = this.addContentForm.get(controlName);
    if (ctrl && typeof ctrl.value === 'string') {
      ctrl.setValue(ctrl.value.trim());
    }
  }

  trimPrizeName(index: number, field: 'name' | 'arabic_name' = 'name'): void {
    const ctrl = this.prizeArray.at(index)?.get(field);
    if (ctrl && typeof ctrl.value === 'string') {
      ctrl.setValue(ctrl.value.trim());
    }
  }

  ngOnInit() {
    this.ngZone.runOutsideAngular(() =>
      document.addEventListener('scroll', this.closeCascadersOnScroll, { capture: true, passive: true }));
    this.langChangeSub = this.translate.onLangChange.subscribe(() => {
      if (this.countryRecords) {
        this.locationOptions = this.buildLocationOptions(this.countryRecords);
        this.syncLocationCascaderFromForm();
      }
    });

    // Clear any stuck loader from previous pages
    this.sharedService.hideLoader();

    // Populate the AR "Model URL" picker with the account's uploaded 3D models.
    this.loadModelOptions();

    // Get client logo from localStorage
    const storedImage = localStorage.getItem('profileImage');
    if (storedImage && storedImage !== 'assets/images/profile.png') {
      this.clientLogo = storedImage;
    }

    this.isSavingDraft = false;
    void this.initializeAllowListCountry();

    // Contest Category options are fetched up front so the cascader is populated
    // before the user opens it (it used to only load on the first dropdown open).
    this.getContestCategoryRecords();

    // Check if user has SDK features access and contest limit
    this.authService.getAll('PURCHASED_PLAN').subscribe({
      next: (response: any) => {
        this.hasSdkAccess = response?.sdk_features === true;
        this.hasAiAccess = response?.ai_attributes === true;
        this.hasWebAccess = (response?.sub_plan?.web_access ?? response?.web_access) === true;
        this.maxParticipants = Number(response?.sub_plan?.Participants ?? response?.Participants ?? 0);
        this.arUnlimited = (response?.sub_plan?.ar_unlimited ?? response?.ar_unlimited) === true;
        // ar_unlimited implies access even if ar_access is missing/false on the response.
        this.hasArAccess = (response?.sub_plan?.ar_access ?? response?.ar_access) === true || this.arUnlimited;
        this.arCampaignsUsed = response?.ar_campaigns_used ?? 0;
        this.arCampaignsAllowed = response?.ar_campaigns_allowed ?? 0;
        this.arUsageResetDate = response?.ar_usage_reset_date ?? null;
        this.arUsageResetType = response?.ar_usage_reset_type === 'auto' ? 'auto' : 'manual';
        // The plan's count misses AR campaigns the backend hasn't counted yet (e.g. awaiting review).
        if (this.hasArAccess && !this.arUnlimited && this.arCampaignsAllowed > 0) {
          void effectiveArCampaignsUsed(this.pagesService, response, true).then(used => {
            this.arCampaignsUsed = used;
          });
        }
        if (this.hasAdvancedFields) {
          this.addContentForm.get('draw_type')?.setValidators([Validators.required]);
          this.updateNoOfUsersValidators();
          this.addContentForm.get('draw_type')?.updateValueAndValidity();
        }
        this.syncWinnersControl();

        // Store plan limits and check
        ({ allowed: this.contestAllowed, created: this.contestCreated } = campaignAllowance(response));

        // Check integrated campaign allowance
        const integratedAllowed = Number(
          response?.integrated_contests_allowed ??
          response?.sub_plan?.Integrated_Contests ??
          response?.Integrated_Contests ??
          response?.integrated_contests ??
          0
        );
        this.integratedCampaignsAllowed = integratedAllowed;
        this.hasIntegratedAccess = integratedAllowed > 0;

        // Integrated_Contests is the maximum number of integrated campaigns inside the
        // total Contest_allowed, not a share reserved for them. A non-integrated campaign
        // may use any remaining slot, so the old "total − integrated" cap (100 − 100 = 0)
        // that forced the format to Integrated is gone; the total limit is checked below.
        this.checkContestLimit();

        // Points remaining validation
        this.remainingPoints = response?.remaining_points ?? 0;
        if (this.remainingPoints > 0) {
          this.addContentForm.get('points_pool_per_contest')?.setValidators([Validators.min(0), Validators.max(this.remainingPoints)]);
          this.addContentForm.get('points_pool_per_contest')?.updateValueAndValidity();
        }

        // When points_pool changes, update no_of_users max validator
        this.addContentForm.get('points_pool_per_contest')?.valueChanges.subscribe(() => {
          this.updateNoOfUsersValidators();
        });
      },
      // No plan: the plan-limited features stay off (their defaults); SubscriptionGuard handles routing.
      error: () => {},
    });

    this.subscribeToPointsChanges();
    // this.addContentForm.get('totallevels')?.valueChanges.subscribe((value) => {
    //   this.updateLevels(value);
    // });

    this.subscribeToContestDateChanges();
    this.startContestCountdown();

    const contestData = history.state.contest;
    const aiContestData = history.state.aiContestData;
    const aiGeneratedAssets = history.state.aiGeneratedAssets;

    // Only add default level if NOT loading AI data
    if (this.levels.length === 0 && !aiContestData) {
      this.addNewLevel();
    }
    this.prizeArray.push(this.createPrize());
    this.prizeCategoryOptions.push(this.cloneCategoriesTree());


    const encodedUid = this.route.snapshot.queryParamMap.get('id');
    const duplicateUid = this.route.snapshot.queryParamMap.get('duplicateId');

    if (encodedUid) {
      const storedId = localStorage.getItem(encodedUid);
      if (storedId) {
        // NOT Number(): a contest id is a uuid, and Number(uuid) is NaN.
        this.contestID = String(storedId);
        this.isEditMode = true;
        this.isDuplicateMode = false;
        this.getContestDetails(this.contestID);
      }
    } else if (duplicateUid) {
      const storedId = localStorage.getItem(duplicateUid) || duplicateUid;
      if (storedId) {
        this.contestID = 0;
        this.isEditMode = false;
        this.isDuplicateMode = true;
        this.getContestDetails(String(storedId), true);
      }
    }

    if (!this.isEditMode && aiContestData) {
      // Load countries, categories and business subjects first, then apply AI data.
      // The contest-category list too: it was left to the eager load, so the AI
      // path could run before it arrived and the Category cascader came up empty.
      Promise.all([
        this.getCountryRecords(),
        this.loadCategoriesAsync(),
        this.loadContestCategoriesAsync(),
        this.loadBusinessSubjectsAsync()
      ]).then(() => {
        this.applyAiContestData(aiContestData, aiGeneratedAssets);
      });
    }


    this.ageRanges = ['All'];
    [
      { start: 18, end: 25 },
      { start: 26, end: 35 },
      { start: 36, end: 45 },
      { start: 46, end: 55 },
      { start: 56, end: 65 },
    ].forEach((r) => this.ageRanges.push(`${r.start}-${r.end}`));
    this.ageRanges.push('65+');

    this.addContentForm.get('format_of_contest')?.valueChanges.subscribe(value => {
      this.onFormatChange(value);
    });

    this.loadBusinessSubjects();
    this.loadGlobalLevelSettings();
  }

  loadGlobalLevelSettings(): void {
    this.pagesService.getAll('GLOBAL_LEVEL_SETTINGS').subscribe({
      next: (res: any) => {
        this.globalLevelSettings = res;
        // Levels (a default new one, or ones rebuilt from getContestDetails on edit) can
        // already exist by the time this resolves — reconcile them now so none are left
        // validated against the pre-settings fallback numbers.
        this.reconcileLevelsWithGlobalSettings();
      },
      error: (err: any) => {
        console.error('Failed to load global level settings:', err);
      }
    });
  }

  loadBusinessSubjects(): void {
    this.pagesService.getAll('GET_BUSINESS_SUBJECTS').subscribe({
      next: (res: any) => {
        const items = res?.items || res || [];
        this.businessSubjects = items;
        this.interestOptions = this.buildCascaderTree(items);
      },
      error: () => { }
    });
  }

  loadBusinessSubjectsAsync(): Promise<void> {
    return new Promise((resolve) => {
      this.pagesService.getAll('GET_BUSINESS_SUBJECTS').subscribe({
        next: (res: any) => {
          const items = res?.items || res || [];
          this.businessSubjects = items;
          this.interestOptions = this.buildCascaderTree(items);
          resolve();
        },
        error: () => resolve()
      });
    });
  }

  onBusinessObjectiveVisibleChange(visible: boolean): void {
    if (!visible) {
      this.businessObjectiveObserver?.disconnect();
      this.businessObjectiveObserver = undefined;
      return;
    }

    setTimeout(() => {
      const dropdown = document.querySelector('.ant-cascader-menus') as HTMLElement | null;
      if (!dropdown) return;

      this.applyBusinessObjectiveTitles(dropdown);

      this.businessObjectiveObserver?.disconnect();
      this.businessObjectiveObserver = new MutationObserver(() => {
        this.applyBusinessObjectiveTitles(dropdown);
      });

      this.businessObjectiveObserver.observe(dropdown, {
        childList: true,
        subtree: true,
      });
    }, 0);
  }

  private buildCascaderTree(items: any[]): any[] {
    const activeItems = items.filter((item: any) => item.is_active);
    const map = new Map<number, any>();

    activeItems.forEach((item: any) => {
      map.set(item.id, {
        value: item.name,
        label: item.name,
        description: item.description || '',
        children: []
      });
    });

    const tree: any[] = [];
    activeItems.forEach((item: any) => {
      const node = map.get(item.id);
      if (item.parent_id && item.parent_id !== 0 && map.has(item.parent_id)) {
        map.get(item.parent_id).children.push(node);
      } else {
        tree.push(node);
      }
    });

    // Mark leaves
    const markLeaves = (nodes: any[]) => {
      nodes.forEach((node: any) => {
        if (node.children.length === 0) {
          node.isLeaf = true;
          delete node.children;
        } else {
          markLeaves(node.children);
        }
      });
    };
    markLeaves(tree);

    return tree;
  }

  private applyBusinessObjectiveTitles(dropdown: HTMLElement): void {
    const optionNodes = dropdown.querySelectorAll(
      '.ant-cascader-menu-item, .ant-cascader-menu-item-content'
    );

    optionNodes.forEach((node) => {
      const label = node.textContent?.trim();
      if (!label) return;

      const description = this.getBusinessObjectiveDescription(label);
      if (!description) return;

      (node as HTMLElement).setAttribute('title', description);
    });
  }

  private getBusinessObjectiveDescription(label: string): string {
    const normalizedLabel = this.normalizeBusinessObjectiveText(label);
    const match = this.businessSubjects.find(
      (subject) =>
        this.normalizeBusinessObjectiveText(subject.name) === normalizedLabel
    );

    return match?.description?.trim() || '';
  }

  private normalizeBusinessObjectiveText(value: string): string {
    return String(value || '')
      .toLowerCase()
      .replace(/\s+/g, ' ')
      .replace(/[^\w\s-]/g, '')
      .trim();
  }

  private applyAiContestData(rawData: any, assets?: { banner?: string; prizes?: string[]; levels?: string[] }): void {
    const data = rawData?.data && typeof rawData.data === 'object' ? rawData.data : rawData;
    if (!data || typeof data !== 'object') {
      return;
    }

    const name = String(data.name ?? data.title ?? '').trim();
    const details = String(data.details ?? data.description ?? data.desc ?? '').trim().slice(0, 1000);
    const arabicName = String(data.arabic_name ?? name).trim();
    const arabicDetails = String(data.arabic_details ?? details).trim().slice(0, 1000);

    const location = this.normalizeAiLocation(data.location ?? data.country);
    const age = this.normalizeAiAge(data.age);

    // Parse dates and auto-fix if they're in the past
    const now = new Date();
    let startDate = this.parseAiDate(data.start_date);
    let endDate = this.parseAiDate(data.end_date);
    let prizeDate = this.parseAiDate(data.prize_announce_date);

    // A start already behind us moves to a few minutes from now — never days
    // out. "1 to 30 November", agreed on the 1st, used to open as 4 November to
    // 4 December: the start had passed by the time the form loaded, and the
    // whole window was pushed three days and stretched (client sheet rows 198,
    // 220). The end the client agreed is kept while it is still ahead; only a
    // window wholly in the past keeps its length instead.
    if (startDate && startDate.getTime() < now.getTime()) {
      const dayMs = 24 * 60 * 60 * 1000;
      const soon = new Date(Math.ceil((now.getTime() + 15 * 60 * 1000) / 60000) * 60000);
      const originalDuration = (endDate && startDate) ? endDate.getTime() - startDate.getTime() : 14 * dayMs;
      const originalPrizeGap = (prizeDate && endDate) ? prizeDate.getTime() - endDate.getTime() : 2 * dayMs;
      startDate = soon;
      if (!endDate || endDate.getTime() <= soon.getTime() + 60 * 60 * 1000) {
        endDate = new Date(soon.getTime() + Math.max(originalDuration, dayMs));
        prizeDate = new Date(endDate.getTime() + Math.max(originalPrizeGap, dayMs));
      }
    }
    if (endDate && prizeDate && prizeDate.getTime() <= endDate.getTime()) {
      prizeDate = new Date(endDate.getTime() + 24 * 60 * 60 * 1000);
    }

    // Normalize gender value
    const gender = ['male', 'female', 'both'].includes(String(data.gender).toLowerCase())
      ? String(data.gender).toLowerCase()
      : 'both';

    // Normalize type value
    const type = ['public', 'private'].includes(String(data.type).toLowerCase())
      ? String(data.type).toLowerCase()
      : (data.contest_type ?? data.contestType ?? '');

    // Normalize KYC tier — Heavy/High is Phase 2 and not selectable yet
    const kycTier = toKycTier(data.kyc_tier ?? data.kycTier) === 'medium' ? 'medium' : 'light';

    // Normalize sharing setting. `share` is the old spelling of `shared`; it is
    // accepted on the way in and rewritten, so nothing keeps saving it.
    const rawSharing = String(data.sharingSetting ?? data.sharing_setting ?? '').trim().toLowerCase();
    const sharingSetting = rawSharing === 'single' ? 'single' : 'shared';

    // Normalize format
    const format = ['integrated', 'non-integrated'].includes(data.format_of_contest ?? data.format ?? '')
      ? (data.format_of_contest ?? data.format)
      : 'non-integrated';

    // Normalize number of winners
    const numberOfWinners = Math.max(1, Number(data.number_of_winners ?? data.winners ?? data.winner_count) || 1);

    //  draw_type / no_of_users / category_id were not being bound at all.
    //
    //  draw_type and no_of_users both become Validators.required once the plan has
    //  SDK features, so the client had to re-pick values the AI already chose and
    //  the form sat invalid until they did. category_id drives the Contest Category
    //  cascader and the contest_category the save sends — unbound, it stayed empty
    //  and saved as null.
    //
    //  Accepts every value the draw-type selector renders, so a generated choice
    //  binds instead of the client re-picking it.
    //
    const drawType = ['random_draw', 'first_to_complete', 'no_draw', 'instant_result']
      .includes(String(data.draw_type ?? ''))
      ? String(data.draw_type)
      : '';

    // Respect the plan cap the same way the save path does.
    const rawUsers = Number(data.no_of_users);
    const noOfUsers = Number.isFinite(rawUsers) && rawUsers > 0 ? Math.round(rawUsers) : '';

    //  Contest Category is the /user_interest taxonomy, NOT the prize categories
    //  list — they are separate trees and an id from the wrong one silently leaves
    //  the cascader empty. buildContestCategoryPath walks contestCategoryList; the
    //  control holds a single path and the save takes its last element as the leaf.
    //
    //  The category and objective the client agreed in the chat arrive as words
    //  (`agreed_contest_category`, `agreed_business_objective`) and win over the
    //  generator's ids: it picks an id from the list by guesswork, and a chat
    //  that settled on "brand awareness" opened with a junk test subject instead.
    //
    //  A category agreed in the chat that is not in the list is left empty and
    //  the client is told, rather than quietly swapped for the generator's guess
    //  — the form used to open as if the category they named existed (client
    //  sheet row 245).
    const agreedCategoryName = String(data.agreed_contest_category ?? '').trim();
    const agreedCategory = this.findTaxonomyByName(this.contestCategoryList, agreedCategoryName);
    const categoryMissing = !!agreedCategoryName && !agreedCategory && this.contestCategoryList.length > 0;
    if (categoryMissing) {
      this.sharedService.createNotification('warning',
        this.translate.instant('ai_category.missing', { name: agreedCategoryName }));
    }
    const rawContestCategory = categoryMissing ? null : (agreedCategory?.id ?? data.category_id);
    const contestCategoryPath = rawContestCategory !== null && rawContestCategory !== undefined && rawContestCategory !== ''
      ? this.buildContestCategoryPath(rawContestCategory)
      : [];

    const agreedObjectives = String(data.agreed_business_objective ?? '')
      .split(/\s*[,،;]\s*/)
      .map(n => this.findTaxonomyByName(this.businessSubjects, n)?.name)
      .filter(Boolean);

    // Normalize business_objective — AI returns array of IDs e.g. [7], map to names
    const businessObjective = agreedObjectives.length
      ? [...new Set(agreedObjectives)]
      : Array.isArray(data.business_objective)
        ? data.business_objective.map((id: any) => {
          const found = this.businessSubjects.find((s: any) => String(s.id) === String(id));
          return found ? found.name : null;
        }).filter(Boolean)
        : [];

    this.addContentForm.patchValue({
      name,
      arabic_name: arabicName,
      details,
      arabic_details: arabicDetails,
      gender,
      age,
      location,
      number_of_winners: numberOfWinners,
      type,
      kyc_tier: kycTier,
      sharingSetting,
      format_of_contest: format,
      start_date: startDate ?? null,
      end_date: endDate ?? null,
      prize_announce_date: prizeDate ?? null,
      business_objective: businessObjective,
      draw_type: drawType,
      // Only meaningful on Instant-Result, so it is gated the same way the save
      // path gates it rather than trusting whatever the generator returned.
      all_completers: drawType === 'instant_result' && data.all_completers === true,
      no_of_users: noOfUsers,
      category_id: contestCategoryPath,
    });
  
    // The toggle decides whether Number of Winners is required, so the control
    // has to be reconciled after any patch that could have changed either one.
    this.syncWinnersControl();

    // points_pool_per_contest is deliberately NOT patched — Luqta allocates the
    // points budget at review time, so the generator always returns 0 and the
    // field is left for the client to fill in.

    // Sync cascaders after patching form
    setTimeout(() => {
      this.syncLocationCascaderFromForm();
      this.syncInterestCascaderFromForm();
    }, 100);

    this.applyAiBanners(assets?.banner ?? data.banner ?? data.banner_url ?? '');

    // Extract category_id from contest level and apply to prizes
    // A uuid category must stay a string (Number(uuid) is NaN).
    const contestCategoryId = typeof data.category_id === 'number' ? data.category_id : (Number(data.category_id) || (data.category_id ? String(data.category_id) : null));

    // Ensure at least 1 prize — create a default if AI didn't provide any
    let prizes = data._contest_prize ?? data.prizes ?? data.prize ?? [];
    if (!Array.isArray(prizes) || prizes.length === 0) {
      prizes = [{
        name: name || 'Grand Prize',
        value: 0,
        description: details || '',
        categories: contestCategoryId ? [contestCategoryId] : []
      }];
    }

    this.applyAiPrizes(prizes, assets?.prizes ?? [], contestCategoryId);

    // Use whatever levels AI provided — only add 1 default if AI returned nothing
    let levels = data._levels_of_contest ?? data.levels ?? [];
    if (!Array.isArray(levels)) levels = [];
    if (levels.length === 0) {
      // Said out loud: the draft came back with no levels and the form put one
      // in, which looked like the AI's own choice (client sheet row 259).
      this.sharedService.createNotification('warning', this.translate.instant('ai_levels.default-added'));
      levels = [{
        level_type: 'text',
        name: 'Share Your Thoughts',
        arabic_name: 'شاركنا رأيك',
        description: 'Tell us what excites you about this campaign',
        arabic_description: 'أخبرنا ما الذي يثيرك في هذا التفاعل',
        level_start_date: data.start_date ?? null,
        level_timeline: data.end_date ?? null,
        textContent: 'Share your thoughts...',
        qrData: '',
        accept_any_answer: true,
      }];
    }

    this.applyAiLevels(levels, assets?.levels ?? [], startDate, endDate);

    // A quiz level arrives without a quiz, and the field is required — so the
    // questions are written and saved here rather than sending the client off to
    // the Quiz page in the middle of a campaign they were just walked through.
    const quizLevels = levels
      .map((level: any, index: number) => ({ level, index }))
      .filter(({ level }: any) => String(level?.level_type ?? level?.type).toLowerCase() === 'quiz');
    if (quizLevels.length) {
      if (!this.quizzesLoaded) {
        this.getQuizzesRecords();
      }
      const pending = quizLevels.filter(({ level }: any) => !level?.quizzes_id);
      if (pending.length) this.autoCreateQuizzes(pending, data);
    }
  }

  /**
   * Writes, saves and selects a quiz for each quiz level that has none.
   *
   * The questions come from the level and from what the client said about the
   * quiz in the chat (`quiz_brief` — a topic, or questions they dictated). Each
   * is saved as one of the company's own quizzes, published so the dropdown
   * lists it, and stays editable on the Quiz page like any other. A level whose
   * quiz could not be written is left empty and named, so the client knows
   * exactly which one to pick by hand.
   */
  private async autoCreateQuizzes(pending: { level: any; index: number }[], contest: any): Promise<void> {
    this.sharedService.createNotification('info', this.translate.instant('quiz_auto.writing', { count: pending.length }));

    const created: string[] = [];
    const failed: string[] = [];
    for (const { level, index } of pending) {
      const label = String(level?.name || `Level ${index + 1}`);
      try {
        const brief = String(level?.quiz_brief ?? '');
        // The count the chat agreed, else one written in the brief ("a
        // 3-question quiz", "one question", "سؤال واحد"). The endpoint reads the
        // brief too, but the count is a field of its own and the two must agree.
        const asked = this.askedQuestionCount(level?.quiz_question_count, brief);
        const quiz = await firstValueFrom(this.aiService.generateQuiz({
          level_name: label,
          level_description: String(level?.description ?? ''),
          campaign_name: String(contest?.name ?? ''),
          campaign_details: String(contest?.details ?? ''),
          business_type: String(contest?.business_type ?? ''),
          language: 'English',
          question_count: asked ?? 4,
          quiz_brief: brief,
        }).pipe(timeout(120000)));

        // The writer has added questions to a short quiz before; the client's
        // number is the number.
        if (asked && Array.isArray(quiz?.questions) && quiz.questions.length > asked) {
          quiz.questions = quiz.questions.slice(0, asked);
        }
        const payload = this.quizPayloadFrom(quiz, label);
        if (!payload) throw new Error('the generated quiz was incomplete');

        const saved: any = await firstValueFrom(this.pagesService.create('QUIZ_UPSERT', payload));
        const id = saved?.id ?? saved?.data?.id;
        if (!id) throw new Error('the quiz was not saved');

        // Listed straight away, so the select shows the title rather than an id.
        this.quizzesList = [
          ...this.quizzesList.filter((q: any) => String(q?.id) !== String(id)),
          { id, quiz_title: payload.quiz_title },
        ];
        const control = this.levels.at(index)?.get('quizzes_id');
        control?.setValue(id);
        control?.markAsDirty();
        created.push(label);
      } catch (err: any) {
        console.error(`[AI quiz] "${label}":`, err?.message ?? err);
        failed.push(label);
      }
    }

    this.cdr.detectChanges();
    if (created.length) {
      this.sharedService.createNotification('success',
        this.translate.instant('quiz_auto.created', { levels: created.join(', ') }));
    }
    if (failed.length) {
      this.sharedService.createNotification('warning',
        this.translate.instant('quiz_auto.failed', { levels: failed.join(', ') }));
    }
  }

  /**
   * How many questions the client asked for: the count the chat agreed, else
   * one written in their quiz brief, else null for the writer's default.
   * Only "\d question(s)" was read before, so "one question" came back as four.
   */
  private askedQuestionCount(agreed: any, brief: string): number | null {
    const n = Number(agreed);
    if (Number.isInteger(n) && n >= 1 && n <= 20) return n;
    const text = String(brief ?? '').replace(/[٠-٩]/g, d => String(d.charCodeAt(0) - 0x0660));
    const WORDS: Record<string, number> = {
      one: 1, single: 1, two: 2, three: 3, four: 4, five: 5,
      six: 6, seven: 7, eight: 8, nine: 9, ten: 10,
    };
    const en = /\b(\d{1,2}|one|single|two|three|four|five|six|seven|eight|nine|ten)[\s-]+(?:quiz\s+)?questions?\b/i.exec(text);
    const found = en ? (WORDS[en[1].toLowerCase()] ?? parseInt(en[1], 10))
      : /سؤال(?:اً|ا)?\s+واحد/.test(text) ? 1
      : /سؤالين|سؤالان/.test(text) ? 2
      : parseInt(/(\d{1,2})\s*(?:أسئلة|اسئلة|سؤال)/.exec(text)?.[1] ?? '', 10);
    return Number.isInteger(found) && found >= 1 && found <= 20 ? found : null;
  }

  /**
   * The generated quiz in the shape QUIZ_UPSERT accepts, or null when it would be
   * refused anyway — every question needs text, two options with text, and a
   * correct one. Only the known fields are sent.
   */
  private quizPayloadFrom(quiz: any, fallbackTitle: string): any | null {
    const text = (v: any) => (typeof v === 'string' ? v.trim() : '');
    const questions = (Array.isArray(quiz?.questions) ? quiz.questions : [])
      .map((q: any, i: number) => ({
        question: text(q?.question),
        arabic_question: text(q?.arabic_question),
        question_order: i + 1,
        options: (Array.isArray(q?.options) ? q.options : [])
          .map((o: any) => ({
            option_text: text(o?.option_text),
            arabic_option_text: text(o?.arabic_option_text),
            is_correct: o?.is_correct === true,
          }))
          .filter((o: any) => o.option_text),
      }));

    const usable = questions.length > 0 && questions.every((q: any) =>
      q.question && q.options.length >= 2 && q.options.some((o: any) => o.is_correct));
    if (!usable) return null;

    return {
      quiz_title: (text(quiz?.quiz_title) || fallbackTitle).slice(0, 100),
      description: text(quiz?.description),
      is_active: true,
      questions,
    };
  }

  private applyAiBanners(banner: string): void {
    if (!banner) return;
    this.bannerPreviews = [banner];
    const bannersArray = this.addContentForm.get('banners_code') as FormArray;
    bannersArray.clear();
    bannersArray.push(this.fb.control(banner));
    this.activeBannerIndex = 0;
  }

  private applyAiPrizes(prizesRaw: any[], prizeImages: string[], contestCategoryId: number | string | null = null): void {
    const prizes = Array.isArray(prizesRaw) ? prizesRaw : [];
    this.prizes.clear();
    this.prizeCategoryOptions = [];
    this.imagePreview = [];

    if (prizes.length === 0) {
      this.prizeArray.push(this.createPrize());
      this.prizeCategoryOptions.push(this.cloneCategoriesTree());
      return;
    }

    prizes.forEach((prize, index) => {
      const imageCode = prize?.image?.path || prize?.image?.url || prizeImages[index] || '';

      // Priority: prize.categories > contestCategoryId > empty
      let categoryValue: number[][] = [];
      if (prize?.categories && Array.isArray(prize.categories) && prize.categories.length > 0) {
        // Use prize-level categories if present
        categoryValue = this.categoryIdsToCascaderValue(prize.categories);
      } else if (typeof contestCategoryId === 'number' && contestCategoryId) {
        // Otherwise, use contest-level category_id (a uuid contest category is not a prize category)
        categoryValue = this.categoryIdsToCascaderValue([contestCategoryId]);
      }

      //  Built from createPrize() rather than assembled here, so a generated
      //  prize has the same controls as a hand-added one.
      // 
      //  It did not before, and the fulfilment fields added for vouchers were
      //  simply absent on this path — which rendered the prize-pool block in a
      //  half-initialised state as soon as anyone used Create with AI.
      const prizeGroup = this.createPrize();
      const arPrizeName = prize?.arabic_prize_name || prize?.arabic_name || '';
      prizeGroup.patchValue({
        name: prize?.name || `Prize ${index + 1}`,
        arabic_name: arPrizeName,
        value: prize?.value ?? prize?.amount ?? '',
        currency: prize?.currency ?? prize?.prize_currency ?? 'USD',
        image_code: imageCode,
        category_type: categoryValue,
        id: prize?.id ?? null,
      });

      this.prizes.push(prizeGroup);
      this.prizeCategoryOptions.push(this.cloneCategoriesTree());
      this.imagePreview[index] = { image_code: imageCode };
    });
  }

  private applyAiLevels(levelsRaw: any[], levelImages: string[] = [], contestStart?: Date | null, contestEnd?: Date | null): void {
    const levels = Array.isArray(levelsRaw) ? levelsRaw : [];
    if (levels.length === 0) return;

    // Must list every type the level-type dropdown offers, or an AI-suggested
    // level of a missing type silently degrades to 'text' below.
    // 'receipt' is deliberately NOT listed: AI generation never produces receipt
    // levels (they need shop/product rules only the business owner can set), so a
    // generated 'receipt' falls back to 'text' like any unknown type.
    const validTypes = [
      'text', 'image', 'qr', 'quiz', 'survey', 'link_task',
      'client_qr_completion',
      'luqta_weebhook', 'client_webhook', 'geolocation',
    ];
    const now = new Date();
    const unsupported: string[] = [];

    const normalizedLevels = levels.map((level: any, index: number) => {
      const levelName = level?.name || `Level ${index + 1}`;
      const levelDescription = String(level?.description ?? level?.details ?? '').slice(0, 1000);
      const arabicDescription = String(level?.arabic_description ?? levelDescription).slice(0, 1000);

      // Normalize level type
      let levelType = String(level?.level_type ?? level?.type ?? 'text').toLowerCase();
      if (['client_qr_completion', 'client qr completion', 'clientqrcompletion'].includes(levelType)) {
        levelType = 'client_qr_completion';
      } else if (!validTypes.includes(levelType)) {
        unsupported.push(levelName);
        levelType = 'text';
      }

      // Use AI-generated image if available, otherwise use level's image
      const levelImage = levelImages[index] ?? level?.image ?? level?.image_url ?? null;

      // For text type, ensure textContent is a string; for qr type, textContent maps from qrData
      let textContent = '';
      if (levelType === 'text') {
        textContent = level?.textContent ?? level?.text_content ?? '';
      } else if (levelType === 'qr') {
        textContent = level?.qrData ?? level?.textContent ?? '';
      }

      // Fix level dates — ensure they're in the future
      let levelStartDate = this.parseAiDate(level?.level_start_date ?? level?.start_date);
      let levelTimeline = this.parseAiDate(level?.level_timeline ?? level?.timeline);

      if (levelStartDate && levelStartDate.getTime() < now.getTime()) {
        levelStartDate = contestStart ?? new Date(now.getTime() + 3 * 24 * 60 * 60 * 1000);
      }
      if (levelTimeline && levelTimeline.getTime() < now.getTime()) {
        levelTimeline = contestEnd ?? new Date(now.getTime() + 17 * 24 * 60 * 60 * 1000);
      }
      // If no dates provided, use contest dates
      if (!levelStartDate) levelStartDate = contestStart ?? null;
      if (!levelTimeline) levelTimeline = contestEnd ?? null;

      return {
        id: level?.id ?? null,
        level_type: levelType,
        type: levelType,
        name: levelName,
        arabic_name: level?.arabic_name ?? levelName,
        description: levelDescription,
        arabic_description: arabicDescription,
        textContent,
        qrData: levelType === 'qr' ? (level?.qrData ?? '') : '',
        image: levelType === 'image' ? levelImage : null,
        level_start_date: levelStartDate?.getTime() ?? null,
        level_timeline: levelTimeline?.getTime() ?? null,
        client_webhook_url: level?.client_webhook_url ?? '',
        luqta_webhook_url: level?.luqta_webhook_url ?? '',
        client_variables: level?.client_variables ?? [],
        quizzes_id: levelType === 'quiz' ? (level?.quizzes_id ?? null) : null,
        accept_any_answer: level?.accept_any_answer ?? (levelType === 'text'),
        // setLevelsData() reads point_areas (falling back to geolocation_data) to
        // rebuild ar_type/ar_config/is_location_fixed/ar_points — pass whichever
        // the generator produced through untouched instead of dropping it.
        // A string is parsed here, or the mode inside it was read as absent.
        point_areas:
          levelType === 'geolocation' ? pointAreasOf(level) : null,

        //  Everything below was generated by the AI and then thrown away here.
        //
        //  setLevelsData() already knows how to read every one of these keys, so
        //  a survey or link_task level arrived with the right TYPE but no
        //  content: empty questions, no URL, no timer. Same for the quiz intro
        //  lines and the Arabic side of a text level's answers.
        //
        //  The generator emits these in exactly the shape setLevelsData expects
        //  — textContent/arabic_textContent as ' || '-joined strings, options as
        //  { option_text, arabic_option_text } objects — so they pass straight
        //  through untouched.
        arabic_textContent: levelType === 'text'
          ? (level?.arabic_textContent ?? '')
          : '',

        quiz_text_en: levelType === 'quiz' ? (level?.quiz_text_en ?? '') : '',
        quiz_text_ar: levelType === 'quiz' ? (level?.quiz_text_ar ?? '') : '',

        survey_questions: levelType === 'survey'
          ? (Array.isArray(level?.survey_questions) ? level.survey_questions : [])
          : [],
        min_answers_required: levelType === 'survey'
          ? (level?.min_answers_required
            ?? this.globalLevelSettings?.survey_min_answers
            ?? 1)
          : 1,
        question_order: levelType === 'survey'
          ? (level?.question_order === 'randomized' ? 'randomized' : 'fixed')
          : 'fixed',

        link_task_url: levelType === 'link_task' ? readLinkTaskUrl(level) : '',
        link_task_instruction: levelType === 'link_task'
          ? readLinkTaskInstruction(level)
          : '',
        link_task_instruction_ar: levelType === 'link_task'
          ? (level?.arabic_task_instruction || level?.link_task_instruction_ar || level?.task_instruction_ar || '')
          : '',
        // Falls back to the workspace minimum rather than a hardcoded 30, which
        // is below the minimum on some workspaces and fails the form validator.
        link_task_timer: levelType === 'link_task'
          ? (readLinkTaskTimer(level)
            || (this.globalLevelSettings?.link_task_min_timer ?? 30))
          : (this.globalLevelSettings?.link_task_min_timer ?? 30),
      };
    });
    if (unsupported.length) {
      this.sharedService.createNotification('warning',
        this.translate.instant('ai_levels.type-unsupported', { levels: unsupported.join(', ') }));
    }

    this.setLevelsData(normalizedLevels);

    // Set level count to update UI
    setTimeout(() => {
      this.addContentForm.get('totallevels')?.setValue(normalizedLevels.length, { emitEvent: true });
    }, 0);
  }

  private normalizeAiLocation(value: any): string[] {
    if (Array.isArray(value)) return value;
    if (typeof value === 'string') {
      return value.split(',').map((v) => v.trim()).filter(Boolean);
    }
    return [];
  }

  /**
   * Only the form's own age groups. The AI wrote bands the select does not
   * have ("18-34"), which were kept as they came (client sheet row 273); they
   * are mapped onto the groups they cover, and anything unreadable is All.
   */
  private normalizeAiAge(value: any): string[] {
    const groups = ageGroupsFrom(value);
    if (!groups && value && String(value).trim()) {
      console.warn('[AI] age not recognised, using All:', value);
    }
    return groups ?? ['All'];
  }

  private parseAiDate(value: any): Date | null {
    if (!value || value === 0) return null;
    const num = Number(value);
    // If it's a large number, treat as epoch milliseconds
    if (!isNaN(num) && num > 1000000000000) {
      const date = new Date(num);
      return Number.isNaN(date.getTime()) ? null : date;
    }
    // If it's a smaller number, treat as epoch seconds
    if (!isNaN(num) && num > 1000000000) {
      const date = new Date(num * 1000);
      return Number.isNaN(date.getTime()) ? null : date;
    }
    // Try parsing as string (ISO date, etc.)
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? null : date;
  }

  navigateBack(): void {
    if (window.history.length > 1) {
      this.location.back();
      return;
    }

    this.router.navigate(['/home/campaign-type']);
  }

  onLocationDropdownOpen(isOpen: boolean) {
    if (isOpen && !this.countriesLoaded) {
      this.getCountryRecords();
    }
    if (isOpen) {
      this.updateAllCountriesSelectedState();
    }
  }

  private countryRecordsRequest?: Promise<void>;

  getCountryRecords(): Promise<void> {
    // The SUPPORTED countries, not the full ISO list. The Location picked here becomes the
    // contest's target country, a foreign key to the platform's own country table — so a
    // name outside it is a contest that cannot be saved, and offering it is a dead end the
    // user only discovers at Submit.
    if (this.countriesLoaded) return Promise.resolve();
    if (this.countryRecordsRequest) return this.countryRecordsRequest;
    this.countryRecordsRequest = this.sharedService.getSupportedCountries().then((data) => {
      this.countryRecords = data;
      this.locationOptions = this.buildLocationOptions(data);
      this.countriesLoaded = true;
      this.updateAllCountriesSelectedState();
      this.syncLocationCascaderFromForm();
      this.refreshReceiptCurrencies();
    }).finally(() => {
      this.countryRecordsRequest = undefined;
    });
    return this.countryRecordsRequest;
  }

  onSelectAllCountries(selectAll: boolean) {
    if (selectAll && this.countryRecords) {
      const allCountryNames = this.countryRecords.map((c: any) => c.name);
      this.addContentForm.get('location')?.setValue(allCountryNames);
    } else {
      this.addContentForm.get('location')?.setValue([]);
    }
    this.syncLocationCascaderFromForm();
  }

  updateAllCountriesSelectedState() {
    const value = this.addContentForm.get('location')?.value || [];
    const selectedLocations = Array.isArray(value) ? value : [value];
    this.allCountriesSelected = selectedLocations.includes(this.ALL_COUNTRIES_VALUE) ||
      (Array.isArray(this.countryRecords) && this.countryRecords.length > 0 &&
        this.countryRecords.every((country: any) => selectedLocations.includes(country.name)));
  }

  buildLocationOptions(records: any[]): any[] {
    if (!Array.isArray(records) || records.length === 0) {
      return [];
    }
    const countryOptions = records
      .map((country: any) => ({
        value: country?.name || '',
        label: country?.name || '',
        isLeaf: true,
      }))
      .filter((option: any) => option.value && option.label);

    if (countryOptions.length === 0) {
      return [];
    }

    return [
      {
        value: this.ALL_COUNTRIES_VALUE,
        label: this.translate.instant('addContests.location.allCountries'),
        isLeaf: true,
      },
      ...countryOptions,
    ];
  }

  /**
   * Splits saved Location names into the supported countries they stand for (as the
   * supported list spells them) and the ones the platform does not run campaigns in.
   * Matching ignores case and accents, so "Cote d'Ivoire" still finds "Côte d'Ivoire".
   */
  private splitSupportedLocations(names: any[]): { kept: string[]; dropped: string[] } {
    const key = (value: any) => String(value ?? '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').trim().toLowerCase();
    const byKey = new Map<string, string>();
    (this.countryRecords || []).forEach((country: any) => {
      byKey.set(key(country.name), country.name);
      if (country.code) byKey.set(key(country.code), country.name);
    });
    const kept: string[] = [];
    const dropped: string[] = [];
    names.forEach((name) => {
      const match = byKey.get(key(name));
      if (match) {
        if (!kept.includes(match)) kept.push(match);
      } else if (String(name ?? '').trim()) {
        dropped.push(String(name).trim());
      }
    });
    return { kept, dropped };
  }

  /** Location as the save sends it: comma-joined, and only countries the platform supports. */
  private locationForSave(): string {
    if (this.allCountriesSelected && this.countryRecords?.length) {
      return this.countryRecords.map((country: any) => country.name).join(',');
    }
    const value = this.addContentForm.get('location')?.value;
    const names = (Array.isArray(value) ? value : String(value || '').split(','))
      .filter((name: any) => name && name !== this.ALL_COUNTRIES_VALUE);
    return (this.countryRecords?.length ? this.splitSupportedLocations(names).kept : names).join(',');
  }

  syncLocationCascaderFromForm(): void {
    const locations = this.addContentForm.get('location')?.value || [];
    let normalized = Array.isArray(locations) ? locations : [locations];
    // Restore the compact saved value to the same country selections used by the picker.
    if (normalized.includes(this.ALL_COUNTRIES_VALUE) && this.countryRecords?.length) {
      normalized = this.countryRecords.map((country: any) => country.name);
      this.addContentForm.get('location')?.setValue(normalized, { emitEvent: false });
    }
    // A campaign saved while the picker offered every ISO country can carry places the
    // platform does not run in (Antarctica, Åland, ...). Copied or edited as they were,
    // the save was refused with a list of countries; drop them here and say which.
    if (this.countryRecords?.length) {
      const { kept, dropped } = this.splitSupportedLocations(normalized);
      if (JSON.stringify(kept) !== JSON.stringify(normalized)) {
        normalized = kept;
        this.addContentForm.get('location')?.setValue(kept, { emitEvent: false });
      }
      if (dropped.length) {
        this.sharedService.createNotification(
          'warning',
          this.translate.instant('addContests.location.unsupportedRemoved', { countries: dropped.join(', ') })
        );
      }
    }
    const countryPaths = normalized
      .filter((location: any) => !!location && location !== this.ALL_COUNTRIES_VALUE)
      .map((location: any) => this.findPathInOptions(this.locationOptions, String(location)) || [String(location)]);

    this.updateAllCountriesSelectedState();
    if (this.allCountriesSelected) {
      this.locationCascaderValue = [[this.ALL_COUNTRIES_VALUE], ...countryPaths];
    } else {
      this.locationCascaderValue = countryPaths;
    }
  }

  onLocationCascaderChange(value: any[]): void {
    const raw = Array.isArray(value) ? value : [];
    const hasAllFlag = raw.some((path: any) => this.pathLeaf(path) === this.ALL_COUNTRIES_VALUE);
    const wasAllSelectedBefore = this.allCountriesSelected;

    if (hasAllFlag && !wasAllSelectedBefore) {
      this.onSelectAllCountries(true);
      return;
    }

    if (!hasAllFlag && wasAllSelectedBefore) {
      this.onSelectAllCountries(false);
      return;
    }

    const locations = this.extractLocationFromCascader(raw);
    this.addContentForm.get('location')?.setValue(locations);
    this.addContentForm.get('location')?.markAsTouched();
    this.updateAllCountriesSelectedState();
    this.locationCascaderValue = raw.filter((path: any) => this.pathLeaf(path) !== this.ALL_COUNTRIES_VALUE);
  }

  private pathLeaf(path: any): string {
    if (Array.isArray(path) && path.length > 0) {
      return String(path[path.length - 1]).trim();
    }
    return String(path ?? '').trim();
  }

  extractLocationFromCascader(value: any): string[] {
    if (!Array.isArray(value)) return [];
    return value
      .map((path: any) => this.pathLeaf(path))
      .filter((location: string) => location.length > 0 && location !== this.ALL_COUNTRIES_VALUE);
  }

  syncInterestCascaderFromForm(): void {
    const interests = this.addContentForm.get('business_objective')?.value || [];
    const normalized = Array.isArray(interests) ? interests : [interests];
    this.interestCascaderValue = normalized
      .filter((interest: any) => !!interest)
      .map((interest: any) => this.findPathInOptions(this.interestOptions, String(interest)) || [String(interest)]);
  }

  onInterestCascaderChange(value: any[]): void {
    const interests = this.extractInterestFromCascader(value);
    this.addContentForm.get('business_objective')?.setValue(interests);
    this.addContentForm.get('business_objective')?.markAsTouched();
    this.interestCascaderValue = value || [];
  }

  extractInterestFromCascader(value: any): string[] {
    if (!Array.isArray(value)) return [];
    return value
      .map((path: any) => {
        if (Array.isArray(path) && path.length > 0) {
          return String(path[path.length - 1]).trim();
        }
        return String(path ?? '').trim();
      })
      .filter((interest: string) => interest.length > 0);
  }

  private findPathInOptions(options: any[], target: string, trail: any[] = []): any[] | null {
    for (const option of options || []) {
      const nextTrail = [...trail, option.value];
      if (String(option.value) === target) {
        return nextTrail;
      }
      if (option.children) {
        const found = this.findPathInOptions(option.children, target, nextTrail);
        if (found) {
          return found;
        }
      }
    }
    return null;
  }

  onCategoryDropdownOpen(isOpen: boolean) {
    if (isOpen && !this.categoriesLoaded) {
      this.getCategoriesRecords();
    }
  }

  getCategoriesRecords() {
    if (this.categoriesLoaded) return;
    this.authService.getAll('CATEGORIES_LOOKUP').subscribe({
      next: (response: any) => {
        this.categoriesList = response;
        this.categoriesTree = this.buildCategoryTree(response);
        this.categoriesLoaded = true;
        this.ensurePrizeCategoryOptions();
      },
      error: (error) => {
        this.sharedService.createNotification(
          'error',
          error?.error?.message || 'Failed to load categories data'
        );
      },
    });
  }

  loadCategoriesAsync(): Promise<void> {
    return new Promise((resolve, reject) => {
      if (this.categoriesLoaded) {
        resolve();
        return;
      }
      this.authService.getAll('CATEGORIES_LOOKUP').subscribe({
        next: (response: any) => {
          this.categoriesList = response;
          this.categoriesTree = this.buildCategoryTree(response);
          this.categoriesLoaded = true;
          this.ensurePrizeCategoryOptions();
          resolve();
        },
        error: (error) => {
          this.sharedService.createNotification(
            'error',
            error?.error?.message || 'Failed to load categories data'
          );
          reject(error);
        },
      });
    });
  }

  // Contest Category — distinct taxonomy from prize categories (CATEGORIES_LOOKUP).
  // Sourced from /user_interest, same endpoint Admin uses for its Contest Category field.
  onContestCategoryDropdownOpen(isOpen: boolean) {
    if (isOpen && !this.contestCategoriesLoaded) {
      this.getContestCategoryRecords();
    }
  }

  getContestCategoryRecords() {
    this.loadContestCategoriesAsync();
  }

  // Shared in-flight request so the eager ngOnInit load and the edit-mode await
  // (and any dropdown open in between) never fire duplicate lookups.
  private contestCategoriesRequest: Promise<void> | null = null;

  loadContestCategoriesAsync(): Promise<void> {
    if (this.contestCategoriesLoaded) return Promise.resolve();
    if (this.contestCategoriesRequest) return this.contestCategoriesRequest;

    this.contestCategoriesRequest = new Promise((resolve) => {
      this.pagesService.getAll('CONTEST_CATEGORY_LOOKUP', { per_page: 1000 }).subscribe({
        next: (response: any) => {
          const items = response?.items || response || [];
          const activeItems = items.filter((item: any) => item.is_active);
          this.contestCategoryList = activeItems;
          this.contestCategoryTree = this.buildCategoryTree(activeItems);
          this.contestCategoriesLoaded = true;
          resolve();
        },
        error: (err: any) => {
          this.contestCategoriesRequest = null;
          this.sharedService.createNotification(
            'error',
            err?.error?.message || 'Failed to load campaign categories'
          );
          resolve();
        }
      });
    });
    return this.contestCategoriesRequest;
  }

  buildCategoryTree(flatList: any[]): any[] {
    const map = new Map<number, any>();
    const roots: any[] = [];

    // Create nodes with cascader format
    flatList.forEach(item => {
      map.set(item.id, {
        value: item.id,
        label: item.name,
        children: [],
        isLeaf: true
      });
    });

    // Build hierarchy
    flatList.forEach(item => {
      const node = map.get(item.id);
      if (item.parent_id === 0) {
        roots.push(node);
      } else {
        const parent = map.get(item.parent_id);
        if (parent) {
          parent.children.push(node);
          parent.isLeaf = false;
        }
      }
    });

    // Remove empty children arrays for leaf nodes
    const cleanLeaves = (nodes: any[]) => {
      nodes.forEach(node => {
        if (node.children.length === 0) {
          delete node.children;
        } else {
          cleanLeaves(node.children);
        }
      });
    };
    cleanLeaves(roots);

    return roots;
  }

  // Extract category IDs from cascader multi-select value
  // Cascader returns [[id1, id2, id3], [id4, id5]] - we need the last ID of each path
  extractCategoryIds(cascaderValue: any): number[] | null {
    if (!cascaderValue || !Array.isArray(cascaderValue) || cascaderValue.length === 0) {
      return null;
    }
    // For multiselect, each item is an array representing the path
    return cascaderValue.map((path: any): number => {
      if (Array.isArray(path) && path.length > 0) {
        return path[path.length - 1] as number; // Get the last ID (the selected category)
      }
      return path as number; // Fallback if it's already a single value
    });
  }

  // Build cascader path for a category ID (for edit mode)
  buildCategoryPath(categoryId: number): number[] {
    const path: number[] = [];
    let currentId = categoryId;

    while (currentId) {
      path.unshift(currentId);
      const category = this.categoriesList.find(c => c.id === currentId);
      if (category && category.parent_id !== 0) {
        currentId = category.parent_id;
      } else {
        break;
      }
    }
    return path;
  }

  // Build cascader path for a saved Contest Category id (edit mode) — mirrors buildCategoryPath
  // but walks contestCategoryList (the /user_interest taxonomy) instead of prize categoriesList.
  // Ids are uuids, so they are matched as strings — Number(uuid) is NaN and left the cascader empty.
  // Returns [] when the id isn't in the list, so a stale id never shows as a blank selection.
  buildContestCategoryPath(categoryId: any): any[] {
    const key = (v: any) => (v === null || v === undefined ? '' : String(v).trim());
    const path: any[] = [];
    let category = this.contestCategoryList.find(c => key(c.id) === key(categoryId));

    while (category && !path.some(id => key(id) === key(category.id))) {
      path.unshift(category.id);
      const parentKey = key(category.parent_id);
      if (!parentKey || parentKey === '0') break;
      category = this.contestCategoryList.find(c => key(c.id) === parentKey);
    }
    return path;
  }

  // A taxonomy entry by its name, for a value the client agreed in words. Exact
  // after normalising ("Food & Drink" = "food and drink") wins; otherwise the
  // entry sharing the most words, so "brand awareness" still finds
  // "Increase Brand Awareness". Nothing below half the words is a match.
  private findTaxonomyByName(list: any[], name: any): any | null {
    const norm = (v: any) => String(v ?? '').toLowerCase().replace(/&/g, ' and ')
      .replace(/[^\p{L}\p{N}]+/gu, ' ').trim();
    const target = norm(name);
    if (!target || !Array.isArray(list)) return null;
    const names = (e: any) => [e?.name, e?.arabic_name].map(norm).filter(Boolean);

    const exact = list.find(e => names(e).includes(target));
    if (exact) return exact;

    const words = new Set(target.split(' '));
    let best: any = null;
    let bestScore = 0.5;
    for (const e of list) {
      for (const n of names(e)) {
        const theirs = new Set(n.split(' '));
        const shared = [...words].filter(w => theirs.has(w)).length;
        const score = shared / Math.max(words.size, theirs.size);
        if (score >= bestScore && (score > bestScore || !best)) { best = e; bestScore = score; }
      }
    }
    return best;
  }

  // Convert saved category IDs to cascader format for edit mode
  categoryIdsToCascaderValue(categoryIds: number[] | number | null): number[][] {
    if (!categoryIds) return [];

    const ids = Array.isArray(categoryIds) ? categoryIds : [categoryIds];
    return ids.map(id => this.buildCategoryPath(id));
  }

  private cloneCategoriesTree(): any[] {
    return this.categoriesTree?.length
      ? JSON.parse(JSON.stringify(this.categoriesTree))
      : [];
  }

  private ensurePrizeCategoryOptions(): void {
    const totalPrizes = this.prizes?.length || 0;
    for (let i = 0; i < totalPrizes; i++) {
      const currentOptions = this.prizeCategoryOptions[i];
      // If row options are missing or were created before categories loaded, refresh them.
      if (!Array.isArray(currentOptions) || currentOptions.length === 0) {
        this.prizeCategoryOptions[i] = this.cloneCategoriesTree();
      }
    }
    if (this.prizeCategoryOptions.length > totalPrizes) {
      this.prizeCategoryOptions.length = totalPrizes;
    }
  }

  onQuizDropdownOpen(isOpen: boolean) {
    if (isOpen && !this.quizzesLoaded) {
      this.getQuizzesRecords();
    }
  }

  getQuizzesRecords() {
    if (this.quizzesLoaded) return;
    this.authService.getAll('GET_QUIZZES_LOOKUP').subscribe({
      next: (response: any) => {
        this.quizzesList = response || [];
        this.quizzesLoaded = true;
      },
      error: (err: any) => {
        this.sharedService.createNotification(
          'error',
          err?.error?.message || 'Failed to load quizzes data'
        );
      },
    });
  }

  constructor(
    private fb: FormBuilder,
    private router: Router,
    private location: Location,
    private route: ActivatedRoute,
    private pagesService: PagesService,
    private authService: AuthService,
    private modal: NzModalService,
    private cdr: ChangeDetectorRef,
    private dataService: DataService,
    private translate: TranslateService,
    private countryService: CountryService,
  ) {
    this.addContentForm = this.fb.group({
      banners_code: this.fb.array([]), // now a FormArray
      name: ['', [Validators.required, noWhitespaceValidator(), Validators.maxLength(100)]],
      arabic_name: ['', [Validators.required, noWhitespaceValidator(), Validators.maxLength(100)]],
      arabic_details: ['', [Validators.required, noWhitespaceValidator(), Validators.maxLength(1000)]],

      is_age_restricted: [false],
      age: [[]],
      details: ['', [Validators.required, noWhitespaceValidator(), Validators.maxLength(1000)]],


      gender: ['both', Validators.required],
      // game_name: ['', Validators.required],
      number_of_winners: [
        '',
        [Validators.required, Validators.min(1), wholeNumberValidator]
      ],
      points_pool_per_contest: ['', [Validators.min(0)]],
      // no_of_users: ['', Validators.required],
      location: [[], Validators.required],
      business_objective: [[], Validators.required],
      category_id: [[]],
      draw_type: [''],
      // Instant-Result only. Off by default, which is what every existing
      // contest is — the backend reads a missing value as off too.
      all_completers: [false],
      no_of_users: [''],
      type: ['', Validators.required],
      client_allow_list: [[]],
      kyc_tier: ['light', Validators.required],
      sharingSetting: ['', Validators.required],
      // total_points: ['', Validators.required],
      start_date: [null, Validators.required],
      end_date: [null, Validators.required],
      prize_announce_date: ['', [Validators.required, prizeAfterEndDate]],
      format_of_contest: ['', Validators.required], //formate of contest
      totallevels: [''],
      levels_of_contest: this.fb.array([]),
      _contest_prize: this.fb.array([]),
    });

    this.addContentForm.get('no_of_users')?.valueChanges.subscribe(() => this.syncWinnersControl());
    // A receipt level offers, and starts with, the currencies of the campaign's countries.
    this.addContentForm.get('location')?.valueChanges.subscribe(() => this.refreshReceiptCurrencies());
    // The API doesn't keep the toggle (it always comes back false), only the age
    // groups, so the two are kept in step: the toggle is on exactly when an age
    // group is picked. That is what lets a reopened campaign show it switched on.
    this.addContentForm.get('is_age_restricted')?.valueChanges.subscribe((restricted: boolean) => {
      const age = this.addContentForm.get('age');
      if (restricted && !hasAgeGroup(age?.value)) {
        age?.setValue([]);
      } else if (!restricted && hasAgeGroup(age?.value)) {
        age?.setValue(['All']);
      }
      age?.setValidators(restricted ? [ageGroupRequired] : []);
      age?.updateValueAndValidity({ emitEvent: false });
    });
    let previousAge: string[] = [];
    this.addContentForm.get('age')?.valueChanges.subscribe((values: string[]) => {
      const normalized = normalizeAgeSelection(values || [], previousAge);
      previousAge = [...normalized];
      this.addContentForm.get('age')?.setValue(normalized, { emitEvent: false });
      // Picking an age group restricts the campaign by age, whatever the toggle says.
      if (hasAgeGroup(normalized) && !this.addContentForm.get('is_age_restricted')?.value) {
        this.addContentForm.get('is_age_restricted')?.setValue(true);
      }
    });
    this.syncWinnersControl();

    // Private campaigns may use an access code without an allow-list.
    this.syncAllowListValidator(this.addContentForm.get('type')?.value);
    this.addContentForm.get('type')?.valueChanges.subscribe((accessMode: string) => {
      this.syncAllowListValidator(accessMode);
    });

    this.initModalForm();
    // Clear the images on window reload
    window.onbeforeunload = () => {
      sessionStorage.removeItem('banners_code');
      sessionStorage.removeItem('image_code');
    };
    this.addContentForm.get('start_date')?.valueChanges.subscribe((value) => {
      this.updateEndDateState(value);

      const endDateControl = this.addContentForm.get('end_date');
      const prizeDateControl = this.addContentForm.get('prize_announce_date');
      const endDate = endDateControl?.value;

      // If start date is cleared/deleted, clear both end date and prize date
      if (!value) {
        if (endDate) {
          endDateControl?.setValue(null);
          endDateControl?.markAsTouched();
        }
        if (prizeDateControl?.value) {
          prizeDateControl?.setValue(null);
          prizeDateControl?.markAsTouched();
        }
        return;
      }

      // If start date has a value, validate end date (must be after start date)
      if (endDate) {
        const start = new Date(value);
        const end = new Date(endDate);

        // If end date is not after start date, clear it and mark as touched
        if (end.getTime() <= start.getTime()) {
          endDateControl?.setValue(null);
          endDateControl?.markAsTouched();
        }
      }
    });

    // Subscribe to end_date changes to validate prize_announce_date
    this.addContentForm.get('end_date')?.valueChanges.subscribe((value) => {
      this.updateContestTimeLeft();
      const prizeDateControl = this.addContentForm.get('prize_announce_date');
      const prizeDate = prizeDateControl?.value;

      // If end date is cleared/deleted, clear prize date
      if (!value) {
        if (prizeDate) {
          prizeDateControl?.setValue(null);
          prizeDateControl?.markAsTouched();
        }
        return;
      }

      // If end date has a value, validate prize date (must be after end date)
      if (prizeDate) {
        const end = new Date(value);
        const prize = new Date(prizeDate);

        // If prize date is not after end date, clear it and mark as touched
        if (prize.getTime() <= end.getTime()) {
          prizeDateControl?.setValue(null);
          prizeDateControl?.markAsTouched();
          return;
        }
      }
      // The prize date is checked against the end date, so it is checked again whenever that moves.
      prizeDateControl?.updateValueAndValidity({ emitEvent: false });
    });
  }

  // ── Survey helpers ──────────────────────────────────────────────────────────
  getSurveyQuestions(levelIndex: number): FormArray {
    return (this.levels.at(levelIndex)?.get('survey_questions') as FormArray) ?? this.fb.array([]);
  }

  getSurveyOptions(levelIndex: number, questionIndex: number): FormArray {
    const questions = this.getSurveyQuestions(levelIndex);
    if (!questions) return this.fb.array([]);
    const question = questions.at(questionIndex);
    if (!question) return this.fb.array([]);
    return (question.get('options') as FormArray) ?? this.fb.array([]);
  }

  createSurveyQuestion(): FormGroup {
    return this.fb.group({
      id: [null],
      question_text: ['', Validators.required],
      arabic_question_text: ['', Validators.required],
      question_type: ['mcq'],       // 'mcq' | 'open_text'
      is_required: [true],
      answer_selection_type: ['multiple'],  // 'single' | 'multiple' — only relevant for 'mcq'
      options: this.fb.array([
        this.createSurveyOption(),
        this.createSurveyOption(),
      ]),
    });
  }

  createSurveyOption(): FormGroup {
    return this.fb.group({
      option_text: ['', Validators.required],
      arabic_option_text: ['', Validators.required],
    });
  }

  addSurveyQuestion(levelIndex: number): void {
    this.getSurveyQuestions(levelIndex).push(this.createSurveyQuestion());
    this.syncSurveyMinAnswers(levelIndex);
  }

  removeSurveyQuestion(levelIndex: number, questionIndex: number): void {
    const questions = this.getSurveyQuestions(levelIndex);
    if (questions.length > 1) questions.removeAt(questionIndex);
    this.syncSurveyMinAnswers(levelIndex);
  }

  setSurveyQuestionRequired(levelIndex: number, questionIndex: number, required: boolean): void {
    this.getSurveyQuestions(levelIndex).at(questionIndex)?.get('is_required')?.setValue(required);
    this.syncSurveyMinAnswers(levelIndex);
  }

  getSurveyRequiredCount(levelIndex: number): number {
    return this.getSurveyQuestions(levelIndex).controls.filter(q => q.get('is_required')?.value === true).length;
  }

  // Lowest value min_answers_required may take: the global floor or the required-question count, whichever is higher.
  getSurveyMinAllowed(levelIndex: number): number {
    return Math.max(this.globalLevelSettings?.survey_min_answers || 1, this.getSurveyRequiredCount(levelIndex));
  }

  // Keeps min_answers_required within [required questions, total questions] after the question
  // list changes, so adding/marking a required question never leaves the level silently invalid.
  private syncSurveyMinAnswers(levelIndex: number): void {
    const ctrl = this.levels.at(levelIndex)?.get('min_answers_required');
    if (!ctrl) return;
    const questions = this.getSurveyQuestions(levelIndex);
    const requiredCount = this.getSurveyRequiredCount(levelIndex);
    const current = Number(ctrl.value) || 0;
    let next = current;
    if (current < requiredCount) next = requiredCount;
    else if (current > questions.length) next = Math.max(questions.length, requiredCount);
    if (next !== current) ctrl.setValue(next);
  }

  addSurveyOption(levelIndex: number, questionIndex: number): void {
    const options = this.getSurveyOptions(levelIndex, questionIndex);
    if (options.length < 6) options.push(this.createSurveyOption());
  }

  removeSurveyOption(levelIndex: number, questionIndex: number, optionIndex: number): void {
    const options = this.getSurveyOptions(levelIndex, questionIndex);
    if (options.length > 2) options.removeAt(optionIndex);
  }

  // Duplicate option text is allowed (non-blocking) but flagged so the creator can double-check.
  // Comparison ignores case and surrounding/repeated whitespace; empty options are never duplicates.
  isSurveyOptionDuplicate(levelIndex: number, questionIndex: number, optionIndex: number,
    field: 'option_text' | 'arabic_option_text'): boolean {
    const normalize = (v: any) => String(v ?? '').trim().replace(/\s+/g, ' ').toLowerCase();
    const options = this.getSurveyOptions(levelIndex, questionIndex).controls;
    const text = normalize(options[optionIndex]?.get(field)?.value);
    if (!text) return false;
    return options.some((o, idx) => idx !== optionIndex && normalize(o.get(field)?.value) === text);
  }

  hasDuplicateSurveyOptions(levelIndex: number, questionIndex: number): boolean {
    return this.getSurveyOptions(levelIndex, questionIndex).controls.some((_, oi) =>
      this.isSurveyOptionDuplicate(levelIndex, questionIndex, oi, 'option_text') ||
      this.isSurveyOptionDuplicate(levelIndex, questionIndex, oi, 'arabic_option_text'));
  }

  onSurveyQuestionTypeChange(levelIndex: number, questionIndex: number, type: string): void {
    const question = this.getSurveyQuestions(levelIndex).at(questionIndex) as FormGroup;
    question.get('question_type')?.setValue(type);
    // MCQ needs options, open_text does not require them
    const options = question.get('options') as FormArray;
    options.controls.forEach(c => {
      const optionGroup = c as FormGroup;
      const textCtrl = optionGroup.get('option_text');
      const arabicTextCtrl = optionGroup.get('arabic_option_text');
      if (type === 'open_text') {
        textCtrl?.clearValidators();
        arabicTextCtrl?.clearValidators();
      } else {
        textCtrl?.setValidators(Validators.required);
        arabicTextCtrl?.setValidators(Validators.required);
      }
      textCtrl?.updateValueAndValidity();
      arabicTextCtrl?.updateValueAndValidity();
    });
  }
  // ────────────────────────────────────────────────────────────────────────────

  // ───────────────────────── Receipt level authoring ─────────────────────────
  /** The receipt rules of a level as one group; disabled unless the level type is 'receipt'. */
  createReceiptRulesGroup(rules?: any): FormGroup {
    const value = receiptRulesToForm(rules);
    return this.fb.group({
      retailers_text: [value.retailers_text],
      products: this.fb.array(value.products.map((p) => this.createReceiptProductGroup(p))),
      min_quantity: [value.min_quantity],
      min_spend: [value.min_spend],
      currencies: [value.currencies ?? []],
      // Not sent; the campaign countries' currencies, for the validator. See refreshReceiptCurrencies().
      allowed_currencies: [[] as string[]],
      spend_basis: [value.spend_basis],
      combination_mode: [value.combination_mode],
      purchase_from: [value.purchase_from],
      purchase_to: [value.purchase_to],
      max_attempts: [value.max_attempts],
      // Not editable here; kept so a save does not reset a deliberate value the backend returned.
      confidence_threshold: [value.confidence_threshold ?? null],
    }, { validators: receiptRulesValidator });
  }

  createReceiptProductGroup(product?: { name?: string; arabic_name?: string; aliases_text?: string; sku?: string; sku_name?: string }): FormGroup {
    return this.fb.group({
      name: [product?.name ?? ''],
      // Shown to Arabic participants on Web QR; empty shows them the name above.
      arabic_name: [product?.arabic_name ?? ''],
      aliases_text: [product?.aliases_text ?? ''],
      sku: [product?.sku ?? ''],
      // Not sent; the name the SKU was made for, so a rename drops it (see buildReceiptRules).
      sku_name: [product?.sku_name ?? (product?.sku ? product?.name ?? '' : '')],
    });
  }

  receiptProducts(levelIndex: number): FormArray {
    return this.levels.at(levelIndex)?.get('receipt_rules.products') as FormArray;
  }

  addReceiptProduct(levelIndex: number): void {
    const products = this.receiptProducts(levelIndex);
    if (!products || products.length >= RECEIPT_MAX_PRODUCTS) return;
    products.push(this.createReceiptProductGroup());
    products.markAsDirty();
  }

  removeReceiptProduct(levelIndex: number, productIndex: number): void {
    const products = this.receiptProducts(levelIndex);
    if (!products) return;
    products.removeAt(productIndex);
    products.markAsDirty();
    // "Listed products only" has nothing left to count once the last product is gone.
    const basis = this.levels.at(levelIndex)?.get('receipt_rules.spend_basis');
    if (basis?.value === 'qualifying_items' && !this.receiptHasProducts(this.levels.at(levelIndex))) {
      basis.setValue('receipt_total');
    }
  }

  /** Whether at least one product has a name — what "Listed products only" needs. */
  receiptHasProducts(level: AbstractControl | null): boolean {
    const rules = (level?.get('receipt_rules') as FormGroup | null)?.getRawValue?.();
    return !!rules && receiptProductCount(rules as ReceiptRulesFormValue) > 0;
  }

  setReceiptSpendBasis(level: AbstractControl | null, basis: 'receipt_total' | 'qualifying_items'): void {
    if (basis === 'qualifying_items' && !this.receiptHasProducts(level)) return;
    const control = level?.get('receipt_rules.spend_basis');
    control?.setValue(basis);
    control?.markAsDirty();
  }

  /**
   * The currencies of the campaign's countries, in the order the countries were picked: one
   * for a single country campaign (EGP for Egypt), several for a campaign in several countries.
   */
  campaignCurrencies(): string[] {
    const raw = this.addContentForm?.get('location')?.value;
    const names: string[] = (Array.isArray(raw) ? raw : String(raw ?? '').split(','))
      .map((name: any) => String(name ?? '').trim())
      .filter((name: string) => name && name !== this.ALL_COUNTRIES_VALUE);
    const records: any[] = Array.isArray(this.countryRecords) ? this.countryRecords : [];
    const key = (value: any) => String(value ?? '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').trim().toLowerCase();
    const codeOf = (name: string): string | null => {
      const record = records.find((r) => key(r.name) === key(name) || key(r.code) === key(name));
      // Before the supported list arrives, match the name against the English country names.
      return record?.code || countryCodeForName(name);
    };
    return cleanCurrencies(names.map((name) => currencyForCountry(codeOf(name))).filter(Boolean));
  }

  /** The currencies the receipt currency picker lists for this level. */
  receiptCurrencyOptions(level: AbstractControl | null): string[] {
    const allowed = cleanCurrencies(level?.get('receipt_rules.allowed_currencies')?.value);
    const chosen = cleanCurrencies(level?.get('receipt_rules.currencies')?.value);
    let base = allowed;
    if (!base.length) {
      const records: any[] = Array.isArray(this.countryRecords) ? this.countryRecords : [];
      base = records.length
        ? cleanCurrencies(records.map((r) => currencyForCountry(r.code)).filter(Boolean)).sort()
        : allCountryCurrencies();
    }
    // A saved currency the campaign no longer uses stays listed, so it can be seen and removed.
    return [...base, ...chosen.filter((c) => !base.includes(c))];
  }

  private currencyLabels = new Map<string, string>();

  /** 'EGP · Egyptian Pound' in the page language. */
  receiptCurrencyLabel(code: string): string {
    const lang = this.isArabic ? 'ar' : 'en';
    const cacheKey = lang + code;
    let label = this.currencyLabels.get(cacheKey);
    if (!label) {
      const name = currencyName(code, lang);
      label = name && name !== code ? `${code} · ${name}` : code;
      this.currencyLabels.set(cacheKey, label);
    }
    return label;
  }

  /**
   * Hands every receipt level the campaign's currencies. A level that still shows the previous
   * default (or nothing) follows the new countries; a choice the admin made is kept, and the
   * validator flags any of it the campaign no longer uses.
   */
  refreshReceiptCurrencies(only?: AbstractControl | null): void {
    const allowed = this.campaignCurrencies();
    const levels = only ? [only] : (this.levels?.controls ?? []);
    for (const level of levels) {
      const rules = level?.get('receipt_rules') as FormGroup | null;
      if (!rules) continue;
      const previous = cleanCurrencies(rules.get('allowed_currencies')?.value);
      const chosen = cleanCurrencies(rules.get('currencies')?.value);
      const followsDefault = chosen.length === 0 || chosen.join() === previous.join();
      rules.get('allowed_currencies')?.setValue(allowed, { emitEvent: false });
      if (followsDefault) rules.get('currencies')?.setValue(allowed, { emitEvent: false });
      rules.updateValueAndValidity();
    }
  }

  /**
   * Translation key of the error for a receipt field. Shown as soon as the field is changed (a
   * decimal typed into a whole-number field should not wait for the admin to leave the field),
   * or once the admin has left any receipt field or tried to submit.
   */
  receiptError(level: AbstractControl | null, field: string): string | null {
    const rules = level?.get('receipt_rules');
    if (!rules || rules.disabled || !(rules.touched || rules.dirty)) return null;
    const code = rules.errors?.['receiptRules']?.[field];
    return code ? 'add-content.receipt.err.' + code : null;
  }

  /** Values for the message of receiptError() (a date, a currency...). */
  receiptErrorParams(level: AbstractControl | null, field: string): Record<string, unknown> {
    return level?.get('receipt_rules')?.errors?.['receiptRules']?.params?.[field] ?? {};
  }

  receiptProductError(level: AbstractControl | null, productIndex: number): string | null {
    const rules = level?.get('receipt_rules');
    if (!rules || rules.disabled || !(rules.touched || rules.dirty)) return null;
    const code = rules.errors?.['receiptRules']?.productRows?.[productIndex];
    return code ? 'add-content.receipt.err.' + code : null;
  }

  receiptProductErrorParams(level: AbstractControl | null, productIndex: number): Record<string, unknown> {
    return this.receiptErrorParams(level, 'product.' + productIndex);
  }

  /** Several products are needed before "any one / all" means anything. */
  receiptHasSeveralProducts(level: AbstractControl | null): boolean {
    const products = (level?.get('receipt_rules.products') as FormArray | null)?.value || [];
    return products.filter((p: any) => String(p?.name ?? '').trim() !== '').length > 1;
  }

  /** Enables the receipt rules only while the level is a receipt level, so they never block other types. */
  private syncReceiptRules(levelGroup: FormGroup): void {
    const rules = levelGroup.get('receipt_rules');
    if (!rules) return;
    if (levelGroup.get('type')?.value === 'receipt') {
      rules.enable({ emitEvent: false });
      this.refreshReceiptCurrencies(levelGroup);
    } else {
      rules.disable({ emitEvent: false });
    }
  }

  /** Puts the receipt rules back to their defaults (used when the level leaves the receipt type). */
  private resetReceiptRules(levelGroup: FormGroup): void {
    const rules = levelGroup.get('receipt_rules') as FormGroup | null;
    if (!rules) return;
    const { products: _products, ...rest } = defaultReceiptRulesForm();
    (rules.get('products') as FormArray)?.clear({ emitEvent: false });
    rules.patchValue({ ...rest, confidence_threshold: null }, { emitEvent: false });
    rules.markAsUntouched();
  }

  /** The `receipt_rules` object the save sends for this level. */
  private buildReceiptRulesPayload(levelGroup: AbstractControl): ReturnType<typeof buildReceiptRules> {
    const raw = (levelGroup.get('receipt_rules') as FormGroup | null)?.getRawValue?.()
      ?? defaultReceiptRulesForm();
    return buildReceiptRules(raw as ReceiptRulesFormValue);
  }

  /** Form value of a receipt level's rules, for the review panel. */
  receiptRulesOfLevel(level: AbstractControl | null): ReceiptRulesPayload | null {
    if (!level || level.get('type')?.value !== 'receipt') return null;
    return this.buildReceiptRulesPayload(level);
  }

  /** Product names string in active language for the review panel. */
  receiptProductNamesForReview(rules: ReceiptRulesPayload | null): string {
    if (!rules || !Array.isArray(rules.products)) return '';
    const arabic = this.isArabic;
    return rules.products
      .map((p: any) => (arabic && String(p?.arabic_name ?? '').trim()) || p?.name)
      .filter((name: any) => !!name)
      .join(', ');
  }

  /** The currencies for the review panel. */
  receiptCurrencyLabelForReview(rules: ReceiptRulesPayload | null): string {
    if (!rules) return '';
    let codes = cleanCurrencies(rules.currencies);
    if (!codes.length && rules.currency) codes = [rules.currency];
    if (!codes.length) codes = this.campaignCurrencies();
    const shown = codes.slice(0, 4).join(' / ');
    return codes.length > 4 ? `${shown} +${codes.length - 4}` : shown;
  }

  /** Purchase window day as dd/MM/yy. */
  receiptDayForReview(value: unknown): string {
    if (value === null || value === undefined || value === '') return '…';
    const str = String(value).trim();
    const parts = str.split('T')[0].split('-');
    if (parts.length === 3) {
      return `${parts[2]}/${parts[1]}/${parts[0].slice(2)}`;
    }
    return str;
  }

  addNewLevel(): void {
    const newLevelGroup = this.fb.group({
      id: [null],
      type: [null, Validators.required],
      name: ['', [Validators.required, Validators.maxLength(LEVEL_NAME_MAX)]],
      arabic_name: ['', [Validators.required, Validators.maxLength(LEVEL_NAME_MAX)]],
      description: ['', [Validators.required, Validators.maxLength(1000)]],
      arabic_description: ['', [Validators.required, Validators.maxLength(1000)]],
      level_start_date: [null, Validators.required],
      level_timeline: [null, Validators.required],
      textContent: this.fb.array([this.createTextContentGroup()]),
      imageFile: [null],
      qrData: [''],
      client_webhook_url: [''],
      luqta_webhook_url: [''],
      accept_any_answer: [false],
      client_variables: this.fb.array([]),
      quizzes_id: [null],
      quiz_text_en: [''],
      quiz_text_ar: [''],
      // Survey fields
      survey_questions: this.fb.array([this.createSurveyQuestion()]),
      min_answers_required: [1, [Validators.min(1)]],
      question_order: ['fixed'],    // 'fixed' | 'randomized'
      // Link Task fields
      link_task_url: [''],
      link_task_instruction: [''],
      link_task_instruction_ar: [''],
      link_open_mode: [DEFAULT_LINK_OPEN_MODE],
      link_task_timer: [30, [Validators.min(this.globalLevelSettings?.link_task_min_timer || 10), Validators.max(this.globalLevelSettings?.link_task_max_timer || 99999), wholeNumberValidator]],
      // Geolocation (Treasure Hunt) fields
      ar_type: [DEFAULT_AR_TYPE],
      is_location_fixed: [true],
      ar_config: this.createArConfigGroup(DEFAULT_AR_TYPE),
      ar_points: this.fb.array([this.createPointGroup({}, 0)]),
      // Receipt fields
      receipt_rules: this.createReceiptRulesGroup(),
    }, { validators: this.levelDateOrderValidator });

    const newIndex = this.levels.length;
    this.subscribeToLevelChanges(newLevelGroup, newIndex);
    this.applyInitialLevelTypeValidators(newLevelGroup);
    this.levels.push(newLevelGroup);
    this.syncPointLocationValidators(newIndex);
    this.levelQrCodes.push(null);
    this.levelImagePreviews.push(null);
    this.isImageVisible.push(false); // Initialize image modal visibility
    this.syncLevelImageValidator(newLevelGroup, newIndex);
  }


  // ───────────────────────── Geolocation (Treasure Hunt) authoring ──────────
  readonly arTypes: ArType[] = ['scan_find', 'find_gift', 'catch_logos'];
  readonly arCommonKeys = AR_COMMON_KEYS as unknown as string[];
  readonly prizeTypes = ['voucher', 'name', 'coins', 'custom'];

  private normalizeArType(value: any): ArType {
    return this.arTypes.includes(value) ? value : DEFAULT_AR_TYPE;
  }

  arTypeKeys(arType: any): string[] {
    return AR_TYPE_KEYS[this.normalizeArType(arType)];
  }

  // Every key the selected mini-game actually renders — the union of common and
  // type-specific keys, minus the ones this game doesn't author.
  arVisibleKeys(arType: any): string[] {
    const type = this.normalizeArType(arType);
    const hidden = AR_HIDDEN_KEYS[type] || [];
    return [...this.arCommonKeys, ...AR_TYPE_KEYS[type]].filter((key) => !hidden.includes(key));
  }

  private isArKeyHidden(arType: ArType, key: string): boolean {
    return (AR_HIDDEN_KEYS[arType] || []).includes(key);
  }

  arDefault(arType: any, key: string): any {
    return AR_DEFAULTS[this.normalizeArType(arType)][key];
  }

  // Numeric ar_config keys that must stay whole numbers in the payload.
  private readonly arIntegerKeys = new Set([
    'objects_total', 'objects_required', 'duration_sec', 'lanes', 'move_ms',
    'move_gap_ms', 'drop_ms', 'respawn_ms',
  ]);

  isArUrlKey(key: string): boolean {
    return key === 'model_url' || key === 'scene_url';
  }

  arAllowsNegative(key: string): boolean {
    return AR_SIGNED_KEYS.includes(key);
  }

  arNeedsAtLeastOne(key: string): boolean {
    return AR_MIN_ONE_KEYS.includes(key);
  }

  // Number inputs still take a typed or pasted minus sign despite min="0", so
  // stop it at the keyboard. '+' and 'e' would otherwise sneak in "1e-3".
  preventNegativeNumberInput(event: KeyboardEvent): void {
    if (event.key === '-' || event.key === '+' || event.key.toLowerCase() === 'e') {
      event.preventDefault();
    }
  }

  preventNegativeNumberPaste(event: ClipboardEvent): void {
    if (/[-+e]/i.test(event.clipboardData?.getData('text') || '')) {
      event.preventDefault();
    }
  }

  /* ------------------------------------------------------------------ */
  /*        3D model picker — model_url is chosen from /my_models       */
  /* ------------------------------------------------------------------ */

  private model3dService = inject(Model3dService);
  private aiService = inject(AiInsightService);

  modelOptions: { label: string; value: string; size: number; sizeLabel: string }[] = [];
  isLoadingModelOptions = false;

  // Shared preview popup state.
  isModelPreviewOpen = false;
  modelPreviewUrl = '';
  modelPreviewTitle = '';
  modelPreviewSize = 0;

  isModelUrlKey(key: string): boolean {
    return key === 'model_url';
  }

  loadModelOptions(): void {
    this.isLoadingModelOptions = true;
    this.model3dService.getMyModels().subscribe({
      next: (res) => {
        this.modelOptions = this.model3dService
          .toRecordList(res)
          .map((record) => {
            const file = this.model3dService.getFileObject(record);
            const size = file?.size ?? 0;
            return {
              label: (record?.name || '').trim() || file?.name || 'Untitled model',
              value: this.model3dService.getModelUrl(record),
              size,
              sizeLabel: size
                ? size < 1024 * 1024
                  ? `${(size / 1024).toFixed(1)} KB`
                  : `${(size / (1024 * 1024)).toFixed(2)} MB`
                : '',
            };
          })
          .filter((option) => !!option.value);
        this.isLoadingModelOptions = false;
      },
      error: () => {
        // A failed list just leaves the picker empty — it must never block the contest form.
        this.isLoadingModelOptions = false;
      },
    });
  }

  // An edited contest may hold a URL that isn't in the account's list (an older or external
  // model). Surface it as an option so opening the form never silently drops the value.
  modelOptionsFor(currentValue: string | null) {
    if (!currentValue || this.modelOptions.some((option) => option.value === currentValue)) {
      return this.modelOptions;
    }
    return [
      { label: currentValue, value: currentValue, size: 0, sizeLabel: '' },
      ...this.modelOptions,
    ];
  }

  openModelPreview(url: string | null): void {
    if (!url) return;
    const match = this.modelOptions.find((option) => option.value === url);
    this.modelPreviewUrl = url;
    this.modelPreviewTitle = match?.label || '';
    this.modelPreviewSize = match?.size || 0;
    this.isModelPreviewOpen = true;
  }

  // Builds an ar_config group holding the union of every key the selected type
  // needs, seeded from `existing` where present and from defaults otherwise.
  private createArConfigGroup(arType: ArType, existing: any = {}): FormGroup {
    const defaults = AR_DEFAULTS[arType];
    const group: Record<string, any> = {};
    [...this.arCommonKeys, ...AR_TYPE_KEYS[arType]].forEach((key) => {
      const raw = existing?.[key];
      // `duration_sec: 0` and `drop_to_m: -0.6` are meaningful values, so only
      // null/undefined/'' fall back to the default — never a falsy check.
      const value = raw === undefined || raw === null || raw === '' ? defaults[key] : raw;
      const nonNegative = !this.isArUrlKey(key) && !this.arAllowsNegative(key);
      // Not on a hidden key: the admin could never fix an error there (Find the Gift).
      const atLeastOne = this.arNeedsAtLeastOne(key) && !this.isArKeyHidden(arType, key);
      group[key] = [
        value,
        atLeastOne
          ? [Validators.min(1), wholeNumberValidator]
          : nonNegative ? [Validators.min(0)] : [],
      ];
    });
    return this.fb.group(group, {
      validators: [this.arConfigRangeValidator(arType)],
    });
  }

  // The two invariants the client relies on and the admin must enforce. Bound to the
  // mini-game so a hidden field can never raise an error the admin has no way to fix.
  private arConfigRangeValidator =
    (arType: ArType) =>
    (control: AbstractControl): ValidationErrors | null => {
      const group = control as FormGroup;
      const num = (key: string) => Number(group.get(key)?.value);
      const errors: ValidationErrors = {};

      if (
        group.get('objects_required') &&
        group.get('objects_total') &&
        !this.isArKeyHidden(arType, 'objects_required')
      ) {
        if (num('objects_required') > num('objects_total')) {
          errors['requiredExceedsTotal'] = true;
        }
      }

      if (group.get('spawn_min_m') && group.get('spawn_max_m')) {
        if (num('spawn_min_m') >= num('spawn_max_m')) {
          errors['spawnRangeInvalid'] = true;
        }
      }

      // Spot & Tap places each object somewhere between the two heights. Equal is a
      // fixed height, which works; a top below the bottom is no range at all.
      if (group.get('height_min_m') && group.get('height_max_m')) {
        // A blank field goes out as its default, so that is what it is compared as.
        const height = (key: string) => {
          const v = group.get(key)?.value;
          return Number(v === null || v === undefined || v === '' ? AR_DEFAULTS[arType][key] : v);
        };
        if (height('height_max_m') < height('height_min_m')) {
          errors['heightRangeInvalid'] = true;
        }
      }

      return Object.keys(errors).length ? errors : null;
    };

  arConfigGroup(levelIndex: number): FormGroup {
    return this.levels.at(levelIndex).get('ar_config') as FormGroup;
  }

  // The range errors live on the ar_config group, so map each one to the field the
  // admin has to change and show it there rather than under the whole card.
  arFieldError(levelIndex: number, key: string): string | null {
    const fieldErrors = this.arConfigGroup(levelIndex)?.get(key)?.errors;
    if (this.arNeedsAtLeastOne(key) && (fieldErrors?.['min'] || fieldErrors?.['wholeNumber'])) {
      return 'geo.config.atLeastOne';
    }
    if (fieldErrors?.['min']) {
      return 'geo.config.negativeInvalid';
    }
    const errors = this.arConfigGroup(levelIndex)?.errors;
    if (key === 'objects_required' && errors?.['requiredExceedsTotal']) {
      return 'geo.config.requiredExceedsTotal';
    }
    if (key === 'spawn_min_m' && errors?.['spawnRangeInvalid']) {
      return 'geo.config.spawnRangeInvalid';
    }
    if (key === 'height_max_m' && errors?.['heightRangeInvalid']) {
      return 'geo.config.heightRangeInvalid';
    }
    return null;
  }

  // Named ar_points, not points: the API's level object already has a numeric
  // `points` (score) field, and a same-named FormArray would collide the moment
  // anything patches a level record wholesale.
  points(levelIndex: number): FormArray {
    return this.levels.at(levelIndex).get('ar_points') as FormArray;
  }

  /**
   * Whether this campaign is a multi-brand collaboration.
   *
   * The value used to be `share` and is `shared` now. Both are accepted on
   * read: campaigns saved before the rename still hold the old one, and a
   * strict check would show them with no sponsorship type selected at all.
   * Only `shared` is ever written.
   */
  isMultiBrand(value: any): boolean {
    const v = String(value ?? '').trim().toLowerCase();
    return v === 'shared' || v === 'share';
  }

  /**
   * The sharing setting as one of the two values the buttons match, or '' when
   * there is none. A stored value in any other shape (`Single`, `single_brand`,
   * `multi_brand`, which the AI brief uses) matched neither button, so the
   * campaign, a duplicate included, reopened with no sponsorship type selected.
   */
  toSharingSetting(value: any): 'single' | 'shared' | '' {
    const v = String(value ?? '').trim().toLowerCase().replace(/[\s-]+/g, '_');
    if (v === 'single' || v === 'single_brand') return 'single';
    if (this.isMultiBrand(v) || v === 'multi_brand') return 'shared';
    return '';
  }

  isLocationFixed(levelIndex: number): boolean {
    return this.levels.at(levelIndex).get('is_location_fixed')?.value !== false;
  }

  // A play-anywhere level carries a single prize, so only its first point is
  // authored. Extra points stay in the form so switching back to fixed keeps them.
  visiblePoints(levelIndex: number): AbstractControl[] {
    const controls = this.points(levelIndex).controls;
    return this.isLocationFixed(levelIndex) ? controls : controls.slice(0, 1);
  }

  createPointGroup(data: any = {}, index = 0): FormGroup {
    const prize = data?.prize || {};
    const knownType = ['voucher', 'name', 'coins'].includes(prize.type);
    const location = data?.location || {};
    const direction = data?.direction || {};

    return this.fb.group({
      id: [data?.id || `p${index + 1}`],
      location_name: [data?.location_name || ''],
      // Custom prizes carry the label in `type` itself, so keep it in its own
      // control and let prize_type act purely as the picker.
      prize_type: [knownType ? prize.type : 'custom'],
      prize_custom_type: [knownType ? '' : (prize.type || '')],
      prize_value: [prize.value ?? ''],
      prize_description: [prize.description || ''],
      prize_image: [prize.image || ''],
      lat: [location.lat ?? null],
      lng: [location.lng ?? null],
      // A 0 or negative radius can never be satisfied and would soft-lock the
      // level, so the admin must not be able to save one.
      radius: [location.radius ?? 50, [Validators.min(1)]],
      // The prize zone has a hard 5 m floor.
      prize_radius: [location.prize_radius ?? 7, [Validators.min(GEO_MIN_PRIZE_RADIUS_M)]],
      google_maps_link: [location.google_maps_link || ''],
      direction_enabled: [direction.enabled === true],
      direction_target_lat: [direction.target?.lat ?? null],
      direction_target_lng: [direction.target?.lng ?? null],
      direction_angle_tolerance: [direction.angle_tolerance ?? GEO_DIRECTION_DEFAULTS.angle_tolerance],
      direction_distance_check: [direction.distance_check ?? GEO_DIRECTION_DEFAULTS.distance_check],
      is_completed: [data?.is_completed === true],
      status: [data?.status || 'in_progress'],
      reached_at: [data?.reached_at ?? null],
    });
  }

  addPoint(levelIndex: number): void {
    const list = this.points(levelIndex);
    // Index-based ids collide after a middle point is removed (p1, p3 → next
    // would also be p3), and the client sends `id` back on completion — so take
    // the next id from the highest suffix in use, not from the list length.
    const highest = list.controls.reduce((max, ctrl) => {
      const match = /^p(\d+)$/.exec(String(ctrl.get('id')?.value || ''));
      return match ? Math.max(max, Number(match[1])) : max;
    }, 0);
    list.push(this.createPointGroup({ id: `p${Math.max(highest, list.length) + 1}` }));
    this.syncPointLocationValidators(levelIndex);
  }

  removePoint(levelIndex: number, pointIndex: number): void {
    const list = this.points(levelIndex);
    if (list.length > 1) {
      list.removeAt(pointIndex);
    }
  }

  // Switching game resets ar_config to that game's defaults — carrying a foreign
  // game's tuning across would ship values the client never reads.
  // Tuned values are lost on the switch, so confirm first whenever any differ from
  // the current game's defaults.
  selectArType(levelIndex: number, arType: ArType): void {
    const current = this.normalizeArType(this.levels.at(levelIndex).get('ar_type')?.value);
    if (current === arType) {
      return;
    }
    if (!this.hasCustomArConfig(levelIndex)) {
      this.onArTypeChange(levelIndex, arType);
      return;
    }
    this.modal.confirm({
      nzTitle: this.translate.instant('geo.arType.switchTitle'),
      nzContent: this.translate.instant('geo.arType.switchContent', {
        game: this.translate.instant('geo.arType.' + arType),
      }),
      nzOkText: this.translate.instant('geo.arType.switchOk'),
      nzCancelText: this.translate.instant('common.cancel'),
      nzOkDanger: true,
      nzClassName: 'contest-styled-modal',
      nzCentered: true,
      nzOnOk: () => this.onArTypeChange(levelIndex, arType),
    });
  }

  private hasCustomArConfig(levelIndex: number): boolean {
    const arType = this.normalizeArType(this.levels.at(levelIndex).get('ar_type')?.value);
    const raw = this.arConfigGroup(levelIndex)?.getRawValue() || {};
    return this.arVisibleKeys(arType).some((key) => {
      const value = raw[key];
      const fallback = AR_DEFAULTS[arType][key];
      if (this.isArUrlKey(key)) {
        return String(value ?? '').trim() !== String(fallback ?? '').trim();
      }
      // An emptied box is sent as the default, so it isn't a change worth warning about.
      if (value === null || value === undefined || value === '') {
        return false;
      }
      return Number(value) !== Number(fallback);
    });
  }

  onArTypeChange(levelIndex: number, arType: ArType): void {
    const levelGroup = this.levels.at(levelIndex) as FormGroup;
    levelGroup.get('ar_type')?.setValue(arType);
    levelGroup.setControl('ar_config', this.createArConfigGroup(arType));
  }

  onLocationFixedChange(levelIndex: number, fixed: boolean): void {
    this.levels.at(levelIndex).get('is_location_fixed')?.setValue(fixed);
    this.syncPointLocationValidators(levelIndex);
  }

  // Coordinates are only required on a location-bound geolocation level. Every
  // level carries the ar_points array, so gating on is_location_fixed alone would
  // make a plain text or quiz level unsaveable for a missing latitude.
  syncPointLocationValidators(levelIndex: number): void {
    const isGeoLevel = this.levels.at(levelIndex).get('type')?.value === 'geolocation';
    const fixed = isGeoLevel && this.isLocationFixed(levelIndex);
    this.points(levelIndex).controls.forEach((ctrl) => {
      const point = ctrl as FormGroup;
      const rules: Record<string, any[]> = {
        lat: [Validators.required],
        lng: [Validators.required],
        radius: [Validators.required, Validators.min(1), wholeNumberValidator],
        prize_radius: [Validators.required, Validators.min(GEO_MIN_PRIZE_RADIUS_M)],
      };
      Object.entries(rules).forEach(([key, validators]) => {
        const control = point.get(key);
        if (!control) {
          return;
        }
        control.setValidators(fixed ? validators : []);
        control.updateValueAndValidity({ emitEvent: false });
      });
    });
  }

  // Keeps the Maps link in step with the coordinates the admin typed, unless the
  // admin has pasted their own link.
  syncMapsLink(levelIndex: number, pointIndex: number): void {
    const point = this.points(levelIndex).at(pointIndex) as FormGroup;
    const lat = point.get('lat')?.value;
    const lng = point.get('lng')?.value;
    const current = point.get('google_maps_link')?.value || '';
    const isGenerated = !current || /^https:\/\/www\.google\.com\/maps\?q=/.test(current);
    if (lat != null && lng != null && isGenerated) {
      point.get('google_maps_link')?.setValue(`https://www.google.com/maps?q=${lat},${lng}`);
    }
  }

  // Serialises a level's authoring controls into the `point_areas` contract.
  private buildPointAreas(group: AbstractControl): any {
    const arType = this.normalizeArType(group.get('ar_type')?.value);
    const isLocationFixed = group.get('is_location_fixed')?.value !== false;
    const configGroup = group.get('ar_config') as FormGroup;
    const rawConfig = configGroup?.getRawValue() || {};

    const ar_config: Record<string, any> = {};
    [...this.arCommonKeys, ...AR_TYPE_KEYS[arType]].forEach((key) => {
      const value = rawConfig[key];
      if (this.isArUrlKey(key)) {
        // null means "use the bundled asset" — an empty box must send null, not ''.
        ar_config[key] = value && String(value).trim() ? String(value).trim() : null;
        return;
      }
      const fallback = AR_DEFAULTS[arType][key];
      if (value === undefined || value === null || value === '') {
        ar_config[key] = fallback;
        return;
      }
      const num = Number(value);
      ar_config[key] = Number.isNaN(num)
        ? fallback
        : (this.arIntegerKeys.has(key) ? Math.round(num) : num);
    });

    // Play-anywhere sends only the one prize the admin can see.
    const pointControls = (group.get('ar_points') as FormArray)?.controls || [];
    const points = (isLocationFixed ? pointControls : pointControls.slice(0, 1)).map((ctrl, index) => {
      const p = ctrl as FormGroup;
      const prizeType = p.get('prize_type')?.value;
      const isCustom = prizeType === 'custom';
      const prize: any = {
        type: isCustom ? (p.get('prize_custom_type')?.value || '').trim() : prizeType,
        // Prize Image is only offered for the 'name' prize type; anything left over
        // from a previous type selection must not be sent.
        image: prizeType === 'name' ? (p.get('prize_image')?.value || '') : '',
      };
      if (prizeType === 'voucher') {
        prize.value = p.get('prize_value')?.value || '';
      } else if (prizeType === 'coins') {
        prize.value = Math.max(0, Math.round(Number(p.get('prize_value')?.value) || 0));
      } else if (prizeType === 'name') {
        prize.description = p.get('prize_description')?.value || '';
      } else {
        const description = (p.get('prize_description')?.value || '').trim();
        if (description) {
          prize.description = description;
        }
      }

      const point: any = {
        id: p.get('id')?.value || `p${index + 1}`,
        prize,
        location_name: p.get('location_name')?.value || '',
        is_completed: !!p.get('is_completed')?.value,
        status: p.get('status')?.value || 'in_progress',
        reached_at: p.get('reached_at')?.value ?? null,
      };

      // `location` is written on every level, fixed or not. The player app ignores
      // lat/lng/radius when is_location_fixed is false, so carrying them costs
      // nothing — whereas omitting them wipes the coordinates on save, and they
      // never come back if the level is switched to fixed again.
      const rawLat = p.get('lat')?.value;
      const rawLng = p.get('lng')?.value;
      const hasCoords =
        rawLat !== null && rawLat !== undefined && rawLat !== '' &&
        rawLng !== null && rawLng !== undefined && rawLng !== '' &&
        !Number.isNaN(Number(rawLat)) && !Number.isNaN(Number(rawLng));
      const lat = hasCoords ? Number(rawLat) : null;
      const lng = hasCoords ? Number(rawLng) : null;

      point.location = {
        lat,
        lng,
        radius: Math.round(Number(p.get('radius')?.value) || 0),
        prize_radius: Math.round(Number(p.get('prize_radius')?.value) || 0),
        google_maps_link:
          p.get('google_maps_link')?.value ||
          (hasCoords ? `https://www.google.com/maps?q=${lat},${lng}` : ''),
      };

      const directionEnabled = !!p.get('direction_enabled')?.value;
      point.direction = { enabled: directionEnabled };
      if (directionEnabled) {
        const tLat = Number(p.get('direction_target_lat')?.value);
        const tLng = Number(p.get('direction_target_lng')?.value);
        point.direction.target = {
          lat: Number.isNaN(tLat) ? null : tLat,
          lng: Number.isNaN(tLng) ? null : tLng,
        };
        const angle = Number(p.get('direction_angle_tolerance')?.value);
        const distance = Number(p.get('direction_distance_check')?.value);
        point.direction.angle_tolerance = Number.isNaN(angle)
          ? GEO_DIRECTION_DEFAULTS.angle_tolerance
          : angle;
        point.direction.distance_check = Number.isNaN(distance)
          ? GEO_DIRECTION_DEFAULTS.distance_check
          : distance;
      }

      return point;
    });

    return {
      type: 'multi_point',
      ar_type: arType,
      is_location_fixed: isLocationFixed,
      ar_config,
      points,
    };
  }

  // Helper method to generate range of numbers
  private range(start: number, end: number): number[] {
    const result: number[] = [];
    for (let i = start; i < end; i++) {
      result.push(i);
    }
    return result;
  }

  // Helper method to check if two dates are on the same day
  private isSameDay(date1: Date, date2: Date): boolean {
    return (
      date1.getFullYear() === date2.getFullYear() &&
      date1.getMonth() === date2.getMonth() &&
      date1.getDate() === date2.getDate()
    );
  }
  private updateEndDateState(startDate: Date | null): void {
    const endDateControl = this.addContentForm.get('end_date');
    if (startDate) {
      endDateControl?.enable();
    } else {
      endDateControl?.disable();
    }
    if (startDate) {
      endDateControl?.setValidators([Validators.required]);
    } else {
      endDateControl?.clearValidators();
    }
    endDateControl?.updateValueAndValidity();
  }
  getDisabledStartDate = (current: Date): boolean => {
    const now = new Date();
    // Set both to midnight for date-only comparison
    const currentDateOnly = new Date(current);
    currentDateOnly.setHours(0, 0, 0, 0);
    const todayDateOnly = new Date(now);
    todayDateOnly.setHours(0, 0, 0, 0);

    // Disable dates before today (not including today)
    return currentDateOnly.getTime() < todayDateOnly.getTime();
  };

  getDisabledStartDateTime = (current: Date | Date[]) => {
    const now = new Date();

    // Handle array case (for range pickers)
    const currentDate = Array.isArray(current) ? current[0] : current;

    // If no current date provided, return no restrictions
    if (!currentDate) {
      return {
        nzDisabledHours: () => [],
        nzDisabledMinutes: () => [],
        nzDisabledSeconds: () => []
      };
    }

    return {
      nzDisabledHours: () => {
        const currentHour = now.getHours();
        // If selecting today, disable hours before current hour
        if (this.isSameDay(currentDate, now)) {
          return this.range(0, currentHour);
        }
        return [];
      },
      nzDisabledMinutes: (hour: number) => {
        const currentHour = now.getHours();
        const currentMinute = now.getMinutes();
        // If selecting today and current hour, disable minutes before current minute
        if (this.isSameDay(currentDate, now) && hour === currentHour) {
          return this.range(0, currentMinute);
        }
        return [];
      },
      nzDisabledSeconds: (hour: number, minute: number) => {
        const currentHour = now.getHours();
        const currentMinute = now.getMinutes();
        const currentSecond = now.getSeconds();
        // If selecting today, current hour and current minute, disable seconds before current second
        if (this.isSameDay(currentDate, now) && hour === currentHour && minute === currentMinute) {
          return this.range(0, currentSecond + 1); // +1 to ensure future time
        }
        return [];
      }
    };
  };
  getDisabledPrizeDate = (current: Date): boolean => {
    const endDate = this.addContentForm.get('end_date')?.value;

    if (!endDate) return true;

    const end = new Date(endDate);

    // Set to midnight for date-only comparison
    const currentDateOnly = new Date(current);
    currentDateOnly.setHours(0, 0, 0, 0);
    const endDateOnly = new Date(end);
    endDateOnly.setHours(0, 0, 0, 0);

    // Allow same day as end date, disable dates before end date
    return currentDateOnly.getTime() < endDateOnly.getTime();
  };

  getDisabledPrizeDateTime = (current: Date | Date[]) => {
    const endDate = this.addContentForm.get('end_date')?.value;

    // Handle array case (for range pickers)
    const currentDate = Array.isArray(current) ? current[0] : current;

    if (!endDate || !currentDate) {
      return {
        nzDisabledHours: () => this.range(0, 24),
        nzDisabledMinutes: () => this.range(0, 60),
        nzDisabledSeconds: () => this.range(0, 60)
      };
    }

    const end = new Date(endDate);

    // Normalize current date for comparison
    const currentDateOnly = new Date(currentDate);
    currentDateOnly.setHours(0, 0, 0, 0);
    const endDateOnly = new Date(end);
    endDateOnly.setHours(0, 0, 0, 0);

    return {
      nzDisabledHours: () => {
        // On same day as end date, disable hours before end hour
        if (currentDateOnly.getTime() === endDateOnly.getTime()) {
          return this.range(0, end.getHours());
        }
        return [];
      },
      nzDisabledMinutes: (hour: number) => {
        // On same day and same hour as end date, disable minutes before end minute
        if (currentDateOnly.getTime() === endDateOnly.getTime() && hour === end.getHours()) {
          return this.range(0, end.getMinutes() + 1);
        }
        return [];
      },
      nzDisabledSeconds: (hour: number, minute: number) => {
        // On same day, same hour, same minute as end date, disable seconds before end second
        if (currentDateOnly.getTime() === endDateOnly.getTime() &&
          hour === end.getHours() &&
          minute === end.getMinutes()) {
          return this.range(0, end.getSeconds() + 1);
        }
        return [];
      }
    };
  };
  getDisabledEndDate = (current: Date): boolean => {
    const startDate = this.addContentForm.get('start_date')?.value;

    if (!startDate) return true;

    const start = new Date(startDate);

    // Set to midnight for date-only comparison
    const currentDateOnly = new Date(current);
    currentDateOnly.setHours(0, 0, 0, 0);
    const startDateOnly = new Date(start);
    startDateOnly.setHours(0, 0, 0, 0);

    // Allow same day as start date, disable dates before start date
    return currentDateOnly.getTime() < startDateOnly.getTime();
  };

  getDisabledEndDateTime = (current: Date | Date[]) => {
    const startDate = this.addContentForm.get('start_date')?.value;

    // Handle array case (for range pickers)
    const currentDate = Array.isArray(current) ? current[0] : current;

    if (!startDate || !currentDate) {
      return {
        nzDisabledHours: () => this.range(0, 24),
        nzDisabledMinutes: () => this.range(0, 60),
        nzDisabledSeconds: () => this.range(0, 60)
      };
    }

    const start = new Date(startDate);

    // Normalize current date for comparison
    const currentDateOnly = new Date(currentDate);
    currentDateOnly.setHours(0, 0, 0, 0);
    const startDateOnly = new Date(start);
    startDateOnly.setHours(0, 0, 0, 0);

    return {
      nzDisabledHours: () => {
        const disabled: number[] = [];

        // On same day as start date, disable hours before start hour
        if (currentDateOnly.getTime() === startDateOnly.getTime()) {
          disabled.push(...this.range(0, start.getHours()));
        }

        return disabled;
      },
      nzDisabledMinutes: (hour: number) => {
        const disabled: number[] = [];

        // On same day and same hour as start, disable minutes before start minute
        if (currentDateOnly.getTime() === startDateOnly.getTime() && hour === start.getHours()) {
          disabled.push(...this.range(0, start.getMinutes() + 1));
        }

        return disabled;
      },
      nzDisabledSeconds: (hour: number, minute: number) => {
        const disabled: number[] = [];

        // On same day, same hour, same minute as start, disable seconds before start second
        if (currentDateOnly.getTime() === startDateOnly.getTime() &&
          hour === start.getHours() &&
          minute === start.getMinutes()) {
          disabled.push(...this.range(0, start.getSeconds() + 1));
        }

        return disabled;
      }
    };
  };
  getDisabledPrizeStartDate = (current: Date): boolean => {
    const contestStartRaw = this.addContentForm.get('start_date')?.value;
    const contestEndRaw = this.addContentForm.get('end_date')?.value;
    if (!contestStartRaw || !contestEndRaw) return true;
    const contestStart = new Date(contestStartRaw);
    const contestEnd = new Date(contestEndRaw);
    // Convert current to midnight for reliable comparison
    const currentDate = new Date(current.setHours(0, 0, 0, 0));
    contestStart.setHours(0, 0, 0, 0);
    contestEnd.setHours(0, 0, 0, 0);
    return currentDate < contestStart || currentDate > contestEnd;
  };
  getDisabledPrizeEndDate = (current: Date): boolean => {
    const contestEndRaw = this.addContentForm.get('end_date')?.value;
    const prizeStartRaw = this.addContentForm.get('prize_start_date')?.value;
    if (!contestEndRaw || !prizeStartRaw) return true;
    const contestEnd = new Date(contestEndRaw);
    const prizeStart = new Date(prizeStartRaw);
    const currentDate = new Date(current);
    // Normalize to midnight
    contestEnd.setHours(0, 0, 0, 0);
    prizeStart.setHours(0, 0, 0, 0);
    currentDate.setHours(0, 0, 0, 0);
    // ❗ Allow same-day selection too
    return currentDate < prizeStart || currentDate > contestEnd;
  };



  initModalForm(): void {
    this.modalForm = this.fb.group({
      start_date: [''],
      end_date: [''],
    });
    this.modalForm.get('start_date')?.valueChanges.subscribe((value) => {
      this.updateEndDateState(value);
    });
  }
  showModal(): void {
    this.isVisible = true;
  }
  handleOk(): void {
    if (this.modalForm.valid) {
      if (!this.lastPrizeGroup) {
        const prizeGroup = this.createPrize();
        prizeGroup.patchValue({
          start_date: this.modalForm.get('start_date')?.value,
          end_date: this.modalForm.get('end_date')?.value,
        });
        this.prizeArray.push(prizeGroup);
      } else {
        this.lastPrizeGroup.patchValue({
          start_date: this.modalForm.get('start_date')?.value,
          end_date: this.modalForm.get('end_date')?.value,
        });
      }
      this.isVisible = false;
    } else {
      this.modalForm.markAllAsTouched();
    }
  }
  handleCancel(): void {
    this.isVisible = false;
  }
  fileData: { [key: string]: File | string } = {}; // Store files separately
  get prizeArray(): FormArray {
    return this.addContentForm.get('_contest_prize') as FormArray;
  }
  createPrize(): FormGroup {
    return this.fb.group({
      name: ['', [Validators.required, noWhitespaceValidator()]],
      // Optional: prizes saved before the field existed have no Arabic name.
      arabic_name: ['', Validators.maxLength(100)],
      value: ['', [Validators.required, Validators.min(0)]],
      currency: ['USD', Validators.required],
      image_code: ['', Validators.required],
      category_type: ['', Validators.required],
      id: [null],

      //  How the prize is actually handed over.
      // 
      //  None of it goes in the contest save — it is PATCHed per prize once the
      //  rows exist and have ids. "item" with no code is what every prize has
      //  always been, so an untouched form saves exactly as it did before.
      prize_kind: ['item'],
      code_mode: ['none'],
      // Blank = unlimited. Only meaningful for a shared voucher.
      quantity: [null, [Validators.min(1)]],
      voucher_code: [''],
      redeem_instructions: [''],
      arabic_redeem_instructions: [''],
      expires_at: [null],
      //  Gift-card codes, one per line, uploaded after the prize is saved.
      //  Write-only: the pool can be topped up but never read back, so this box
      //  is always empty on load and what is typed is added to what is there.
      codes: [''],
      // Reported by the pool endpoint on edit, so the client can see the stock.
      code_pool_total: [0],
      code_pool_claimed: [0],

      //  "Generate vouchers" (code_mode 'generated'). `quantity` above doubles as
      //  the number of vouchers, and the rest share control names with the
      //  Vouchers page so one component renders both.
      ...this.generatedVoucherControls(),
    });
  }

  /**
   * The controls only the "Generate vouchers" mode reads. They sit on every prize
   * group so switching modes needs no control juggling, and are ignored by the
   * save unless code_mode is 'generated'.
   */
  private generatedVoucherControls(config?: any): Record<string, any> {
    return {
      voucher_value: [config?.value ?? null],
      voucher_currency: [config?.currency || 'USD'],
      voucher_expires_at: [parseServerDate(config?.expires_at)],
      usage_type: [config?.usage_type || 'SINGLE_USE'],
      carry_over_balance: [config?.carry_over_balance === true],
      max_uses: [config?.max_uses ?? null],
      // What the server reports about the batch behind this prize (counts, status).
      // Read-only here: it drives the counts line and the lock, and is never sent.
      voucher_config: [config ?? null],
    };
  }

  /**
   * Save the fulfilment settings for every prize, once they have ids.
   *
   * Two calls per prize at most, and only for prizes that actually need them:
   * one PATCH for the configuration, one POST for any gift-card codes typed in.
   * Both are keyed by prize id, which is why this cannot run until the contest
   * save has come back — a new contest's prizes do not exist before then.
   *
   * The save response lists prizes in the order they were submitted, so index
   * is the only reliable pairing: a brand-new prize has no id on our side to
   * match against.
   *
   * Failures are collected rather than thrown. The contest and its prizes are
   * already saved by this point; losing the voucher configuration is worth a
   * warning, not a rollback the API could not honour anyway.
   */
  private savePrizePools(response: any, skipIncomplete = false): Observable<string[]> {
    // 2326 returns the saved contest, prizes under contest._contest_prize — with or
    // without the {isSuccess, data} envelope depending on the caller. Reading only
    // `prizes` left every new prize without an id, so its voucher settings were dropped.
    const body = response?.data ?? response;
    const saved: any[] =
      [body?.contest?._contest_prize, body?._contest_prize, body?.prizes]
        .find((list) => Array.isArray(list)) ?? [];
    const calls: Observable<string | null>[] = [];

    this.prizeArray.controls.forEach((group, index) => {
      const prizeId = saved[index]?.id ?? group.get('id')?.value;
      if (!prizeId) return;

      const kind = group.get('prize_kind')?.value || 'item';
      const mode = group.get('code_mode')?.value || 'none';
      const name = group.get('name')?.value || `Prize ${index + 1}`;

      // An untouched prize is a plain item with no code — exactly what the row
      // already is, so there is nothing to say.
      const codes = this.parsedCodes(index);
      if (kind === 'item' && mode === 'none' && !codes.length) return;

      //  On a draft, a voucher the client has not finished describing is simply
      //  not sent yet. The API would refuse it, and an error about unfinished
      //  work is the wrong thing to show someone who is explicitly saving work
      //  in progress. The submit path validates up front instead.
      if (skipIncomplete) {
        if (mode === 'shared' && !(group.get('voucher_code')?.value || '').trim()) return;
        if (mode === 'unique' && !codes.length && !group.get('code_pool_total')?.value) return;
        if (mode === 'generated' && this.generatedVoucherProblems(group).length) return;
      }

      // Generated vouchers have their own payload: the rules go to the batch
      // behind the prize, and none of the shared/unique fields apply.
      if (mode === 'generated') {
        const payload = this.generatedVoucherPayload(group, kind);
        if (!payload) return;
        calls.push(
          this.pagesService.patch('CONTEST_PRIZE_CONFIG', prizeId, payload).pipe(
            map(() => null),
            catchError((err: any) =>
              of(`${name}: ${err?.error?.message || 'could not save the prize pool settings'}`)
            )
          ) as Observable<string | null>
        );
        return;
      }

      const rawQty = group.get('quantity')?.value;
      const expires = group.get('expires_at')?.value;

      calls.push(
        this.pagesService.patch('CONTEST_PRIZE_CONFIG', prizeId, {
          prize_kind: kind,
          code_mode: mode,
          // 0 is how the API spells "unlimited"; blank means the same thing here.
          quantity: Number(rawQty) > 0 ? Math.trunc(Number(rawQty)) : 0,
          voucher_code: mode === 'shared' ? (group.get('voucher_code')?.value || '').trim() : '',
          redeem_instructions: (group.get('redeem_instructions')?.value || '').trim(),
          arabic_redeem_instructions: (group.get('arabic_redeem_instructions')?.value || '').trim(),
          expires_at: expires ? new Date(expires).toISOString() : null,
        }).pipe(
          switchMap(() =>
            // Codes only after the mode is saved: the upload endpoint refuses a
            // prize that does not issue unique codes yet.
            mode === 'unique' && codes.length
              ? this.pagesService.create('CONTEST_PRIZE_CODES', { contest_prize_id: prizeId, codes })
                .pipe(map(() => null))
              : of(null)
          ),
          catchError((err: any) =>
            of(`${name}: ${err?.error?.message || 'could not save the prize pool settings'}`)
          )
        ) as Observable<string | null>
      );
    });

    if (!calls.length) return of([]);
    return forkJoin(calls).pipe(map((results) => results.filter(Boolean) as string[]));
  }

  /** A prize hands over a code only in these two modes. */
  isCodePrize(index: number): boolean {
    return this.prizeArray.at(index)?.get('code_mode')?.value !== 'none';
  }

  isSharedCode(index: number): boolean {
    return this.prizeArray.at(index)?.get('code_mode')?.value === 'shared';
  }

  isUniqueCode(index: number): boolean {
    return this.prizeArray.at(index)?.get('code_mode')?.value === 'unique';
  }

  /** The prize's form group, typed for the shared voucher fields component. */
  prizeGroupAt(index: number): FormGroup {
    return this.prizeArray.at(index) as FormGroup;
  }

  isGeneratedCode(index: number): boolean {
    return this.prizeArray.at(index)?.get('code_mode')?.value === 'generated';
  }

  // ── Generated vouchers (code_mode 'generated') ──

  /** Currencies a voucher can be issued in (2383). Loaded once, the first time they are needed. */
  voucherCurrencies: VoucherCurrency[] = DEFAULT_VOUCHER_CURRENCIES;
  private voucherCurrenciesRequested = false;

  private ensureVoucherCurrencies(): void {
    if (this.voucherCurrenciesRequested) return;
    this.voucherCurrenciesRequested = true;
    this.pagesService.getAll('VOUCHER_CURRENCIES', {}).subscribe({
      next: (res: any) => {
        const list = Array.isArray(res) ? res : res?.currencies;
        this.voucherCurrencies = mergeVoucherCurrencies(list);
      },
      // Let the next switch to the mode try again.
      error: () => {
        this.voucherCurrencies = mergeVoucherCurrencies([]);
        this.voucherCurrenciesRequested = false;
      },
    });
  }

  /** What the server says about the batch behind a saved generated prize. */
  voucherConfig(index: number): any {
    return this.prizeArray.at(index)?.get('voucher_config')?.value ?? null;
  }

  private handedOut(cfg: any): number {
    return cfg
      ? Math.max(Number(cfg.assigned) || 0, Number(cfg.used) || 0, Number(cfg.redeemed) || 0)
      : 0;
  }

  /** How many of this prize's vouchers are already with winners or spent. */
  voucherHandedOut(index: number): number {
    return this.handedOut(this.voucherConfig(index));
  }

  /** Once any voucher is handed out the value, currency, expiry and usage rules are fixed. */
  voucherRulesLocked(index: number): boolean {
    return this.voucherHandedOut(index) > 0;
  }

  /** Anything the API would refuse about a generated-voucher prize, as translated messages. */
  private generatedVoucherProblems(group: AbstractControl): string[] {
    const v = (group as FormGroup).getRawValue();
    const handedOut = this.handedOut(v.voucher_config);
    const locked = handedOut > 0;
    const blank = (x: any) => x === null || x === undefined || x === '';
    const keys = validateVoucherRules(
      {
        quantity: blank(v.quantity) ? null : Number(v.quantity),
        value: blank(v.voucher_value) ? null : Number(v.voucher_value),
        currency: v.voucher_currency,
        expires: v.voucher_expires_at,
        usageType: v.usage_type,
        carry: v.carry_over_balance === true,
        maxUses: blank(v.max_uses) ? null : Number(v.max_uses),
      },
      // Locked rules are not editable, so an expiry that has since passed is not the user's to fix.
      { checkExpiry: !locked },
    );
    const problems = keys.map((k) => this.translate.instant(k));
    if (locked && Number(v.quantity) < handedOut) {
      problems.push(this.translate.instant('vouchers.errors.quantityBelowIssued', { min: handedOut }));
    }
    return problems;
  }

  /**
   * The 2369 body for a generated-voucher prize, or null when there is nothing to send.
   *
   * With vouchers already handed out the rule fields are left off: the server then
   * keeps the batch's own values, so a rule that has since expired cannot make an
   * unrelated edit to the contest fail.
   */
  private generatedVoucherPayload(group: AbstractControl, kind: string): Record<string, any> | null {
    const v = (group as FormGroup).getRawValue();
    const cfg = v.voucher_config;
    const quantity = Math.trunc(Number(v.quantity));

    const base: Record<string, any> = {
      prize_kind: kind,
      code_mode: 'generated',
      quantity,
      redeem_instructions: (v.redeem_instructions || '').trim(),
      arabic_redeem_instructions: (v.arabic_redeem_instructions || '').trim(),
    };

    if (this.handedOut(cfg) > 0) {
      // The only thing left to change is the quantity and the instructions; if the batch
      // has expired the server would refuse even those, so skip the call when unchanged.
      const expired = cfg?.expires_at ? new Date(cfg.expires_at).getTime() <= Date.now() : false;
      if (quantity === Number(cfg.quantity) && expired) return null;
      return base;
    }

    const multi = v.usage_type === 'MULTI_USE';
    const hasMax = v.max_uses !== null && v.max_uses !== undefined && v.max_uses !== '';
    return {
      ...base,
      voucher_value: Number(v.voucher_value),
      voucher_currency: v.voucher_currency,
      usage_type: v.usage_type,
      carry_over_balance: multi && v.carry_over_balance === true,
      // Only meaningful for multi-use; null leaves a carry-over voucher uncapped.
      ...(multi && hasMax ? { max_uses: Number(v.max_uses) } : {}),
      voucher_expires_at: toDateParam(v.voucher_expires_at),
    };
  }

  /**
   * Keep kind and code type from contradicting each other.
   *
   * A physical item has no code. A voucher starts as a batch LuQta generates,
   * and a gift card as a pool of uploaded codes; the code type below can then
   * change that. Picking the kind the prize already has keeps its code type.
   */
  onPrizeKindChange(index: number, kind: string): void {
    const group = this.prizeArray.at(index);
    if (!group) return;

    // Vouchers already handed out: the prize stays a generated voucher.
    if (this.voucherRulesLocked(index)) return;

    const mode = group.get('code_mode')?.value || 'none';
    if (group.get('prize_kind')?.value === kind && (kind === 'item') === (mode === 'none')) return;

    group.patchValue({ prize_kind: kind });
    if (kind === 'item') this.setCodeMode(group, 'none');
    else if (kind === 'voucher') this.setCodeMode(group, 'generated');
    // Only a voucher can be generated; a gift card keeps a shared code it already has.
    else this.setCodeMode(group, mode === 'shared' ? 'shared' : 'unique');
  }

  /** The "Code type" choice: one shared code, a code per winner, or a generated batch. */
  onCodeModeChange(index: number, mode: 'shared' | 'unique' | 'generated'): void {
    const group = this.prizeArray.at(index);
    if (!group || this.voucherRulesLocked(index)) return;
    const kind = group.get('prize_kind')?.value;
    if (kind === 'item' || (mode === 'generated' && kind !== 'voucher')) return;
    if (group.get('code_mode')?.value === mode) return;
    this.setCodeMode(group, mode);
  }

  private setCodeMode(group: AbstractControl, mode: string): void {
    const before = group.get('code_mode')?.value;
    group.patchValue({ code_mode: mode });
    // The two code sources are mutually exclusive server-side — a unique-code
    // prize is refused if it also carries a single code on the row.
    if (mode !== 'shared') group.patchValue({ voucher_code: '' });
    if (mode !== 'unique') group.patchValue({ codes: '' });
    if (mode === 'generated') {
      this.ensureVoucherCurrencies();
      this.prefillVoucherFromPrize(group);
    }
    // A shared code's "how many can claim" and a generated batch's size are different
    // numbers; carrying one into the other would silently misconfigure the prize.
    if ((before === 'generated') !== (mode === 'generated')) group.patchValue({ quantity: null });
  }

  /**
   * A new batch starts from the prize itself: an "AED 60" prize should not open
   * as an empty USD voucher. Only fills what the client has not set, and never
   * touches a prize that already has a batch — those rules came from the server.
   */
  private prefillVoucherFromPrize(group: AbstractControl): void {
    if (group.get('voucher_config')?.value) return;
    const prizeValue = Number(group.get('value')?.value);
    if (group.get('voucher_value')?.value == null && prizeValue > 0) {
      group.patchValue({ voucher_value: prizeValue });
    }
    const currency = String(group.get('currency')?.value || '').toUpperCase();
    const supported = !this.voucherCurrencies.length
      || this.voucherCurrencies.some((c) => c.code === currency);
    if (/^[A-Z]{3}$/.test(currency) && supported && group.get('voucher_currency')?.pristine) {
      group.patchValue({ voucher_currency: currency });
    }
  }

  /**
   * Anything wrong with the prize pool settings, before the save goes out.
   *
   * The API enforces all of this too, but only on the PATCH that runs AFTER the
   * contest is already saved — so an error there arrives as a warning about
   * work the client thought was finished. Catching it here keeps it a form
   * error, where it belongs.
   */
  private prizePoolProblems(): string[] {
    const problems: string[] = [];

    this.prizeArray.controls.forEach((group, index) => {
      const kind = group.get('prize_kind')?.value || 'item';
      if (kind === 'item') return;

      const name = group.get('name')?.value || `Prize ${index + 1}`;
      const mode = group.get('code_mode')?.value || 'none';

      // A shared voucher IS its code — saving the mode without one would leave
      // a prize that awards an empty string.
      if (mode === 'shared' && !(group.get('voucher_code')?.value || '').trim()) {
        problems.push(`${name}: ${this.translate.instant('addContests.prizePool.errors.codeRequired')}`);
      }

      // A unique-code prize with an empty pool saves fine and then cannot award
      // anything, which is worth catching before the contest goes live.
      if (mode === 'unique'
        && !this.parsedCodes(index).length
        && !group.get('code_pool_total')?.value) {
        problems.push(`${name}: ${this.translate.instant('addContests.prizePool.errors.codesRequired')}`);
      }

      // The picker greys out past days, but a date typed in or saved long ago can
      // still be there. Only a date the client set now is theirs to fix: an old
      // prize whose code has since expired must not block an unrelated edit.
      const expires = group.get('expires_at');
      if (mode !== 'generated' && expires?.dirty && this.isPastDay(expires.value)) {
        problems.push(`${name}: ${this.translate.instant('addContests.prizePool.errors.expiryPast')}`);
      }

      // A generated batch is checked against the same limits the API enforces.
      if (mode === 'generated') {
        this.generatedVoucherProblems(group).forEach((p) => problems.push(`${name}: ${p}`));
      }
    });

    return problems;
  }

  /** A code cannot expire before today, so the picker greys out past days. */
  readonly disablePastDay = (d: Date): boolean => this.isPastDay(d);

  isPastDay(value: any): boolean {
    if (!value) return false;
    const time = new Date(value).getTime();
    return !Number.isNaN(time) && time < startOfToday().getTime();
  }

  /** Lines the client has typed into a gift-card pool box, cleaned up. */
  parsedCodes(index: number): string[] {
    const raw = this.prizeArray.at(index)?.get('codes')?.value || '';
    return String(raw)
      // Split on anything that is not a legal code character — the same
      // set the upload endpoint accepts. Saves guessing whether the client
      // pasted newlines, commas, tabs or a mixture.
      .split(/[^A-Za-z0-9._:-]+/)
      .map((c: string) => c.trim())
      .filter((c: string) => c.length > 0);
  }

  /** The prize pool's codes as loaded on edit, per prize group. */
  private uploadedCodesByPrize = new WeakMap<AbstractControl, GiftCardCode[]>();

  uploadedCodes(index: number): GiftCardCode[] {
    const group = this.prizeArray.at(index);
    return (group && this.uploadedCodesByPrize.get(group)) || [];
  }

  giftCardStatusClass(status: GiftCardCodeStatus): string {
    if (status === 'claimed') return 'is-claimed';
    if (status === 'expired') return 'is-expired';
    return 'is-open';
  }

  trackGiftCardCode(_: number, code: GiftCardCode): string {
    return code.id;
  }
  timelineOrDateValidator: ValidatorFn = (
    group: AbstractControl
  ): ValidationErrors | null => {
    const timeline = group.get('timeline')?.value;
    const startDate = group.get('start_date')?.value;
    const endDate = group.get('end_date')?.value;
    if (timeline === 0) {
      const startDateValid = !!startDate;
      const endDateValid = !!endDate;
      return startDateValid && endDateValid ? null : { dateRequired: true };
    } else {
      return timeline ? null : { timelineRequired: true };
    }
  };
  get lastPrizeGroup(): FormGroup {
    return this.prizeArray.at(this.prizeArray.length - 1) as FormGroup;
  }
  showModalsubmit(): void {
    this.isVisiblesubmit = true;
  }
  handleOksubmit(): void {
    this.isVisiblesubmit = false;
  }
  handleCancelsubmit(): void {
    this.isVisiblesubmit = false;
  }
  handleAccessCodeModalOk() {
    this.isAccessCodeModalVisible = false;
    this.markFormAsSaved();
    // Navigate to success page after access code modal
    this.navigateAfterSave();
  }

  /** Editing a live Web QR campaign keeps it published (see onSubmit). */
  private keepsWebQrPublication(): boolean {
    return this.isEditMode && !!webQrApprovalStatus(this.singleContestData);
  }

  /** A campaign that is still live after the save has nothing left to publish, so it
   * returns to the list; everything else continues to the publish step. */
  private navigateAfterSave(): void {
    if (this.keepsWebQrPublication()) {
      this.sharedService.createNotification('success', this.translate.instant('addContests.webQrCampaignUpdated'));
      this.router.navigate(['home/campaigns']);
      return;
    }
    this.router.navigate(['home/campaign-submission-success'], {
      queryParams: { mode: this.isEditMode ? 'edit' : 'new', source: 'add-contest', contestId: this.contestID }
    });
  }
  openAiRegenModal(event?: Event, context: 'banner' | 'prize' | 'level' = 'banner', index: number = 0) {
    if (event) { event.stopPropagation(); event.preventDefault(); }
    this.aiRegenContext = context;
    this.aiRegenTargetIndex = index;
    if (context === 'prize') {
      const name = this.prizeArray.at(index).get('name')?.value || '';
      this.aiRegenPrompt = `Professional product photography, ${name}.`;
    } else if (context === 'level') {
      const name = (this.levels.at(index) as FormGroup).get('name')?.value || '';
      const description = (this.levels.at(index) as FormGroup).get('description')?.value || '';
      this.aiRegenPrompt = `Beautiful photography scene, ${name}. ${description}.`;
    } else {
      const name = this.addContentForm.get('name')?.value || '';
      const description = this.addContentForm.get('details')?.value || '';
      this.aiRegenPrompt = `Stunning wide cinematic banner photograph for a campaign called "${name}". ${description}.`;
    }
    this.aiRegenPreviewUrl = '';
    this.isAiRegenModalVisible = true;
  }

  submitAiRegen() {
    if (!this.aiRegenPrompt.trim() || this.aiRegeneratingBanner) return;
    this.aiRegeneratingBanner = true;
    const chatboltUrl = (environment as any).chatboltApiUrl || 'https://xtro-edge-chatbolt-backend.hf.space';

    fetch(`${chatboltUrl}/api/v1/ai-endpoints/public/execute-by-name/image_regenerator-l9o6p`, {
      method: 'POST',
      headers: {
        'x-api-key': environment.chatboltKeys.endpoints,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ image_prompt: this.aiRegenPrompt.trim() + ' Beautiful golden hour lighting, vibrant rich colors, professional advertising campaign style, ultra high quality, 8k, photorealistic, shallow depth of field, absolutely no text no letters no words no numbers no watermark no overlay no typography' }),
    })
      .then(res => res.json())
      .then((result: any) => {
        this.aiRegeneratingBanner = false;
        const imageUrl = result?.data?.image_url || result?.data?.url || result?.image_url || result?.url || '';
        if (imageUrl) {
          this.aiRegenPreviewUrl = imageUrl;
        } else {
          this.sharedService.createNotification('error', 'AI could not generate an image. Please try again.');
        }
      })
      .catch(() => {
        this.aiRegeneratingBanner = false;
        this.sharedService.createNotification('error', 'AI image generation failed. Please try again.');
      });
  }

  applyAiRegenImage() {
    if (!this.aiRegenPreviewUrl) return;
    if (this.aiRegenContext === 'prize') {
      this.imagePreview[this.aiRegenTargetIndex] = { image_code: this.aiRegenPreviewUrl };
      const control = this.prizeArray.at(this.aiRegenTargetIndex).get('image_code');
      control?.setValue(this.aiRegenPreviewUrl);
      control?.markAsTouched();
      this.sharedService.createNotification('success', 'Prize image updated with AI!');
    } else if (this.aiRegenContext === 'level') {
      this.levelImagePreviews[this.aiRegenTargetIndex] = this.aiRegenPreviewUrl;
      const levelGroup = this.levels.at(this.aiRegenTargetIndex) as FormGroup;
      levelGroup.get('image')?.setValue(this.aiRegenPreviewUrl);
      levelGroup.get('imageFile')?.setValue(this.aiRegenPreviewUrl);
      levelGroup.get('imageFile')?.markAsTouched();
      this.sharedService.createNotification('success', 'Level image updated with AI!');
    } else {
      this.bannerPreviews[this.activeBannerIndex] = this.aiRegenPreviewUrl;
      const bannersArray = this.addContentForm.get('banners') as any;
      if (bannersArray?.at?.(this.activeBannerIndex)) {
        bannersArray.at(this.activeBannerIndex).setValue(this.aiRegenPreviewUrl);
      }
      this.sharedService.createNotification('success', 'Banner updated with AI image!');
    }
    this.isAiRegenModalVisible = false;
    this.aiRegenPreviewUrl = '';
  }

  regenerateBannerWithAi(event?: Event) {
    if (event) { event.stopPropagation(); event.preventDefault(); }
    if (this.aiRegeneratingBanner) return;

    const name = this.addContentForm.get('name')?.value || '';
    const description = this.addContentForm.get('details')?.value || '';
    if (!name && !description) {
      this.sharedService.createNotification('warning', 'Please enter campaign name or description first.');
      return;
    }

    const image_prompt = `Stunning wide cinematic banner photograph for a campaign called "${name}". ${description}. Beautiful golden hour lighting, vibrant rich colors, professional advertising campaign style, ultra high quality, 8k, photorealistic, shallow depth of field, absolutely no text no letters no words no numbers no watermark no overlay no typography`;

    this.aiRegeneratingBanner = true;
    const chatboltUrl = (environment as any).chatboltApiUrl || 'https://xtro-edge-chatbolt-backend.hf.space';

    fetch(`${chatboltUrl}/api/v1/ai-endpoints/public/execute-by-name/image_regenerator-l9o6p`, {
      method: 'POST',
      headers: {
        'x-api-key': environment.chatboltKeys.endpoints,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ image_prompt }),
    })
      .then(res => res.json())
      .then((result: any) => {
        this.aiRegeneratingBanner = false;
        const imageUrl = result?.data?.image_url || result?.data?.url || result?.image_url || result?.url || '';
        if (imageUrl) {
          this.bannerPreviews[this.activeBannerIndex] = imageUrl;
          const bannersArray = this.addContentForm.get('banners') as any;
          if (bannersArray?.at?.(this.activeBannerIndex)) {
            bannersArray.at(this.activeBannerIndex).setValue(imageUrl);
          }
          this.sharedService.createNotification('success', 'Banner regenerated with AI!');
        } else {
          this.sharedService.createNotification('error', 'AI could not generate an image. Please try again.');
        }
      })
      .catch(() => {
        this.aiRegeneratingBanner = false;
        this.sharedService.createNotification('error', 'AI image generation failed. Please try again.');
      });
  }

  openImagePreview(url: string, title: string, event?: Event) {
    if (event) { event.stopPropagation(); event.preventDefault(); }
    if (!url) return;
    this.imagePreviewUrl = url;
    this.imagePreviewTitle = title || 'Image Preview';
    this.isImagePreviewVisible = true;
  }

  copyAccessCode() {
    if (this.accessCode) {
      navigator.clipboard.writeText(this.accessCode).then(() => {
        this.sharedService.createNotification('success', 'Access key copied to clipboard!');
        this.handleAccessCodeModalOk();
      }).catch(() => {
        this.handleAccessCodeModalOk();
      });
    } else {
      this.handleAccessCodeModalOk();
    }
  }

  readonly luqtaWebhookUrl = LUQTA_WEBHOOK_URL;

  copyHardcodedLuqtaWebhookUrl(): void {
    navigator.clipboard.writeText(this.luqtaWebhookUrl).then(() => {
      this.sharedService.createNotification('success', 'Webhook URL copied!');
    }).catch(() => {
      this.sharedService.createNotification('error', 'Failed to copy URL.');
    });
  }

  confirmSaveAsDraft(): void {
    this.showToAdmin = false;

    // Using modal.create() (not modal.confirm()) so the two actions below are real,
    // independent footer buttons — not ng-zorro's Ok/Cancel pair. modal.confirm() maps
    // BOTH the "X" close icon and the Cancel button to the same nzOnCancel callback,
    // which previously meant pressing "X" silently saved a draft just like "Just Save Draft".
    // With custom nzFooter buttons, "X" only dismisses the dialog and saves nothing.
    const modalRef = this.modal.create({
      nzTitle: 'Save Draft?',
      nzContent: `
        <p style="font-family:'Codec Pro',sans-serif; font-size:14px; color:#475467; line-height:1.6; margin-bottom:16px;">
          Your campaign will be saved as a draft. You can also send it to the admin for review.
        </p>
      `,
      nzCentered: true,
      nzWidth: 520,
      nzClosable: true,
      nzClassName: 'contest-styled-modal save-draft-modal',
      nzFooter: [
        {
          label: 'Just Save Draft',
          onClick: () => {
            this.showToAdmin = false;
            this.onSubmit(true);
            modalRef.close();
          }
        },
        {
          label: 'Save & Send to Admin',
          type: 'primary',
          onClick: () => {
            this.showToAdmin = true;
            this.onSubmit(true);
            modalRef.close();
          }
        }
      ]
    });
  }

  // Updated onSubmit method to only validate when NOT saving as draft
  checkContestLimit(): void {
    const noneLeft = hasNoCampaignsLeft(this.contestAllowed, this.contestCreated);
    // The plan's limit gates sending NEW campaigns only. Updating a campaign already sent is
    // never blocked, but sending a saved draft uses up a campaign just like a new one — so a
    // draft saved with none left can't be sent from its edit page either (it still saves).
    if (this.isEditMode) {
      this.contestLimitReached = noneLeft && this.singleContestData?.status === 'draft';
      return;
    }
    this.contestLimitReached = noneLeft;
    if (!noneLeft) return;
    // Called from both the plan load and the duplicate-details load; warn only once. Coming from
    // Create Campaign, the client was already warned there and chose Continue.
    if (this.contestLimitWarningShown || history.state?.[NO_CAMPAIGNS_LEFT_ACKNOWLEDGED]) return;
    this.contestLimitWarningShown = true;
    this.showNoCampaignsLeftModal('addContests.noCampaignsLeftCreateMessage', {
      label: this.translate.instant('addContests.noCampaignsLeftContinue'),
      onClick: () => {},
    });
  }

  private showNoCampaignsLeftModal(messageKey: string, secondary: { label: string; onClick: () => void }): void {
    openNoCampaignsLeftModal(this.modal, this.translate, this.router, messageKey, secondary);
  }

  canSaveCampaign(): boolean {
    if (!this.isEditMode) return !this.router.url.split('?')[0].endsWith('/edit');
    return !!(this.editDetailsLoaded && this.contestID && this.singleContestData?.status
      && canEditCampaign(this.singleContestData));
  }

  onSubmit(saveAsDraft: boolean) {
    // An edit URL can be opened directly, or the campaign can change while details load.
    // Guard BOTH draft and normal saves before constructing either write payload.
    if (!this.canSaveCampaign()) {
      this.sharedService.createNotification('error', this.translate.instant(cannotEditMessageKey(this.singleContestData)));
      return;
    }
    // Prevent duplicate contests from rapid double-clicks while a request is in flight
    if (this.isSubmitting) {
      return;
    }
    // Out of plan campaigns: submitting is blocked, but a draft can still be saved.
    if (!saveAsDraft && this.contestLimitReached) {
      this.showNoCampaignsLeftModal('addContests.noCampaignsLeftSubmitMessage', {
        label: this.translate.instant('addContests.noCampaignsLeftSaveDraft'),
        onClick: () => {
          this.showToAdmin = false;
          this.onSubmit(true);
        },
      });
      return;
    }
    // No AR campaigns left this cycle: a campaign with a Treasure Hunt level (e.g. a copy, or a
    // draft from before the limit) can be saved as a draft, but not sent.
    if (!saveAsDraft && this.arLimitReached()
      && this.levels.controls.some(level => level.get('type')?.value === 'geolocation')) {
      this.showArLockedModal(true);
      return;
    }
    this.sharedService.showLoader();
    if (!saveAsDraft) {
      this.addContentForm.markAllAsTouched();
    }
    // Validate participants does not exceed plan limit
    if (!saveAsDraft && this.hasAdvancedFields && this.maxParticipants > 0) {
      const noOfUsers = Number(this.addContentForm.get('no_of_users')?.value);
      if (noOfUsers && noOfUsers > this.maxParticipants) {
        this.sharedService.hideLoader();
        this.modal.error({
          nzTitle: this.isArabic ? 'تجاوز حد المشاركين' : 'Participant Limit Exceeded',
          nzContent: this.isArabic
            ? `خطتك تسمح بحد أقصى <b>${this.maxParticipants}</b> مشارك، لكنك أدخلت <b>${noOfUsers}</b>. يرجى تقليل عدد المشاركين للمتابعة.`
            : `Your plan allows a maximum of <b>${this.maxParticipants}</b> participants, but you have entered <b>${noOfUsers}</b>. Please reduce the number of participants to continue.`,
          nzCentered: true,
          nzClassName: 'contest-styled-modal contest-modal-error',
          nzOkText: this.isArabic ? 'موافق' : 'OK',
        });
        return;
      }
    }
    // Validate first_to_complete requires at least 2 levels
    if (!saveAsDraft && this.isFirstToComplete() && this.levels.length < 2) {
      this.sharedService.hideLoader();
      this.modal.error({
        nzTitle: 'Validation Error',
        nzContent: '"First To Complete" draw type requires at least 2 levels.',
        nzCentered: true,
        nzClassName: 'contest-styled-modal contest-modal-error',
        nzOkText: 'OK',
      });
      return;
    }
    // The level pickers already block dates outside the campaign Start/End Dates, but levels
    // saved earlier keep their old dates if those dates are later moved — catch that here.
    if (!saveAsDraft) {
      const outOfRangeMessages = this.describeLevelsOutsideContestRange();
      if (outOfRangeMessages.length) {
        this.sharedService.hideLoader();
        this.modal.error({
          nzTitle: this.isArabic ? 'خطأ في التحقق' : 'Validation Error',
          nzContent: [
            this.isArabic
              ? 'يجب أن تكون تواريخ المستويات بين تاريخ بدء الحملة وتاريخ انتهائها:'
              : 'Level dates must be between the campaign Start Date and End Date:',
            ...outOfRangeMessages,
          ].join('<br>'),
          nzCentered: true,
          nzClassName: 'contest-styled-modal contest-modal-error',
          nzOkText: this.isArabic ? 'موافق' : 'OK',
        });
        this.levels.controls.forEach((level) =>
          (level as FormGroup).markAllAsTouched()
        );
        return;
      }
    }
    // Named on its own and taken back to step 1, rather than left inside "check all fields".
    const prizeDateControl = this.addContentForm.get('prize_announce_date');
    if (!saveAsDraft && prizeDateControl?.hasError('beforeEndDate')) {
      this.sharedService.hideLoader();
      prizeDateControl.markAsTouched();
      this.currentStep = 1;
      this.modal.error({
        nzTitle: this.isArabic ? 'خطأ في التحقق' : 'Validation Error',
        nzContent: this.translate.instant('addContestsValidation.prizeDateBeforeEndDate'),
        nzCentered: true,
        nzClassName: 'contest-styled-modal contest-modal-error',
        nzOkText: this.isArabic ? 'موافق' : 'OK',
      });
      return;
    }
    // Switching the format clears these, but a campaign already saved that way still carries them.
    const integratedLevels = this.integratedLevelNumbers();
    if (!saveAsDraft && this.addContentForm.get('format_of_contest')?.value === 'non-integrated'
      && integratedLevels.length) {
      this.sharedService.hideLoader();
      this.currentStep = 3;
      integratedLevels.forEach((number) => this.levels.at(number - 1).get('type')?.markAsTouched());
      this.modal.error({
        nzTitle: this.isArabic ? 'خطأ في التحقق' : 'Validation Error',
        nzContent: this.translate.instant('Levels_Array.Level_type_integrated_only', {
          levels: integratedLevels.join(', '),
        }),
        nzCentered: true,
        nzClassName: 'contest-styled-modal contest-modal-error',
        nzOkText: this.isArabic ? 'موافق' : 'OK',
      });
      return;
    }
    if (!this.addContentForm.valid && !saveAsDraft) {
      this.sharedService.hideLoader();
      // Recursive debug helper
      const logInvalid = (ctrl: AbstractControl, path: string) => {
        if (ctrl.disabled) return;
        if (ctrl instanceof FormGroup) {
          if (ctrl.invalid) console.log(`[INVALID GROUP] ${path}`, ctrl.errors);
          Object.keys(ctrl.controls).forEach(k => logInvalid(ctrl.controls[k], `${path}.${k}`));
        } else if (ctrl instanceof FormArray) {
          if (ctrl.invalid) console.log(`[INVALID ARRAY] ${path}`, ctrl.errors);
          ctrl.controls.forEach((c, i) => logInvalid(c, `${path}[${i}]`));
        } else {
          if (ctrl.invalid) console.log(`[INVALID] ${path}`, ctrl.errors, '| value:', ctrl.value);
        }
      };
      logInvalid(this.addContentForm, 'form');
      const invalidLevelMessages = this.describeInvalidLevels();
      const content = invalidLevelMessages.length
        ? ['Form is invalid! Please fix the following:', ...invalidLevelMessages].join('<br>')
        : 'Form is invalid! Please check all fields.';
      this.modal.error({
        nzTitle: 'Validation Error',
        nzContent: content,
        nzCentered: true,
        nzClassName: 'contest-styled-modal contest-modal-error',
        nzOkText: 'OK',
      });
      this.prizes.controls.forEach((prize) =>
        (prize as FormGroup).markAllAsTouched()
      );
      this.levels.controls.forEach((level) =>
        (level as FormGroup).markAllAsTouched()
      );
      return;
    }
    const form = this.addContentForm;
    const level_data = this.levels.controls.map((group: AbstractControl) => {
      const levelType = group.get('type')?.value;
      const levelPayload: any = {
        type: levelType || null,
        description: group.get('description')?.value || '',
        arabic_name: group.get('arabic_name')?.value || '',
        arabic_description: group.get('arabic_description')?.value || '',
        name: group.get('name')?.value || '',
        client_webhook_url: group.get('client_webhook_url')?.value || '',
        client_variables: group.get('client_variables')?.value || [],
        luqta_webhook_url: group.get('luqta_webhook_url')?.value || '',
        start_date: group.get('level_start_date')?.value || null,
        level_start_date: group.get('level_start_date')?.value || null,
        level_timeline: group.get('level_timeline')?.value || null,
        id: this.isEditMode ? (group.get('id')?.value || null) : null,
        accept_any_answer: !!group.get('accept_any_answer')?.value,
        // Sent as null for every non-quiz level (the quiz branch below sets it): leaving the key
        // out kept the quiz of a level that was switched from quiz to text on the server, and
        // duplicating the campaign then failed with "Level N uses a quiz that does not exist".
        quizzes_id: null,
        quiz_text_en: '',
        quiz_text_ar: '',
      };

      if (levelType === 'survey') {
        const questions = (group.get('survey_questions') as FormArray)?.value || [];
        levelPayload.survey_questions = questions.map((q: any) => {
          const isOpenText = q.question_type === 'open_text';
          const selectionType = isOpenText ? 'single' : (q.answer_selection_type === 'single' ? 'single' : 'multiple');
          // is_multi_select carries the Answer Type toggle (single/multiple) — question_type itself
          // already tells the backend mcq vs open_text, so it doesn't need to be encoded here too.
          const isMultiSelect = !isOpenText && selectionType === 'multiple';
          return {
            id: this.isEditMode ? (q.id || null) : null,
            question_text: q.question_text,
            arabic_question_text: q.arabic_question_text,
            question_type: q.question_type,
            is_required: q.is_required,
            answer_selection_type: selectionType,
            is_multi_select: isMultiSelect,
            options: q.question_type === 'mcq'
              ? (q.options || [])
                .filter((o: any) => o?.option_text?.trim())
                .map((o: any) => ({ option_text: o.option_text, arabic_option_text: o.arabic_option_text }))
              : [],
          };
        });
        levelPayload.min_answers_required = group.get('min_answers_required')?.value || 1;
        levelPayload.question_order = group.get('question_order')?.value || 'fixed';
      } else if (levelType === 'link_task') {
        Object.assign(levelPayload, linkTaskPayload({
          url: group.get('link_task_url')?.value,
          instruction: group.get('link_task_instruction')?.value,
          instructionAr: group.get('link_task_instruction_ar')?.value,
          timer: group.get('link_task_timer')?.value,
          openMode: group.get('link_open_mode')?.value,
        }));
      } else if (levelType === 'text') {
        // Use getRawValue() to include values from any disabled controls
        const textContentArray = (group.get('textContent') as FormArray)?.getRawValue() || [];
        const filledTextContent = textContentArray.filter((item: any) => item?.text?.trim());
        levelPayload.textContent = filledTextContent.map((item: any) => item.text).join(' || ');
        levelPayload.arabic_textContent = filledTextContent.map((item: any) => item.arabic_text || '').join(' || ');
      } else if (levelType === 'geolocation') {
        levelPayload.point_areas = this.buildPointAreas(group);
      } else if (levelType === 'receipt') {
        levelPayload.receipt_rules = this.buildReceiptRulesPayload(group);
      } else if (levelType === 'qr') {
        levelPayload.qrData = group.get('qrData')?.value || '';
        levelPayload.textContent = group.get('qrData')?.value || '';
      } else if (levelType === 'image') {
        const levelIndex = this.levels.controls.indexOf(group);
        // Check form value first, then fallback to preview
        let imageValue = group.get('image')?.value || this.levelImagePreviews[levelIndex] || '';

        // Backend expects image object with path field
        if (typeof imageValue === 'string' && imageValue.startsWith('data:image')) {
          // Base64 data URL
          levelPayload.image = { path: imageValue };
        } else if (typeof imageValue === 'string' && /^https?:\/\//i.test(imageValue)) {
          // AI-generated image URL (Together AI, etc.) — wrap in path object
          levelPayload.image = { path: imageValue, url: imageValue };
        } else if (typeof imageValue === 'object' && imageValue?.path) {
          // Already an image object with path
          levelPayload.image = imageValue;
        } else if (typeof imageValue === 'object' && imageValue?.url) {
          // Image object with url but no path
          levelPayload.image = { path: imageValue.url, url: imageValue.url, ...imageValue };
        } else if (typeof imageValue === 'string' && imageValue.trim() !== '') {
          // Any other non-empty string (relative path etc.)
          levelPayload.image = { path: imageValue };
        } else {
          levelPayload.image = null;
        }
      }

      if (levelType === 'luqta_weebhook') {
        levelPayload.luqta_webhook_url =
          group.get('luqta_webhook_url')?.value || '';
      }

      if (levelType === 'quiz') {
        levelPayload.quizzes_id = group.get('quizzes_id')?.value || null;
        levelPayload.quiz_text_en = group.get('quiz_text_en')?.value || '';
        levelPayload.quiz_text_ar = group.get('quiz_text_ar')?.value || '';
      }

      return levelPayload;
    });

    const hasIntegratedLevelType = level_data.some(
      (level) => level?.type === 'client_webhook' || level?.type === 'luqta_weebhook'
    );

    const prize_data = this.prizes.controls.map((group: AbstractControl, index: number) => {
      const imageCode = group.get('image_code')?.value || '';
      let imageObj;
      if (imageCode && !imageCode.startsWith('data:') && this.originalPrizeImageObjects[index]) {
        const orig = this.originalPrizeImageObjects[index];
        imageObj = {
          access: orig.access || 'public',
          path: orig.path || '',
          name: orig.name || '',
          type: 'public',
          size: 0,
          mime: 0,
          meta: 0,
          url: orig.url || '',
        };
      } else {
        imageObj = {
          name: form.get('name')?.value || '',
          type: form.get('type')?.value || '',
          size: 0,
          meta: 0,
          mime: 0,
          path: imageCode,
          url: imageCode,
        };
      }
      const arabicNameVal = (group.get('arabic_name')?.value || '').trim();
      return {
        name: group.get('name')?.value || '',
        arabic_name: arabicNameVal,
        arabic_prize_name: arabicNameVal,
        value: group.get('value')?.value || 0,
        currency: group.get('currency')?.value || 'USD',
        image: imageObj,
        categories: this.extractCategoryIds(group.get('category_type')?.value),
        id: group.get('id')?.value || null,
      };
    });

    // ✅ FIX: Get type value properly - prioritize existing data in edit mode
    const contestType = form.get('type')?.value ||
      (this.isEditMode && this.singleContestData?.type) ||
      null;

    // Contest Category — category_id holds a single cascader path (e.g. [parentId, leafId]); send the leaf id.
    const categoryPath = form.get('category_id')?.value;
    const contestCategoryId = Array.isArray(categoryPath) && categoryPath.length > 0
      ? categoryPath[categoryPath.length - 1]
      : null;

    // ✅ VALIDATION: Type is required for final submission (not draft)
    if (!contestType && !saveAsDraft) {
      this.sharedService.hideLoader();
      this.modal.error({
        nzTitle: 'Validation Error',
        nzContent: 'Campaign type is required for submission.',
        nzCentered: true,
        nzClassName: 'contest-styled-modal contest-modal-error',
        nzOkText: 'OK',
      });
      return;
    }

    // ✅ BASE PAYLOAD matching request_contest_client API
    const basePayload: any = {
      contest_id: this.isEditMode ? this.contestID : 0,
      no_of_users: 0,
      _contest_prize: prize_data,
      level_details: level_data,
      name: (form.get('name')?.value || '').trim(),
      arabic_name: (form.get('arabic_name')?.value || '').trim(),
      arabic_details: (form.get('arabic_details')?.value || '').trim(),
      details: (form.get('details')?.value || '').trim(),
      game_name: '',
      type: contestType,
      client_allow_list:
        contestType === 'private'
          ? (form.get('client_allow_list')?.value || []).map((entry: string) => String(entry).trim()).filter(Boolean)
          : [],
      kyc_tier: form.get('kyc_tier')?.value || 'light',
      format_of_contest: form.get('format_of_contest')?.value || '',
      // The API resolves country names; the All Countries marker belongs only to the picker.
      location: this.locationForSave(),
      gender: form.get('gender')?.value || 'both',
      prize_announce_date: !this.isNoDraw && form.get('prize_announce_date')?.value
        ? new Date(form.get('prize_announce_date')?.value).toISOString()
        : null,
      start_date: form.get('start_date')?.value
        ? new Date(form.get('start_date')?.value).toISOString()
        : null,
      end_date: form.get('end_date')?.value
        ? new Date(form.get('end_date')?.value).toISOString()
        : null,
      number_of_winners: this.isNoDraw ? 0 : form.get('number_of_winners')?.value || 0,
      banners: (form.get('banners_code') as FormArray).value.map(
        (value: string, index: number) => {
          if (value && !value.startsWith('data:')) {
            if (this.originalBannerObjects[index]) {
              const orig = this.originalBannerObjects[index];
              return {
                access: orig.access || 'public',
                path: orig.path || '',
                name: orig.name || '',
                type: 'public',
                size: 0,
                mime: 0,
                meta: 0,
                url: orig.url || '',
              };
            }
          }
          return {
            name: form.get('name')?.value || '',
            type: form.get('type')?.value || '',
            size: 0,
            meta: 0,
            mime: 0,
            path: value,
            url: value,
          };
        }
      ),
      total_points: 0,
      status: 'pending',
      link: '',
      is_draw_enabled: false,
      is_draw_completed: false,
      metadata: {},
      sharingSetting: form.get('sharingSetting')?.value || '',
      View_Type: 'Recent Contest',
      plan_id: 0,
      user_ids: [],
      total_views: 0,
      Clicks: 0,
      is_age_restricted: !!form.get('is_age_restricted')?.value,
      age: Array.isArray(form.get('age')?.value) && form.get('age')?.value.length > 0
        ? form.get('age')?.value
        : ['All'],
      edited_by: 0,
      notification: '',
      draw_type: '',
      all_completers: false,
      draw_target_level: 0,
      business_objective: form.get('business_objective')?.value || [],
      // Keep the saved destination on update. The old hardcoded '1' turned every
      // Web QR-only edit into a request for LuQta app publication.
      is_visible_on_luqta_app: this.isEditMode && isWebQrOnlyApproved(this.singleContestData)
        ? '0'
        : (this.isEditMode ? (this.singleContestData?.is_visible_on_luqta_app ?? '1') : '1'),
      show_to_admin: false,
      contest_category: contestCategoryId,
    };

    // Include SDK fields only if user has SDK access
    if (this.hasAdvancedFields) {
      basePayload.draw_type = form.get('draw_type')?.value || '';
      //  Only ever true on Instant-Result, whatever is left in the control.
      // 
      //  number_of_winners keeps being sent alongside it: the column is NOT
      //  NULL, and the backend ignores the number rather than expecting it to
      //  be absent. That also means the client's last choice survives the
      //  toggle being switched off again.
      basePayload.all_completers =
        basePayload.draw_type === 'instant_result'
        && form.get('all_completers')?.value === true;
      basePayload.no_of_users = form.get('no_of_users')?.value || 0;
    }

    // A CHOSEN DRAW TYPE IS A DRAW.
    //
    // is_draw_enabled was hardcoded false a few lines above and never revisited, so every
    // engagement a business created was saved with the draw switched OFF — even the ones where
    // they had picked Random Draw. The server honours an explicit false (the admin panel has a
    // real "No Draw" button and means it), so the flag being wrong here was enough: the admin
    // wizard reads it before the type and showed "No Draw" for a contest stored as RANDOM_DRAW.
    //
    // Derived from the same thing the server would derive it from, rather than sent as its own
    // fact that can disagree with the type beside it.
    basePayload.is_draw_enabled =
      !!basePayload.draw_type && basePayload.draw_type !== 'no_draw';

    // Include points_pool_per_contest if provided
    const pointsPool = form.get('points_pool_per_contest')?.value;
    if (pointsPool !== null && pointsPool !== '' && pointsPool !== undefined) {
      basePayload.points_pool_per_contest = Number(pointsPool);
    }

    // Ensure format_of_contest always has a valid value
    if (!basePayload.format_of_contest) {
      basePayload.format_of_contest = hasIntegratedLevelType ? 'integrated' : 'non-integrated';
      this.addContentForm.get('format_of_contest')?.setValue(basePayload.format_of_contest, { emitEvent: false });
    }

    // ✅ Status handling
    if (saveAsDraft) {
      basePayload.status = 'draft';
      basePayload.show_to_admin = this.showToAdmin;
    } else if (this.isEditMode && this.singleContestData?.status === 'draft') {
      basePayload.status = 'pending';
    } else if (this.keepsWebQrPublication()) {
      // A live Web QR campaign stays published while it is edited instead of
      // dropping back to "pending" and off the web.
      basePayload.status = webQrApprovalStatus(this.singleContestData);
    }

    // For update, merge existing contest data
    if (this.isEditMode) {
      if (!this.contestID) {
        this.sharedService.hideLoader();
        this.sharedService.createNotification('error', 'Campaign ID is missing for update!');
        return;
      }
      // An emptied prize date keeps the saved one, but only while that is still after the end:
      // a draft whose end moved past it would otherwise be saved announcing before it ends.
      const savedPrizeDate = this.singleContestData?.prize_announce_date;
      const savedPrizeStillAfterEnd = !!savedPrizeDate && (!basePayload.end_date
        || new Date(savedPrizeDate).getTime() > new Date(basePayload.end_date).getTime());
      basePayload.prize_announce_date = basePayload.prize_announce_date
        ? basePayload.prize_announce_date
        : savedPrizeStillAfterEnd && !this.isNoDraw ? savedPrizeDate : null;
      basePayload.banners = basePayload.banners && basePayload.banners.length > 0
        ? basePayload.banners
        : this.singleContestData?.banners || [];
    }

    //  Prize pool settings are checked before anything is sent.
    // 
    //  Not on a draft: a draft is incomplete by definition, and refusing to save
    //  one over a half-filled voucher would be the wrong trade.
    if (!saveAsDraft) {
      const poolProblems = this.prizePoolProblems();
      if (poolProblems.length) {
        this.sharedService.hideLoader();
        this.modal.error({
          nzTitle: this.translate.instant('addContests.prizePool.errors.title'),
          // Joined with a separator rather than a newline: nzContent renders
          // as HTML, where a raw newline is just whitespace.
          nzContent: poolProblems.join('  ·  '),
          nzCentered: true,
          nzClassName: 'contest-styled-modal contest-modal-error',
          nzOkText: 'OK',
        });
        return;
      }
    }

    // All synchronous validation has passed — lock submission until the API call resolves
    this.isSubmitting = true;

    if (saveAsDraft) {
      // ✅ DRAFT: Always use SAVE_CONTEST_DRAFT (create & update use the same API;
      // contest_id in the payload tells the backend whether to insert or update).
      const draftPayload: any = {
        ...basePayload,
        _contest_prize: prize_data,
        _levels_of_contest: level_data,
        user_id: this.authService.getUserId() || this.singleContestData?.user_id,
      };
      delete draftPayload.level_details;

      const draftCall = this.pagesService.update('SAVE_CONTEST_DRAFT', draftPayload);

      draftCall
        .pipe(
          timeout(SAVE_TIMEOUT_MS),
          catchError(err => {
            if (err.name === 'TimeoutError') {
              return throwError(() => ({ error: { message: SAVE_TIMEOUT_MESSAGE } }));
            }
            return throwError(() => err);
          })
        )
        .subscribe({
          next: async (response: any) => {
            try {
              //  Prize pools go out here too, for the same reason as on submit:
              //  they are keyed by prize id, and the draft endpoint now keeps
              //  those ids stable across saves and returns them.
              // 
              //  skipIncomplete, because a draft is unfinished by definition —
              //  a voucher with no code yet is simply not sent.
              await firstValueFrom(this.savePrizePools(response, true));

              this.sharedService.hideLoader();
              this.isSubmitting = false;
              this.dataService.refreshContestData();
              this.markFormAsSaved();

              this.modal.success({
                nzTitle: this.isEditMode ? 'Draft Updated' : 'Draft Saved',
                nzContent: this.isEditMode
                  ? 'Your campaign draft has been updated successfully.'
                  : 'Your campaign has been saved as a draft successfully.',
                nzCentered: true,
                nzClassName: 'contest-styled-modal contest-modal-success',
                nzOkText: 'OK',
              });
              this.router.navigate(['home/campaigns']);

              this.addContentForm.reset();
              (this.addContentForm.get('banners_code') as FormArray).clear();
              this.prizes.clear();
              this.levels.clear();
              this.prizeCategoryOptions = [];
              this.prizeArray.push(this.createPrize());
              this.prizeCategoryOptions.push(this.cloneCategoriesTree());
              this.imagePreview = [];
            } catch (error: any) {
              this.sharedService.hideLoader();
              this.isSubmitting = false;
              this.showSaveError(error?.error?.message || 'Failed to save engagement data');
            }
          },
          error: (err) => {
            this.sharedService.hideLoader();
            this.isSubmitting = false;

            // The integrated number is a ceiling for integrated campaigns, not a reserved share,
            // so a plan-limit error is shown as is instead of forcing the campaign to integrated.
            const errMsg = String(err.error?.message || err?.message || '');
            this.showSaveError(errMsg || 'Failed to save draft');
          },
        });
    } else {
      // ✅ SUBMIT: Single REQUEST_CONTEST_CLIENT API call for create & update
      this.pagesService.update('REQUEST_CONTEST_CLIENT', basePayload)
        .pipe(
          timeout(SAVE_TIMEOUT_MS),
          catchError(err => {
            if (err.name === 'TimeoutError') {
              return throwError(() => ({ error: { message: SAVE_TIMEOUT_MESSAGE } }));
            }
            return throwError(() => err);
          })
        )
        .subscribe({
          next: (response: any) => {
            //  Prize pools go out before anything else happens.
            // 
            //  They are keyed by prize id, which only exists once this response
            //  is in — and the success branch below resets the form and
            //  navigates away, so anything fired alongside it would be racing a
            //  teardown.
            this.savePrizePools(response).subscribe((poolErrors) => {
              if (poolErrors.length) {
                // The contest saved; only the fulfilment settings did not. Said
                // plainly rather than dressed up as a failed save.
                this.sharedService.createNotification(
                  'warning',
                  this.translate.instant('addContests.prizePool.saveFailed') + ' ' + poolErrors.join(' · ')
                );
              }
              this.finishContestSave(response);
            });
          },
          error: (err) => {
            this.sharedService.hideLoader();
            this.isSubmitting = false;

            const errMsg = String(err.error?.message || err?.message || '');
            this.showSaveError(errMsg || 'Failed to save campaign');
          },
        });
    }
  }

  /** Save/submit failure popup. Backend plan-limit errors are reworded and get a purple OK. */
  private showSaveError(message: string): void {
    const planLimit = formatPlanLimitMessage(message);
    this.modal.error({
      nzTitle: 'Error',
      nzContent: planLimit.text,
      nzCentered: true,
      nzClassName: 'contest-styled-modal contest-modal-error' + (planLimit.isPlanLimit ? ' contest-modal-plan-limit' : ''),
      nzOkText: 'OK',
    });
  }

  /**
   * Everything the save used to do inline, once the prize pools are away.
   *
   * Split out only so the pool calls can complete first — the body below
   * resets the form and navigates, which would pull the rug from under them.
   */
  private finishContestSave(response: any): void {
    this.sharedService.hideLoader();
    this.isSubmitting = false;
    this.dataService.refreshContestData();
    this.markFormAsSaved();

    const resData = response?.contest || response?.data || response;
    this.contestID = resData.id || resData.contest_id || this.contestID;
    // An allow-listed campaign admits participants by phone number; the API still
    // mints an access code for every private campaign, but it is not the way in.
    const usesAllowList = this.allowListNumbers.length > 0 ||
      (Array.isArray(resData.client_allow_list) && resData.client_allow_list.length > 0);
    if (resData.type === 'private' && resData.access_code && !usesAllowList) {
      // Keep the form intact behind the Access Code modal — it's reset
      // only once the user dismisses the modal (handleAccessCodeModalOk).
      this.accessCode = resData.access_code;
      this.isAccessCodeModalVisible = true;
    } else {
      this.navigateAfterSave();

      this.addContentForm.reset();
      (this.addContentForm.get('banners_code') as FormArray).clear();
      this.prizes.clear();
      this.levels.clear();
      this.prizeCategoryOptions = [];
      this.prizeArray.push(this.createPrize());
      this.prizeCategoryOptions.push(this.cloneCategoriesTree());
      this.imagePreview = [];
    }
  }
  // Async version that returns a promise
  async setPrizeAsync(id: string | null, data?: any[]): Promise<void> {
    return new Promise((resolve, reject) => {
      let prizePayload: any[] = [];
      if (data && data.length > 0) {
        prizePayload = data.map((item, index) => {
          const arName = item.arabic_prize_name || item.arabic_name || '';
          return {
            name: item.name,
            arabic_name: arName,
            arabic_prize_name: arName,
            value: item.value,
            image: item.image,
            categories: item.categories || null,
            id: item.id || null,
          };
        });
      }
      const prizeData = {
        _contest_prize: prizePayload,
        contest_id: id,
      };
      this.authService.login('REQUEST_CONTEST_SET_PRIZE', prizeData)
        .pipe(
          timeout(15000),
          catchError(err => {
            if (err.name === 'TimeoutError') {
              return throwError(() => ({ error: { message: 'Request timed out. Please try again.' } }));
            }
            return throwError(() => err);
          })
        )
        .subscribe({
          next: () => resolve(),
          error: (error) => {
            this.sharedService.createNotification('error', error.error?.message || 'Failed to save prizes');
            reject(error);
          },
        });
    });
  }

  // Async version that returns a promise
  async setLevelAsync(id: string | null, data?: any[]): Promise<void> {
    return new Promise(async (resolve, reject) => {
      let levelsData: any;
      if (data && data?.length > 0) {
        levelsData = {
          level_details: data,
          contest_id: id,
        };
      } else {
        const level_details = await this.getLevelsData();
        levelsData = {
          level_details,
          contest_id: id,
        };
      }
      this.authService.login('REQUEST_CONTEST_SET_LEVEL', levelsData)
        .pipe(
          timeout(15000),
          catchError(err => {
            if (err.name === 'TimeoutError') {
              return throwError(() => ({ error: { message: 'Request timed out. Please try again.' } }));
            }
            return throwError(() => err);
          })
        )
        .subscribe({
          next: () => resolve(),
          error: (error) => {
            this.sharedService.createNotification('error', error.error?.message || 'Failed to save levels');
            reject(error);
          },
        });
    });
  }

  async setLevel(id: string | null, data?: any[]) {
    this.sharedService.showLoader();
    let levelsData: any;
    if (data && data?.length > 0) {
      levelsData = {
        level_details: data,
        contest_id: id,
      };
    } else {
      const level_details = await this.getLevelsData();
      levelsData = {
        level_details,
        contest_id: id,
      };
    }
    this.authService.login('REQUEST_CONTEST_SET_LEVEL', levelsData).subscribe({
      next: (response: any) => {
        this.sharedService.hideLoader();
        // this.router.navigate(['home/campaigns']);
      },
      error: (error) => {
        this.sharedService.createNotification('error', error.error.message);
        this.sharedService.hideLoader();
      },
    });
  }
  setPrize(id: string | null, data?: any[]) {
    this.sharedService.showLoader();
    let prizePayload: any[] = [];
    if (data && data.length > 0) {
      // Use passed-in data directly (from onSubmit)
      prizePayload = data.map((item, index) => {
        const arName = item.arabic_prize_name || item.arabic_name || '';
        return {
          name: item.name,
          arabic_name: arName,
          arabic_prize_name: arName,
          value: item.value,
          image: item.image,
          categories: item.categories || null,
          id: item.id || null,
        };
      });
    } else {
      // Fallback to form data if not passed
      const prizesArray = this.addContentForm.get(
        '_contest_prize'
      ) as FormArray;
      prizePayload = prizesArray.controls.map((group) => {
        const arName = (group.get('arabic_name')?.value || '').trim();
        return {
          name: group.get('name')?.value,
          arabic_name: arName,
          arabic_prize_name: arName,
          value: group.get('value')?.value,
          currency: group.get('currency')?.value || 'USD',
          image: {
            name: this.addContentForm.get('name')?.value,
            type: this.addContentForm.get('type')?.value,
            size: 0,
            meta: 0,
            mime: 0,
            path: group.get('image_code')?.value,
            url: group.get('image_code')?.value,
          },
          categories: this.extractCategoryIds(group.get('category_type')?.value),
          id: null,
        };
      });
    }
    const prizeData = {
      _contest_prize: prizePayload,
      contest_id: id,
    };
    this.authService.login('REQUEST_CONTEST_SET_PRIZE', prizeData).subscribe({
      next: () => {
        this.sharedService.hideLoader();
        this.sharedService.createNotification(
          'success',
          'Prizes saved successfully'
        );
      },
      error: (error) => {
        this.sharedService.hideLoader();
        this.sharedService.createNotification(
          'error',
          error.error?.message || 'Failed to save prizes'
        );
      },
    });
  }
  triggerProfileFileInput(): void {
    this.profileFileInput.nativeElement.click();
  }
  triggerPrizeFileInput(index: number) {
    const fileInput = document.getElementById(
      `prizeFileInput${index}`
    ) as HTMLInputElement;
    fileInput?.click();
  }
  private validateCampaignImage(file: File): boolean {
    if (!isSupportedCampaignImage(file)) {
      this.sharedService.createNotification('error', this.translate.instant('addContestsValidation.imageFormat'));
      return false;
    }
    if (file.size > 2 * 1024 * 1024) {
      this.sharedService.createNotification('error', this.translate.instant('addContestsValidation.imageSize'));
      return false;
    }
    return true;
  }

  onBannerFileSelected(event: Event) {
    const input = event.target as HTMLInputElement;
    const files = input.files;
    const bannersArray = this.addContentForm.get('banners_code') as FormArray;
    if (files && files.length > 0) {
      Array.from(files).forEach((file) => {
        if (!this.validateCampaignImage(file)) return;
        const reader = new FileReader();
        reader.onload = () => {
          const base64Data = reader.result as string;
          this.bannerPreviews.push(base64Data);
          bannersArray.push(this.fb.control(base64Data));
        };
        reader.readAsDataURL(file);
      });
    }
    input.value = '';
  }
  // Handling prize image selection
  onPrizeFileSelected(event: Event, index: number) {
    this.handleFileSelection(event, 'image_code', index);
  }
  handleFileSelection(event: any, field: string, index: number) {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    input.value = '';
    if (file && this.validateCampaignImage(file)) {
      const reader = new FileReader();
      reader.onload = () => {
        if (!Array.isArray(this.imagePreview)) {
          this.imagePreview = [];
        }
        const base64 = reader.result as string;
        this.imagePreview[index] = { [field]: base64 };
        const control = this.prizeArray.at(index).get(field);
        control?.setValue(base64);
        control?.markAsTouched();
        control?.updateValueAndValidity();
      };
      reader.readAsDataURL(file);
    }
  }
  prevBanner(event: Event): void {
    event.stopPropagation();
    if (this.activeBannerIndex > 0) {
      this.activeBannerIndex--;
    }
  }

  nextBanner(event: Event): void {
    event.stopPropagation();
    if (this.activeBannerIndex < this.bannerPreviews.length - 1) {
      this.activeBannerIndex++;
    }
  }

  goToBanner(index: number, event: Event): void {
    event.stopPropagation();
    this.activeBannerIndex = index;
  }

  // For removing uploaded banner
  removeBanner(index: number): void {
    // Remove from preview array
    this.bannerPreviews.splice(index, 1);

    // Remove from FormArray
    const bannersArray = this.addContentForm.get('banners_code') as FormArray;
    if (index < bannersArray.length) {
      bannersArray.removeAt(index);
    }

    // ✅ Update singleContestData if in edit mode
    if (this.isEditMode && this.singleContestData?.banners) {
      this.singleContestData.banners.splice(index, 1);
    }

    // Reset active index if needed
    if (this.activeBannerIndex >= this.bannerPreviews.length) {
      this.activeBannerIndex = Math.max(0, this.bannerPreviews.length - 1);
    }
  }
  get levels() {
    return this.addContentForm.get('levels_of_contest') as FormArray;
  }
  private updateLevels(count: number) {
    const currentLength = this.levels.length;
    // If increasing, add new groups; if decreasing, remove extras
    if (count > currentLength) {
      for (let i = currentLength; i < count; i++) {
        const newLevelGroup = this.fb.group({
          id: [null],
          type: [null, Validators.required],
          name: ['', [Validators.required, Validators.maxLength(LEVEL_NAME_MAX)]],
          arabic_name: ['', [Validators.required, Validators.maxLength(LEVEL_NAME_MAX)]],
          description: ['', [Validators.required, Validators.maxLength(1000)]],
          arabic_description: ['', [Validators.required, Validators.maxLength(1000)]],
          level_start_date: [null, Validators.required],
          level_timeline: [null, Validators.required],
          textContent: this.fb.array([this.createTextContentGroup()]),
          imageFile: [null],
          qrData: [''],
          client_webhook_url: [''],
          luqta_webhook_url: [''],
          accept_any_answer: [false],
          client_variables: this.fb.array([]),
          quizzes_id: [null],
          quiz_text_en: [''],
          quiz_text_ar: [''],
          survey_questions: this.fb.array([this.createSurveyQuestion()]),
          min_answers_required: [1, [Validators.min(1)]],
          question_order: ['fixed'],
          link_task_url: [''],
          link_task_instruction: [''],
          link_task_instruction_ar: [''],
          link_open_mode: [DEFAULT_LINK_OPEN_MODE],
          link_task_timer: [30, [Validators.min(this.globalLevelSettings?.link_task_min_timer || 10), Validators.max(this.globalLevelSettings?.link_task_max_timer || 99999), wholeNumberValidator]],
          // Geolocation (Treasure Hunt) fields — must match addNewLevel(). Without
          // them, picking "Treasure Hunt" on a level created by the level-count
          // spinner leaves the template binding to a null ar_config/ar_points.
          ar_type: [DEFAULT_AR_TYPE],
          is_location_fixed: [true],
          ar_config: this.createArConfigGroup(DEFAULT_AR_TYPE),
          ar_points: this.fb.array([this.createPointGroup({}, 0)]),
          receipt_rules: this.createReceiptRulesGroup(),
        }, { validators: this.levelDateOrderValidator });

        this.subscribeToLevelChanges(newLevelGroup, i);
        this.applyInitialLevelTypeValidators(newLevelGroup);
        this.levels.push(newLevelGroup);
        this.syncPointLocationValidators(this.levels.length - 1);
        this.levelQrCodes.push(null);
        this.levelImagePreviews.push(null);
        this.isImageVisible.push(false);
      }
    } else if (count < currentLength) {
      for (let i = currentLength - 1; i >= count; i--) {
        this.levels.removeAt(i);
        this.levelQrCodes.splice(i, 1);
        this.levelImagePreviews.splice(i, 1);
        this.isImageVisible.splice(i, 1);
      }
      // this.calculateTotalPoints();
    }
    // If count == currentLength, do nothing (keep existing data)
  }

  subItems(index: number): FormArray {
    const group = this.levels.at(index) as FormGroup;
    return (group.get('client_variables') as FormArray) ?? this.fb.array([]);
  }
  deleteSubItem(levelIndex: number, subIndex: number) {
    const clientVariables = this.subItems(levelIndex);
    if (clientVariables && clientVariables.length > subIndex) {
      clientVariables.removeAt(subIndex);
      (this.levels.at(levelIndex) as FormGroup).updateValueAndValidity();
    }
  }
  // add subItem to a given item
  addSubItem(itemIndex: number) {
    this.subItems(itemIndex).push(
      this.fb.group({
        variable_name: ['', [Validators.required]],
        display_name: ['', [Validators.required]],
        datatype: ['', [Validators.required]],
      })
    );
    (this.levels.at(itemIndex) as FormGroup).updateValueAndValidity();
  }

  private createClientVariableGroup(): FormGroup {
    return this.fb.group({
      variable_name: ['', [Validators.required]],
      display_name: ['', [Validators.required]],
      datatype: ['', [Validators.required]],
    });
  }

  ngOnDestroy(): void {
    this.levelTypeSwitchPrompt?.destroy();
    this.stopContestCountdown();
    this.langChangeSub?.unsubscribe();
    this.businessObjectiveObserver?.disconnect();
    document.removeEventListener('scroll', this.closeCascadersOnScroll, true);
  }

  @HostListener('document:keydown.escape')
  closeOpenCascaders(): void {
    const open = this.cascaders?.filter(cascader => cascader.menuVisible) ?? [];
    if (open.length) {
      this.ngZone.run(() => open.forEach(cascader => cascader.closeMenu()));
    }
  }

  // Get textContent FormArray for a specific level
  getTextContents(levelIndex: number): FormArray {
    const group = this.levels.at(levelIndex) as FormGroup;
    return (group.get('textContent') as FormArray) ?? this.fb.array([]);
  }

  getAcceptAnyAnswerControl(levelIndex: number): FormControl {
    return (this.levels.at(levelIndex) as FormGroup).get('accept_any_answer') as FormControl;
  }

  private getTextAnswerValidators(acceptAny: boolean): ValidatorFn[] {
    return acceptAny
      ? [Validators.maxLength(1000)]
      : [Validators.required, Validators.maxLength(1000)];
  }

  // Each accepted text answer field
  private createTextContentGroup(acceptAny: boolean = false): FormGroup {
    const validators = this.getTextAnswerValidators(acceptAny);
    return this.fb.group({
      text: ['', validators],
      arabic_text: [''],
    });
  }

  /**
   * A textContent row for a level of the given type.
   *
   * Only a `text` level asks the admin for answers, so only there is the row
   * validated. Every other type still carries the array -- the level group has
   * one shape whatever the type -- but its row must be free of validators:
   * the field is not rendered for those types, so a required error on it could
   * never be seen, let alone fixed. A form stuck invalid with nothing to
   * correct is exactly what this prevents.
   */
  private createTextContentGroupForType(type: string, acceptAny: boolean): FormGroup {
    return type === 'text'
      ? this.createTextContentGroup(acceptAny)
      : this.fb.group({ text: [''], arabic_text: [''] });
  }

  private updateTextAnswerControls(levelGroup: FormGroup, acceptAny: boolean): void {
    if (levelGroup.get('type')?.value !== 'text') return;

    const textContentArray = levelGroup.get('textContent') as FormArray;
    if (!textContentArray) return;

    if (textContentArray.length === 0) {
      textContentArray.push(this.createTextContentGroup(acceptAny));
    }

    if (acceptAny && textContentArray.length > 1) {
      for (let i = textContentArray.length - 1; i > 0; i--) {
        textContentArray.removeAt(i);
      }
    }

    const validators = this.getTextAnswerValidators(acceptAny);
    textContentArray.controls.forEach((control) => {
      const group = control as FormGroup;
      group.get('text')?.setValidators(validators);
      group.get('arabic_text')?.clearValidators();
      if (acceptAny) {
        group.get('text')?.setValue('', { emitEvent: false });
        group.get('arabic_text')?.setValue('', { emitEvent: false });
        group.disable({ emitEvent: false });
      } else {
        group.enable({ emitEvent: false });
      }
      group.get('text')?.updateValueAndValidity({ emitEvent: false });
      group.get('arabic_text')?.updateValueAndValidity({ emitEvent: false });
    });
    textContentArray.updateValueAndValidity({ emitEvent: false });
    levelGroup.updateValueAndValidity({ emitEvent: false });
  }

  // Add a new text content field
  addTextContent(levelIndex: number) {
    const textContents = this.getTextContents(levelIndex);
    const acceptAny = !!this.getAcceptAnyAnswerControl(levelIndex)?.value;
    if (acceptAny) {
      return;
    }
    textContents.push(this.createTextContentGroup(acceptAny));
  }

  // Remove a text content field
  removeTextContent(levelIndex: number, textIndex: number) {
    const textContents = this.getTextContents(levelIndex);
    if (textContents && textContents.length > 1) {
      textContents.removeAt(textIndex);
    }
  }
  // Both min_answers_required (survey) and link_task_timer (link task) are validated against
  // globalLevelSettings, a backend-configured minimum/maximum that can be tightened over time.
  // A level built before that setting loaded (race with getContestDetails on edit), or an
  // existing level whose stored value predates a since-raised minimum, would otherwise carry a
  // value that fails today's validator with no visible per-field error — the level looks fully
  // filled in but silently makes the whole form invalid. Bumping the value up to the current
  // minimum (never down, and only when it's actually out of range) keeps stored/legitimate
  // higher values untouched while guaranteeing the control is never invalid purely from settings
  // having changed since the value was set.
  private applySurveyMinAnswersValidator(levelGroup: FormGroup, required: boolean): void {
    const minAnswers = this.globalLevelSettings?.survey_min_answers || 1;
    const ctrl = levelGroup.get('min_answers_required');
    if (ctrl && Number(ctrl.value) < minAnswers) {
      ctrl.setValue(minAnswers, { emitEvent: false });
    }
    ctrl?.setValidators(required ? [Validators.required, Validators.min(minAnswers)] : [Validators.min(minAnswers)]);
    ctrl?.updateValueAndValidity({ emitEvent: false });
  }

  // For the timer input: stops ".", "e", "-" and a non-digit paste before they land.
  readonly blockNonDigitKey = blockNonDigitKey;
  readonly blockNonDigitPaste = blockNonDigitPaste;

  private applyLinkTaskTimerValidator(levelGroup: FormGroup, required: boolean): void {
    const minTimer = this.globalLevelSettings?.link_task_min_timer || 10;
    const maxTimer = this.globalLevelSettings?.link_task_max_timer || 99999;
    const ctrl = levelGroup.get('link_task_timer');
    const value = Number(ctrl?.value);
    if (ctrl && (value < minTimer || value > maxTimer)) {
      ctrl.setValue(Math.min(Math.max(value, minTimer), maxTimer), { emitEvent: false });
    }
    // Whole seconds only: "12.5" or "1e3" is not a timer (QA #340).
    ctrl?.setValidators(required
      ? [Validators.required, Validators.min(minTimer), Validators.max(maxTimer), wholeNumberValidator]
      : [Validators.min(minTimer), Validators.max(maxTimer), wholeNumberValidator]);
    ctrl?.updateValueAndValidity({ emitEvent: false });
  }

  // Re-applies the above clamping to every existing level once the real globalLevelSettings
  // response lands — covers levels built while the settings call was still in flight.
  private reconcileLevelsWithGlobalSettings(): void {
    if (!this.globalLevelSettings) return;
    this.levels.controls.forEach((levelControl) => {
      const levelGroup = levelControl as FormGroup;
      const type = levelGroup.get('type')?.value;
      if (type === 'survey') {
        this.applySurveyMinAnswersValidator(levelGroup, true);
      } else if (type === 'link_task') {
        this.applyLinkTaskTimerValidator(levelGroup, true);
      }
    });
  }

  // The generic "Form is invalid" modal used to give no clue which of potentially many levels
  // was the problem — a level that looks fully filled in on screen can still be invalid because
  // of a different level elsewhere in the same contest. Point at the specific level(s)/field(s)
  // so the user isn't left guessing.
  private describeInvalidLevels(): string[] {
    const messages: string[] = [];
    this.levels.controls.forEach((levelControl, index) => {
      const levelGroup = levelControl as FormGroup;
      if (levelGroup.disabled || levelGroup.valid) return;
      const name = levelGroup.get('name')?.value || `Level ${index + 1}`;
      const type = levelGroup.get('type')?.value;

      if (type === 'survey' && levelGroup.get('min_answers_required')?.invalid) {
        const minAnswers = this.globalLevelSettings?.survey_min_answers || 1;
        messages.push(`"${name}": Minimum Answers Required must be at least ${minAnswers}.`);
      }
      if (type === 'survey') {
        const questions = levelGroup.get('survey_questions') as FormArray | null;
        if (questions && questions.enabled) {
          if (questions.length === 0) {
            messages.push(`Level ${index + 1} "${name}": add at least one survey question.`);
          }
          questions.controls.forEach((qControl, qi) => {
            const q = qControl as FormGroup;
            if (q.valid) return;
            const missing: string[] = [];
            if (q.get('question_text')?.invalid) missing.push('the question');
            if (q.get('arabic_question_text')?.invalid) missing.push('its Arabic wording');
            const options = q.get('options') as FormArray | null;
            if (options?.invalid) missing.push('every answer option, in both languages');
            messages.push(
              `Level ${index + 1} "${name}" — Question ${qi + 1}: fill in ${missing.length ? missing.join(', ') : 'the missing fields'}.`,
            );
          });
        }
      }
      if (levelGroup.errors?.['minAnswersExceedsQuestions']) {
        messages.push(`"${name}": Minimum Answers Required cannot exceed the number of questions.`);
      }
      if (levelGroup.errors?.['minAnswersBelowRequired']) {
        messages.push(`"${name}": Minimum Answers Required cannot be less than the number of required questions (${levelGroup.errors['minAnswersBelowRequired']}).`);
      }
      if (type === 'receipt') {
        messages.push(...this.describeReceiptProblems(levelGroup, index, name));
      }
      if (type === 'link_task' && levelGroup.get('link_task_timer')?.invalid) {
        const minTimer = this.globalLevelSettings?.link_task_min_timer || 10;
        const maxTimer = this.globalLevelSettings?.link_task_max_timer || 99999;
        messages.push(this.isArabic
          ? `"${name}": يجب أن يكون المؤقت عددًا صحيحًا من الثواني بين ${minTimer} و${maxTimer}.`
          : `"${name}": Timer must be a whole number of seconds between ${minTimer} and ${maxTimer}.`);
      }
      if (type === 'link_task') {
        const missing: string[] = [];
        const url = levelGroup.get('link_task_url');
        if (url?.hasError('required')) missing.push(this.isArabic ? 'الرابط الخارجي' : 'the External URL');
        else if (url?.invalid) missing.push(this.isArabic ? 'رابطًا صالحًا (مثل https://example.com)' : 'a valid URL (e.g. https://example.com)');
        if (levelGroup.get('link_task_instruction')?.invalid) missing.push(this.isArabic ? 'تعليمات المهمة' : 'the task instruction');
        if (levelGroup.get('link_task_instruction_ar')?.invalid) missing.push(this.isArabic ? 'تعليمات المهمة بالعربية' : 'the Arabic task instruction');
        if (missing.length) {
          messages.push(this.isArabic
            ? `"${name}": أدخل ${missing.join('، ')}.`
            : `"${name}": enter ${missing.join(', ')}.`);
        }
      }
      // A missing level date was only flagged under its field; the popup just said "check all fields".
      if (levelGroup.get('level_start_date')?.hasError('required')) {
        messages.push(this.isArabic
          ? `"${name}": تاريخ ووقت بدء المستوى مطلوب.`
          : `"${name}": Level start date & time is required.`);
      }
      if (levelGroup.get('level_timeline')?.hasError('required')) {
        messages.push(this.isArabic
          ? `"${name}": تاريخ انتهاء المستوى مطلوب.`
          : `"${name}": Level expiry date is required.`);
      }
      if (levelGroup.errors?.['levelDateOrder']) {
        messages.push(this.isArabic
          ? `"${name}": يجب أن يكون تاريخ ووقت بدء المستوى قبل تاريخ انتهاء المستوى.`
          : `"${name}": Level start date must be before the level expiry date.`);
      }
      if (type === 'geolocation') {
        // The AR settings and the point cards sit inside collapsible sub-cards, so
        // an invalid value there is easy to miss entirely — name it.
        const arConfig = levelGroup.get('ar_config');
        if (arConfig?.errors?.['requiredExceedsTotal']) {
          messages.push(`"${name}": Required Objects cannot be greater than Total Objects.`);
        }
        if (arConfig?.errors?.['spawnRangeInvalid']) {
          messages.push(`"${name}": Spawn Min must be less than Spawn Max.`);
        }
        if (arConfig?.errors?.['heightRangeInvalid']) {
          messages.push(`"${name}": Highest Height cannot be below Lowest Height.`);
        }
        const arKeys = Object.keys((arConfig as FormGroup)?.controls || {});
        const atLeastOneFields = arKeys
          .filter((key) => this.arNeedsAtLeastOne(key))
          .filter((key) => arConfig?.get(key)?.errors?.['min'] || arConfig?.get(key)?.errors?.['wholeNumber'])
          .map((key) => this.translate.instant('geo.config.' + key));
        if (atLeastOneFields.length) {
          messages.push(`"${name}": ${atLeastOneFields.join(', ')} must be a whole number of 1 or more.`);
        }
        const negativeFields = arKeys
          .filter((key) => !this.arNeedsAtLeastOne(key) && arConfig?.get(key)?.errors?.['min'])
          .map((key) => this.translate.instant('geo.config.' + key));
        if (negativeFields.length) {
          messages.push(`"${name}": ${negativeFields.join(', ')} cannot be negative.`);
        }
        (this.points(index)?.controls || []).forEach((pointControl, pointIndex) => {
          const point = pointControl as FormGroup;
          if (point.valid) return;
          const label = `"${name}" — Point ${pointIndex + 1}`;
          if (point.get('lat')?.invalid || point.get('lng')?.invalid) {
            messages.push(`${label}: pick a location on the map.`);
          }
          if (point.get('radius')?.invalid) {
            messages.push(`${label}: Radius must be at least 1 m.`);
          }
          if (point.get('prize_radius')?.invalid) {
            messages.push(`${label}: Prize Radius must be at least ${GEO_MIN_PRIZE_RADIUS_M} m.`);
          }
        });
      }
      if (levelGroup.errors?.['clientVariablesRequired'] || levelGroup.errors?.['clientVariablesIncomplete']) {
        messages.push(`"${name}": Client webhook variables are incomplete.`);
      }
    });
    return messages;
  }

  private describeReceiptProblems(levelGroup: FormGroup, index: number, name: string): string[] {
    const errors = levelGroup.get('receipt_rules')?.errors?.['receiptRules'];
    if (!errors) return [];
    const say = (code: string, params?: object) => this.translate.instant('add-content.receipt.err.' + code, params);
    const prefix = `Level ${index + 1} "${name}": `;
    const messages: string[] = [];
    for (const field of ['retailers', 'products', 'min_quantity', 'min_spend', 'spend_basis',
      'currency', 'purchase_from', 'purchase_to', 'max_attempts']) {
      if (errors[field]) messages.push(prefix + say(errors[field], errors.params?.[field]));
    }
    Object.entries(errors.productRows || {}).forEach(([row, code]) => {
      messages.push(`${prefix}${this.translate.instant('add-content.receipt.product')} ${Number(row) + 1}: `
        + say(code as string, errors.params?.['product.' + row]));
    });
    return messages;
  }

  private describeLevelsOutsideContestRange(): string[] {
    const toTime = (raw: any) => raw ? new Date(raw).getTime() : NaN;
    const startTime = toTime(this.addContentForm.get('start_date')?.value);
    const endTime = toTime(this.addContentForm.get('end_date')?.value);
    if (Number.isNaN(startTime) && Number.isNaN(endTime)) return [];

    const fmt = (value: Date | number) => formatDate(value, 'dd/MM/yy hh:mm a', 'en-US');
    const messages: string[] = [];
    this.levels.controls.forEach((levelControl, index) => {
      const levelGroup = levelControl as FormGroup;
      if (levelGroup.disabled) return;
      const name = levelGroup.get('name')?.value || `Level ${index + 1}`;
      const fields: { key: string; label: string; arLabel: string }[] = [
        { key: 'level_start_date', label: 'Level start date & time', arLabel: 'تاريخ ووقت بدء المستوى' },
        { key: 'level_timeline', label: 'Level expiry date', arLabel: 'تاريخ انتهاء المستوى' },
      ];
      fields.forEach(({ key, label, arLabel }) => {
        const time = toTime(levelGroup.get(key)?.value);
        if (Number.isNaN(time)) return;
        if (!Number.isNaN(startTime) && time < startTime) {
          messages.push(this.isArabic
            ? `"${name}": ${arLabel} (${fmt(time)}) قبل تاريخ بدء الحملة (${fmt(startTime)}).`
            : `"${name}": ${label} (${fmt(time)}) is before the campaign Start Date (${fmt(startTime)}).`);
        }
        if (!Number.isNaN(endTime) && time > endTime) {
          messages.push(this.isArabic
            ? `"${name}": ${arLabel} (${fmt(time)}) بعد تاريخ انتهاء الحملة (${fmt(endTime)}).`
            : `"${name}": ${label} (${fmt(time)}) is after the campaign End Date (${fmt(endTime)}).`);
        }
      });
    });
    return messages;
  }

  /**
   * An image level must actually carry an image.
   *
   * The requirement was only ever applied when the admin CHANGED a level's
   * type to `image` (see subscribeToLevelTypeChanges). Every other way of
   * arriving at an image level -- loading a campaign or deleting the image
   * off one -- left the control with no validator, so a level of type image
   * saved with `image: null` and said nothing.
   *
   * `imageFile` is the control the template shows the error on; it is only a
   * marker, so an image reached any other way counts too: the `image` control
   * for a saved one, the preview for one picked in this session.
   */
  private syncLevelImageValidator(levelGroup: FormGroup, index?: number): void {
    const control = levelGroup.get('imageFile');
    if (!control) return;

    if (levelGroup.get('type')?.value !== 'image') {
      control.clearValidators();
      control.updateValueAndValidity({ emitEvent: false });
      return;
    }

    const i = index ?? this.levels.controls.indexOf(levelGroup);
    const hasImage = !!levelGroup.get('image')?.value
      || !!control.value
      || (i >= 0 && !!this.levelImagePreviews[i]);

    if (hasImage) {
      control.clearValidators();
    } else {
      control.setValidators([Validators.required]);
    }
    control.updateValueAndValidity({ emitEvent: false });
  }

  applyInitialLevelTypeValidators(levelGroup: FormGroup): void {
    const type = levelGroup.get('type')?.value;
    const surveyQuestionsArray = levelGroup.get('survey_questions') as FormArray;

    levelGroup.get('link_task_url')?.clearValidators();
    levelGroup.get('link_task_instruction')?.clearValidators();
    levelGroup.get('link_task_instruction_ar')?.clearValidators();
    levelGroup.get('link_task_timer')?.clearValidators();
    levelGroup.get('min_answers_required')?.clearValidators();

    if (type === 'link_task') {
      this.applyLinkTaskFieldValidators(levelGroup);
      this.applyLinkTaskTimerValidator(levelGroup, true);
      surveyQuestionsArray?.disable({ emitEvent: false });
    } else if (type === 'survey') {
      this.applySurveyMinAnswersValidator(levelGroup, true);
      surveyQuestionsArray?.enable({ emitEvent: false });
      // Clear validators on options for any open_text questions
      surveyQuestionsArray?.controls.forEach(qCtrl => {
        const qGroup = qCtrl as FormGroup;
        if (qGroup.get('question_type')?.value === 'open_text') {
          const opts = qGroup.get('options') as FormArray;
          opts?.controls.forEach(c => {
            c.clearValidators();
            c.updateValueAndValidity({ emitEvent: false });
          });
        }
      });
    } else {
      surveyQuestionsArray?.disable({ emitEvent: false });
    }

    levelGroup.get('link_task_url')?.updateValueAndValidity({ emitEvent: false });
    levelGroup.get('link_task_instruction')?.updateValueAndValidity({ emitEvent: false });
    levelGroup.get('link_task_instruction_ar')?.updateValueAndValidity({ emitEvent: false });
    levelGroup.get('link_task_timer')?.updateValueAndValidity({ emitEvent: false });
    levelGroup.get('min_answers_required')?.updateValueAndValidity({ emitEvent: false });
    this.syncReceiptRules(levelGroup);
    levelGroup.updateValueAndValidity({ emitEvent: false });
  }

  /**
   * A Link level needs a real web link and its instruction in both languages, like every other
   * bilingual level field. ^https?:// alone accepted "https://.com" and "https://example..com",
   * and the Arabic instruction could be left empty.
   */
  private applyLinkTaskFieldValidators(levelGroup: FormGroup): void {
    levelGroup.get('link_task_url')?.setValidators([Validators.required, webUrlValidator()]);
    levelGroup.get('link_task_instruction')?.setValidators([Validators.required, noWhitespaceValidator()]);
    levelGroup.get('link_task_instruction_ar')?.setValidators([Validators.required, noWhitespaceValidator()]);
  }

  // Whether a Treasure Hunt (AR) level can be picked: the plan includes AR and, on a limited
  // plan, this cycle's AR campaigns aren't all used. The server's quota gate let campaigns
  // through after the limit, so the limit is checked here too.
  canUseTreasureHunt(): boolean {
    // AR access, not web access. Web access is a different entitlement and grants nothing here — reading
    // it as permission offered the level to plans without AR, and the server then refused the
    // whole contest at Submit with AR_NOT_IN_PLAN, after the level had been filled in.
    return this.hasArAccess && !this.arLimitReached();
  }

  /** Every AR campaign of a limited plan is used this cycle, and this campaign isn't one of them. */
  arLimitReached(): boolean {
    return this.hasArAccess && !this.arUnlimited && this.arCampaignsAllowed > 0
      && this.arCampaignsUsed >= this.arCampaignsAllowed && !this.campaignAlreadyCountsForAr;
  }

  arUsageHintParams(): { used: number; allowed: number } {
    return { used: this.arCampaignsUsed, allowed: this.arCampaignsAllowed };
  }

  // No AR in the plan, or (limited plan) no AR campaigns left this cycle. `submitting`: a
  // Treasure Hunt level was already in the campaign, so say how to send it anyway.
  private showArLockedModal(submitting: boolean = false): void {
    const limit = this.hasArAccess && this.arLimitReached();
    const titleKey = limit ? 'Levels_Array.ar_locked_limit_title' : 'Levels_Array.ar_locked_no_access_title';
    const bodyKey = !limit ? 'Levels_Array.ar_locked_no_access_body'
      : submitting ? 'Levels_Array.ar_locked_limit_submit_body'
      : this.arUsageResetDate ? 'Levels_Array.ar_locked_limit_body'
      : 'Levels_Array.ar_locked_limit_body_no_date';
    const params = {
      used: this.arCampaignsUsed,
      allowed: this.arCampaignsAllowed,
      date: this.arUsageResetDate ? new Date(this.arUsageResetDate).toLocaleDateString() : '',
    };

    this.modal.create({
      nzTitle: this.translate.instant(titleKey),
      nzContent: this.translate.instant(bodyKey, params),
      nzClassName: 'contest-styled-modal',
      nzCentered: true,
      nzOkText: this.translate.instant('Plan&Price.upgrade'),
      nzOnOk: () => this.router.navigate(['/subscription']),
      nzCancelText: this.translate.instant('common.cancel'),
    });
  }

  /**
   * Whether the admin has entered anything that belongs to `type` on this level,
   * i.e. anything a type switch would throw away. Defaults (timer, radius, the
   * game's stock tuning) and on/off toggles don't count.
   */
  private levelTypeHasData(levelGroup: FormGroup, type: string | null): boolean {
    const filled = (value: any): boolean => {
      if (value === null || value === undefined || typeof value === 'boolean') return false;
      if (value instanceof Blob) return true;
      if (Array.isArray(value)) return value.some(filled);
      if (typeof value === 'object') return Object.values(value).some(filled);
      return String(value).trim() !== '';
    };
    const raw = (key: string) => levelGroup.get(key)?.getRawValue?.() ?? levelGroup.get(key)?.value;
    const index = this.levels.controls.indexOf(levelGroup);

    switch (type) {
      case 'text':
        return filled(raw('textContent'));
      case 'qr':
        return filled(raw('qrData'));
      case 'image':
        return filled(raw('image')) || filled(raw('imageFile')) || (index >= 0 && !!this.levelImagePreviews[index]);
      case 'quiz':
        return filled([raw('quizzes_id'), raw('quiz_text_en'), raw('quiz_text_ar')]);
      case 'survey':
        return (raw('survey_questions') || []).some((q: any) =>
          filled([q?.question_text, q?.arabic_question_text, q?.options]));
      case 'link_task':
        return filled([raw('link_task_url'), raw('link_task_instruction'), raw('link_task_instruction_ar')]);
      case 'receipt':
        return receiptRulesHaveData(
          (levelGroup.get('receipt_rules') as FormGroup | null)?.getRawValue?.() ?? null);
      case 'client_webhook':
        return filled([raw('client_webhook_url'), raw('client_variables')]);
      case 'luqta_weebhook':
        return filled(raw('luqta_webhook_url'));
      case 'geolocation':
        return (index >= 0 && this.hasCustomArConfig(index))
          || (raw('ar_points') || []).some((p: any) => filled([
            p?.location_name, p?.lat, p?.lng, p?.prize_custom_type,
            p?.prize_value, p?.prize_description, p?.prize_image,
          ]));
      default:
        return false;
    }
  }

  subscribeToLevelTypeChanges(levelGroup: FormGroup, index: number): void {
    const typeControl = levelGroup.get('type');
    let previousType = typeControl?.value ?? null;
    let switchConfirmed = false;
    typeControl?.valueChanges.subscribe((type) => {
      if (type === 'geolocation' && !this.canUseTreasureHunt()) {
        typeControl.setValue(previousType, { emitEvent: false });
        this.showArLockedModal();
        return;
      }
      // Everything below wipes the old type's fields, so a level that already has
      // some filled in keeps its type until the admin agrees to lose them.
      // An empty type is never an admin's pick — it's addContentForm.reset() after a
      // save — and once the campaign is saved nothing is left to protect, so neither
      // may pop this over the next page.
      if (type && !this.campaignSaved && !switchConfirmed && type !== previousType
        && this.levelTypeHasData(levelGroup, previousType)) {
        typeControl.setValue(previousType, { emitEvent: false });
        this.levelTypeSwitchPrompt?.destroy();
        const prompt = this.levelTypeSwitchPrompt = this.modal.confirm({
          nzTitle: this.translate.instant('Levels_Array.Level_type_switch_title'),
          nzContent: this.translate.instant('Levels_Array.Level_type_switch_content'),
          nzOkText: this.translate.instant('Levels_Array.Level_type_switch_ok'),
          nzCancelText: this.translate.instant('common.cancel'),
          nzOkDanger: true,
          nzClassName: 'contest-styled-modal',
          nzCentered: true,
          nzOnOk: () => {
            switchConfirmed = true;
            typeControl.setValue(type);
          },
        });
        prompt.afterClose.subscribe(() => {
          if (this.levelTypeSwitchPrompt === prompt) this.levelTypeSwitchPrompt = null;
        });
        return;
      }
      switchConfirmed = false;
      const fromType = previousType;
      previousType = type;
      const textContentArray = levelGroup.get('textContent') as FormArray;
      const imageFileControl = levelGroup.get('imageFile');
      const qrDataControl = levelGroup.get('qrData');
      const clientWebhookControl = levelGroup.get('client_webhook_url');
      const luqtaWebhookControl = levelGroup.get('luqta_webhook_url');
      const quizzesIdControl = levelGroup.get('quizzes_id');

      // Reset ALL conditional fields and clear validators when type changes
      // Clear textContent array and reset with one empty field
      textContentArray?.clear();
      textContentArray?.push(
        this.createTextContentGroupForType(type, !!levelGroup.get('accept_any_answer')?.value)
      );

      imageFileControl?.clearValidators();
      imageFileControl?.setValue(null, { emitEvent: false });
      levelGroup.get('image')?.setValue(null, { emitEvent: false });
      this.levelImagePreviews[index] = null;

      qrDataControl?.clearValidators();
      qrDataControl?.setValue('', { emitEvent: false });
      this.levelQrCodes[index] = null;

      clientWebhookControl?.clearValidators();
      clientWebhookControl?.setValue('', { emitEvent: false });

      // luqta_webhook_url is user-editable, no auto-fill
      if (type !== 'luqta_weebhook') {
        luqtaWebhookControl?.setValue('', { emitEvent: false });
      }

      quizzesIdControl?.clearValidators();
      quizzesIdControl?.setValue(null, { emitEvent: false });
      if (type !== 'quiz') {
        levelGroup.get('quiz_text_en')?.setValue('', { emitEvent: false });
        levelGroup.get('quiz_text_ar')?.setValue('', { emitEvent: false });
      }

      // The remaining types keep their fields in arrays/groups of their own; reset
      // them on the way out so a confirmed switch really does start the level clean.
      if (fromType === 'client_webhook') {
        (levelGroup.get('client_variables') as FormArray)?.clear({ emitEvent: false });
      }
      if (fromType === 'survey') {
        const questions = levelGroup.get('survey_questions') as FormArray;
        questions?.clear({ emitEvent: false });
        questions?.push(this.createSurveyQuestion(), { emitEvent: false });
        levelGroup.get('min_answers_required')?.setValue(1, { emitEvent: false });
        levelGroup.get('question_order')?.setValue('fixed', { emitEvent: false });
      }
      if (fromType === 'link_task') {
        ['link_task_url', 'link_task_instruction', 'link_task_instruction_ar'].forEach((key) =>
          levelGroup.get(key)?.setValue('', { emitEvent: false }));
        levelGroup.get('link_task_timer')?.setValue(30, { emitEvent: false });
      }
      if (fromType === 'receipt') {
        this.resetReceiptRules(levelGroup);
      }
      if (fromType === 'geolocation') {
        levelGroup.get('ar_type')?.setValue(DEFAULT_AR_TYPE, { emitEvent: false });
        levelGroup.get('is_location_fixed')?.setValue(true, { emitEvent: false });
        levelGroup.setControl('ar_config', this.createArConfigGroup(DEFAULT_AR_TYPE), { emitEvent: false });
        const points = levelGroup.get('ar_points') as FormArray;
        points?.clear({ emitEvent: false });
        points?.push(this.createPointGroup({}, 0), { emitEvent: false });
      }

      // Clear link_task / survey validators before re-applying
      levelGroup.get('link_task_url')?.clearValidators();
      levelGroup.get('link_task_instruction')?.clearValidators();
      levelGroup.get('link_task_instruction_ar')?.clearValidators();

      // Set validators based on selected type
      if (type === 'qr') {
        qrDataControl?.setValidators([Validators.required]);
      } else if (type === 'image') {
        imageFileControl?.setValidators([Validators.required]);
      } else if (type === 'client_webhook') {
        clientWebhookControl?.setValidators([
          Validators.required,
          Validators.pattern(/^https?:\/\/.+/)
        ]);
      } else if (type === 'quiz') {
        quizzesIdControl?.setValidators([Validators.required]);
      } else if (type === 'link_task') {
        this.applyLinkTaskFieldValidators(levelGroup);
        this.applyLinkTaskTimerValidator(levelGroup, true);
      }

      // min_answers_required only required for survey
      levelGroup.get('min_answers_required')?.clearValidators();
      if (type === 'survey') {
        this.applySurveyMinAnswersValidator(levelGroup, true);
      }
      levelGroup.get('min_answers_required')?.updateValueAndValidity({ emitEvent: false });

      // link_task_timer only required for link_task
      if (type !== 'link_task') {
        levelGroup.get('link_task_timer')?.clearValidators();
        levelGroup.get('link_task_timer')?.updateValueAndValidity({ emitEvent: false });
      }

      // survey_questions must only validate when type === 'survey'
      const surveyQuestionsArray = levelGroup.get('survey_questions') as FormArray;
      if (type === 'survey') {
        surveyQuestionsArray?.enable({ emitEvent: false });
      } else {
        surveyQuestionsArray?.disable({ emitEvent: false });
      }

      // Client Webhook should always start with one variable row by default.
      if (type === 'client_webhook') {
        const variables = levelGroup.get('client_variables') as FormArray;
        if (variables && variables.length === 0) {
          variables.push(this.createClientVariableGroup());
        }
      }

      // Update validity for all controls
      textContentArray?.updateValueAndValidity({ emitEvent: false });
      imageFileControl?.updateValueAndValidity({ emitEvent: false });
      qrDataControl?.updateValueAndValidity({ emitEvent: false });
      clientWebhookControl?.updateValueAndValidity({ emitEvent: false });
      luqtaWebhookControl?.updateValueAndValidity({ emitEvent: false });
      quizzesIdControl?.updateValueAndValidity({ emitEvent: false });
      levelGroup.get('link_task_url')?.updateValueAndValidity({ emitEvent: false });
      levelGroup.get('link_task_instruction')?.updateValueAndValidity({ emitEvent: false });
      levelGroup.get('link_task_instruction_ar')?.updateValueAndValidity({ emitEvent: false });
      // Receipt rules only validate (and only enable) while the level is a receipt level.
      this.syncReceiptRules(levelGroup);
      levelGroup.updateValueAndValidity({ emitEvent: false });

      // Coordinates become required only once this level is a fixed geolocation
      // level, and must be released again the moment it becomes anything else.
      this.syncPointLocationValidators(this.levels.controls.indexOf(levelGroup));

      // Ensure text answer inputs reflect Accept Any Answer state
      this.updateTextAnswerControls(levelGroup, !!levelGroup.get('accept_any_answer')?.value);
    });
  }
  generateQrCode(index: number): void {
    const levelGroup = this.levels.at(index) as FormGroup;
    const qrData = levelGroup.get('qrData')?.value;
    if (qrData) {
      this.levelQrCodes[index] = qrData;
      this.levelQrCodesOriginal[index] = qrData; // Store original value
      this.qrModalIndex = index;
      this.showQRModal();
    } else {
      this.levelQrCodes[index] = null;
      this.sharedService.createNotification(
        'error',
        'Please enter text for QR code generation.'
      );
    }
  }

  // Check if QR input has changed since last generation
  hasQrInputChanged(index: number): boolean {
    const levelGroup = this.levels.at(index) as FormGroup;
    const currentQrData = levelGroup.get('qrData')?.value;
    return currentQrData !== this.levelQrCodesOriginal[index];
  }

  // Check if generate button should be disabled
  isQrGenerateDisabled(index: number): boolean {
    const levelGroup = this.levels.at(index) as FormGroup;
    const qrData = levelGroup.get('qrData')?.value;
    return !qrData || qrData.trim() === '';
  }
  triggerImageUpload(imageInput: HTMLInputElement): void {
    imageInput.click();
  }

  // Trigger level image file input
  triggerLevelImageInput(index: number): void {
    if (this.levelImagePreviews[index]) {
      const levelGroup = this.levels.at(index) as FormGroup;
      const existingImage = levelGroup.get('image')?.value;
      if (!existingImage && this.levelImagePreviews[index]) {
        levelGroup.get('image')?.setValue(this.levelImagePreviews[index]);
      }
    }
    const fileInput: any = document.getElementById(`imageFile_${index}`) as HTMLInputElement;
    if (fileInput) {
      fileInput.click();
    }
  }

  // Show image preview modal
  showImageModal(index: number): void {
    if (this.levelImagePreviews[index]) {
      this.isImageVisible[index] = true;
    }
  }

  // Handle image modal OK / Save Image
  handleImageOk(index: number): void {
    if (this.levelImagePreviews[index]) {
      const levelGroup = this.levels.at(index) as FormGroup;
      const imagePreview = this.levelImagePreviews[index];
      levelGroup.get('image')?.setValue(imagePreview);
      levelGroup.get('imageFile')?.setValue('has_image');
      levelGroup.get('imageFile')?.markAsTouched();
      levelGroup.get('imageFile')?.updateValueAndValidity();
      this.sharedService.createNotification('success', 'Image saved successfully');
    }
    this.isImageVisible[index] = false;
  }

  // Handle image modal cancel
  handleImageCancel(index: number): void {
    this.isImageVisible[index] = false;
  }

  // Delete Level Image
  deleteLevelImage(index: number): void {
    this.levelImagePreviews[index] = null;
    const levelGroup = this.levels.at(index) as FormGroup;
    if (levelGroup) {
      levelGroup.get('image')?.setValue(null);
      levelGroup.get('imageFile')?.setValue(null);
      // The level needs an image again, so the requirement comes back.
      this.syncLevelImageValidator(levelGroup, index);
    }
    const fileInput = document.getElementById(`imageFile_${index}`) as HTMLInputElement;
    if (fileInput) {
      fileInput.value = '';
    }
  }

  // Download Level Image to user device
  downloadLevelImage(index: number): void {
    const imageUrl = this.levelImagePreviews[index];
    if (!imageUrl || typeof imageUrl !== 'string') return;

    const levelGroup = this.levels.at(index) as FormGroup;
    const levelName = levelGroup?.get('name')?.value || `level_${index + 1}`;
    const cleanFileName = `${(levelName.replace(/[^a-zA-Z0-9_-]/g, '_') || 'image')}_${Date.now()}.png`;

    this.downloadDirectImage(imageUrl, cleanFileName);
  }

  // Download direct image helper
  downloadDirectImage(imageUrl: string, fileName?: string): void {
    if (!imageUrl || typeof imageUrl !== 'string') return;
    const downloadName = fileName || `image_${Date.now()}.png`;

    if (imageUrl.startsWith('data:')) {
      const link = document.createElement('a');
      link.href = imageUrl;
      link.download = downloadName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } else {
      fetch(imageUrl)
        .then(response => response.blob())
        .then(blob => {
          const blobUrl = window.URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = blobUrl;
          link.download = downloadName;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          window.URL.revokeObjectURL(blobUrl);
        })
        .catch(() => {
          const link = document.createElement('a');
          link.href = imageUrl;
          link.target = '_blank';
          link.download = downloadName;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
        });
    }
  }

  async onLevelImageSelected(event: Event, index: number) {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    // The accept filter can be bypassed ("All files" in the picker), so check type and size here too.
    // A rejected file leaves any existing level image untouched.
    if (file && this.validateCampaignImage(file)) {
      const reader = new FileReader();
      reader.onload = () => {
        this.levelImagePreviews[index] = reader.result;
        const levelGroup = this.levels.at(index) as FormGroup;
        if (levelGroup) {
          levelGroup.get('image')?.setValue(reader.result);
          levelGroup.get('imageFile')?.setValue('has_image');
          levelGroup.get('imageFile')?.markAsTouched();
          levelGroup.get('imageFile')?.updateValueAndValidity();
        }
      };
      reader.readAsDataURL(file);
    }
    // The input now stays mounted, so clear it to let the same file be picked again.
    input.value = '';
  }
  confirmRemoveLevel(index: number): void {
    // Prevent deleting the last remaining level
    if (this.levels.length === 1) {
      this.sharedService.createNotification('warning', 'At least one level is required.');
      return;
    }
    if (this.isFirstToComplete() && this.levels.length <= 2) {
      this.sharedService.createNotification('warning', '"First To Complete" draw type requires at least 2 levels.');
      return;
    }

    const levelGroup = this.levels.at(index) as FormGroup;
    const hasData = this.hasMeaningfulLevelData(levelGroup);

    if (hasData) {
      this.showConfirm(index);
    } else {
      this.removeLevel(index);
    }
  }

  removeLevel(index: number): void {
    this.levels.removeAt(index);
    this.levelQrCodes.splice(index, 1);
    this.levelImagePreviews.splice(index, 1);
    this.isImageVisible.splice(index, 1);
    // Frontend-only removal: keep the original levels array index-aligned with
    // the form, but don't call the delete API.
    const originalData = this.singleContestData || {};
    if (originalData._levels_of_contest?.[index]) {
      originalData._levels_of_contest.splice(index, 1);
    }
    // this.calculateTotalPoints();
    const newCount = this.levels.length || null;
    this.addContentForm
      .get('totallevels')
      ?.setValue(newCount, { emitEvent: true });

    // Update level count selection to reflect the new count
    if (newCount && newCount <= 5) {
      this.selectedLevelCount = newCount;
      this.customLevelCount = null;
    } else {
      this.selectedLevelCount = null;
      this.customLevelCount = newCount;
    }
  }
  showConfirm(index: number): void {
    this.modal.confirm({
      nzTitle: 'Confirm Removal',
      nzContent:
        'This level contains data. Are you sure you want to remove it?',
      nzOnOk: () => this.removeLevel(index),
      nzClassName: 'contest-styled-modal',
      nzCentered: true,
      nzOnCancel: () => { },
    });
  }

  private hasMeaningfulLevelData(levelGroup: FormGroup): boolean {
    const stringFields = [
      'name',
      'arabic_name',
      'description',
      'arabic_description',
      'client_webhook_url',
      'luqta_webhook_url',
    ];

    const hasFilledString = stringFields.some((field) => {
      const value = levelGroup.get(field)?.value;
      return String(value ?? '').trim().length > 0;
    });

    const hasSelectedValue = [
      'type',
      'level_timeline',
      'level_start_date',
      'imageFile',
      'quizzes_id',
    ].some((field) => {
      const value = levelGroup.get(field)?.value;
      return value !== null && value !== undefined && String(value).trim() !== '';
    });

    const textContent = levelGroup.get('textContent') as FormArray | null;
    const hasTextContent = !!textContent?.value?.some((item: any) =>
      String(item?.text ?? '').trim().length > 0 || String(item?.arabic_text ?? '').trim().length > 0
    );

    const clientVariables = levelGroup.get('client_variables') as FormArray | null;
    const hasClientVariables = !!clientVariables?.length;

    return hasFilledString || hasSelectedValue || hasTextContent || hasClientVariables;
  }

  async getLevelsData(): Promise<any[] | null> {
    if (!this.addContentForm.get('levels_of_contest')?.valid) {
      (
        this.addContentForm.get('levels_of_contest') as FormArray
      ).controls.forEach((levelControl) => {
        (levelControl as FormGroup).markAllAsTouched();
      });
      this.sharedService.createNotification(
        'error',
        'Please fill all required level fields.'
      );
      return null;
    }
    const levelsArray = this.addContentForm.get(
      'levels_of_contest'
    ) as FormArray;
    const levelDataPromises = levelsArray.controls.map(async (levelControl) => {
      const levelGroup = levelControl as FormGroup;
      const levelType = levelGroup.get('type')?.value;
      const data: any = {
        type: levelType,
        description: levelGroup.get('description')?.value || '',
        name: levelGroup.get('name')?.value || '',
        arabic_name: levelGroup.get('arabic_name')?.value || '',
        arabic_description: levelGroup.get('arabic_description')?.value || '',
        // points: levelGroup.get('points')?.value || '',
        start_date: levelGroup.get('level_start_date')?.value || null,
        level_start_date: levelGroup.get('level_start_date')?.value || null,
        level_timeline: levelGroup.get('level_timeline')?.value,
        // Cleared for non-quiz levels; the 'quiz' case sets it. See the submit payload above.
        quizzes_id: null,
      };
      switch (levelType) {
        case 'text':
          const textContentArray = (levelGroup.get('textContent') as FormArray)?.getRawValue() || [];
          const filledTextContent = textContentArray.filter((item: any) => item?.text?.trim());
          data.textContent = filledTextContent.map((item: any) => item.text).join(' || ');
          data.arabic_textContent = filledTextContent.map((item: any) => item.arabic_text || '').join(' || ');
          break;
        case 'qr':
          data.qrData = levelGroup.get('qrData')?.value;
          data.textContent = levelGroup.get('qrData')?.value;
          break;
        case 'image':
          const imageFile = levelGroup.get('image')?.value;
          if (imageFile instanceof File) {
            // Uploaded file → convert to base64
            data.image = await this.fileToBase64(imageFile);
          } else if (typeof imageFile === 'string' && imageFile.startsWith('data:image')) {
            // Already a base64 data URL
            data.image = imageFile;
          } else if (typeof imageFile === 'string' && /^https?:\/\//i.test(imageFile)) {
            // AI-generated image URL (e.g. Together AI) — pass through
            data.image = imageFile;
          } else if (imageFile && typeof imageFile === 'object') {
            // Xano image object ({ path, url, name, type, size, mime, meta, access })
            data.image = imageFile.url || imageFile.path || imageFile;
          } else if (typeof imageFile === 'string' && imageFile.trim() !== '') {
            // Any other non-empty string (relative path, etc.) — pass through
            data.image = imageFile;
          } else {
            data.image = '';
          }
          break;
        case 'client_webhook':
          data.client_webhook_url = levelGroup.get('client_webhook_url')?.value;
          data.client_variables = levelGroup.get('client_variables')?.value;
          break;
        case 'luqta_weebhook':
          data.luqta_webhook_url =
            levelGroup.get('luqta_webhook_url')?.value || '';
          break;
        case 'quiz':
          data.quizzes_id = levelGroup.get('quizzes_id')?.value || null;
          data.quiz_text_en = levelGroup.get('quiz_text_en')?.value || '';
          data.quiz_text_ar = levelGroup.get('quiz_text_ar')?.value || '';
          break;
        case 'geolocation':
          data.point_areas = this.buildPointAreas(levelGroup);
          break;
        case 'receipt':
          data.receipt_rules = this.buildReceiptRulesPayload(levelGroup);
          break;
        case 'survey':
          data.survey_questions = ((levelGroup.get('survey_questions') as FormArray)?.value || []).map(
            (q: any) => ({
              question_text: q.question_text,
              arabic_question_text: q.arabic_question_text,
              question_type: q.question_type,
              is_required: q.is_required,
              options: q.question_type === 'mcq'
                ? (q.options || [])
                  .filter((o: any) => o?.option_text?.trim())
                  .map((o: any) => ({ option_text: o.option_text, arabic_option_text: o.arabic_option_text }))
                : [],
            })
          );
          data.min_answers_required = levelGroup.get('min_answers_required')?.value || 1;
          data.question_order = levelGroup.get('question_order')?.value || 'fixed';
          break;
        case 'link_task':
          Object.assign(data, linkTaskPayload({
            url: levelGroup.get('link_task_url')?.value,
            instruction: levelGroup.get('link_task_instruction')?.value,
            instructionAr: levelGroup.get('link_task_instruction_ar')?.value,
            timer: levelGroup.get('link_task_timer')?.value,
            openMode: levelGroup.get('link_open_mode')?.value,
          }));
          break;
      }
      return data;
    });
    return Promise.all(levelDataPromises);
  }
  setLevelsData(levelsData: any[], isDuplicate: boolean = false, dateShiftMs: number = 0) {
    // A copy of an ended campaign moves its levels with the campaign dates.
    const levelDate = (value: any) => value ? new Date(new Date(value).getTime() + dateShiftMs) : null;
    this.levels.clear();
    this.isImageVisible = [];
    levelsData.forEach((level: any) => {
      const levelRecord = level?.level_details ?? level?.level_detail ?? level?.details ?? level;
      const levelType =
        levelRecord?.level_type ||
        levelRecord?.type ||
        levelRecord?.levelType ||
        level?.level_type ||
        level?.type ||
        level?.levelType ||
        '';
      const isTextType = levelType === 'text';
      const acceptAny = !!(levelRecord?.accept_any_answer ?? level?.accept_any_answer ?? false);
      const rawTextContent =
        levelRecord?.textContent ??
        levelRecord?.text_content ??
        levelRecord?.text ??
        level?.textContent ??
        level?.text_content ??
        level?.text ??
        '';
      const rawArabicTextContent =
        levelRecord?.arabic_textContent ??
        levelRecord?.arabic_text_content ??
        level?.arabic_textContent ??
        level?.arabic_text_content ??
        '';
      const rawQrData =
        levelRecord?.qrData ??
        levelRecord?.qr_data ??
        levelRecord?.qr_code ??
        level?.qrData ??
        level?.qr_data ??
        level?.qr_code ??
        rawTextContent;
      const rawImage =
        levelRecord?.image ??
        levelRecord?.image_url ??
        levelRecord?.imageUrl ??
        levelRecord?.image_path ??
        level?.image ??
        level?.image_url ??
        level?.imageUrl ??
        level?.image_path ??
        null;
      const levelStartDateValue =
        levelRecord?.level_start_date ??
        levelRecord?.start_date ??
        levelRecord?.level_start ??
        level?.level_start_date ??
        level?.start_date ??
        level?.level_start ??
        null;
      const levelTimelineValue =
        levelRecord?.level_timeline ??
        levelRecord?.timeline ??
        level?.level_timeline ??
        level?.timeline ??
        null;

      const clientVariablesArray = this.fb.array(
        (levelRecord?.client_variables || level?.client_variables || []).map((cv: any) =>
          this.fb.group({
            variable_name: [cv.variable_name || '', Validators.required],
            display_name: [cv.display_name || '', Validators.required],
            datatype: [cv.datatype || '', Validators.required],
          })
        )
      );
      const savedPointAreas = pointAreasOf(levelRecord) ?? pointAreasOf(level);
      const savedArType = this.normalizeArType(savedPointAreas?.ar_type);

      const levelGroup = this.fb.group({
        id: [isDuplicate ? null : (levelRecord?.id ?? level?.id)],
        type: [levelType, Validators.required], // Accommodate existing 'type' or 'level_type'
        description: [
          levelRecord?.description ?? level?.description,
          [Validators.required, Validators.maxLength(1000)],
        ],
        name: [levelRecord?.name ?? level?.name, [Validators.required, Validators.maxLength(LEVEL_NAME_MAX)]],
        arabic_name: [
          levelRecord?.arabic_name ?? level?.arabic_name,
          [Validators.required, Validators.maxLength(LEVEL_NAME_MAX)],
        ],
        arabic_description: [levelRecord?.arabic_description ?? level?.arabic_description, Validators.required],
        luqta_webhook_url: [levelRecord?.luqta_webhook_url ?? level?.luqta_webhook_url],
        client_webhook_url: [levelRecord?.client_webhook_url ?? level?.client_webhook_url],
        client_variables: clientVariablesArray,
        level_start_date: [levelDate(levelStartDateValue), Validators.required],
        level_timeline: [levelDate(levelTimelineValue), Validators.required],
        accept_any_answer: [acceptAny],
        textContent: this.fb.array(
          (() => {
            const validators = isTextType ? this.getTextAnswerValidators(acceptAny) : [Validators.maxLength(1000)];
            const textParts = rawTextContent && String(rawTextContent).trim() !== ''
              ? String(rawTextContent).split(' || ')
              : [''];
            // Arabic text used to not exist — an older saved level simply has no
            // arabic_textContent, so every part falls back to an empty string.
            const arabicParts = rawArabicTextContent && String(rawArabicTextContent).trim() !== ''
              ? String(rawArabicTextContent).split(' || ')
              : [];
            return textParts.map((text: string, idx: number) =>
              this.fb.group({
                text: [text, validators],
                arabic_text: [arabicParts[idx] || ''],
              })
            );
          })()
        ),
        image: [rawImage || null],
        imageFile: [null],
        qrData: [rawQrData],
        quizzes_id: [levelRecord?.quizzes_id ?? level?.quizzes_id ?? null],
        quiz_text_en: [levelRecord?.quiz_text_en ?? level?.quiz_text_en ?? ''],
        quiz_text_ar: [levelRecord?.quiz_text_ar ?? level?.quiz_text_ar ?? ''],
        survey_questions: this.fb.array(
          (() => {
            const qs: any[] = levelRecord?.survey_questions ?? level?.survey_questions ?? [];
            if (qs.length) {
              return qs.map((q: any) => {
                // question_type comes straight from the API (it's the field that reliably
                // round-trips) — it drives Question Type. is_multi_select is a separate flag for
                // the Answer Type toggle (single/multiple), reconstructed here the same way.
                const questionType = q.question_type || 'mcq';
                const selectionType = q.is_multi_select === false ? 'single'
                  : q.is_multi_select === true ? 'multiple'
                    : (q.answer_selection_type || 'multiple');
                return this.fb.group({
                  id: [isDuplicate ? null : (q.id ?? null)],
                  question_text: [q.question_text || '', Validators.required],
                  arabic_question_text: [q.arabic_question_text || '', Validators.required],
                  question_type: [questionType],
                  is_required: [q.is_required ?? true],
                  answer_selection_type: [selectionType],
                  options: this.fb.array(
                    (() => {
                      // Options used to be plain strings — accept both shapes so
                      // surveys saved before the Arabic option field existed still load.
                      const rawOpts: any[] = q.answer_options?.length ? q.answer_options
                        : q.options?.length ? q.options
                          : ['', ''];
                      const validators = questionType === 'open_text' ? [] : [Validators.required];
                      return rawOpts.map((opt: any) => {
                        const isLegacyString = typeof opt === 'string';
                        return this.fb.group({
                          option_text: [isLegacyString ? opt : (opt?.option_text ?? ''), validators],
                          arabic_option_text: [isLegacyString ? '' : (opt?.arabic_option_text ?? ''), validators],
                        });
                      });
                    })()
                  ),
                });
              });
            }
            return [this.createSurveyQuestion()];
          })()
        ),
        min_answers_required: [levelRecord?.min_answers_required ?? levelRecord?.minimum_answers ?? level?.min_answers_required ?? level?.minimum_answers ?? 1, [Validators.min(this.globalLevelSettings?.survey_min_answers || 1)]],
        question_order: [levelRecord?.question_order ?? level?.question_order ?? 'fixed'],
        link_task_url: [readLinkTaskUrl(levelRecord) || readLinkTaskUrl(level)],
        link_task_instruction: [readLinkTaskInstruction(levelRecord) || readLinkTaskInstruction(level)],
        // The API returns it as arabic_task_instruction; the other names are older shapes.
        link_task_instruction_ar: [[levelRecord, level].flatMap((src: any) => [
          src?.arabic_task_instruction, src?.link_task_instruction_ar, src?.task_instruction_ar,
        ]).find((value: any) => typeof value === 'string' && value.trim() !== '') ?? ''],
        link_open_mode: [readLinkOpenMode(levelRecord?.link_open_mode ? levelRecord : level)],
        link_task_timer: [readLinkTaskTimer(levelRecord) || readLinkTaskTimer(level) || 30, [Validators.min(this.globalLevelSettings?.link_task_min_timer || 10), Validators.max(this.globalLevelSettings?.link_task_max_timer || 99999), wholeNumberValidator]],
        // Geolocation (Treasure Hunt) — older payloads name the same object geolocation_data.
        ar_type: [savedArType],
        // Absent means location-bound: every contest written before this field
        // existed is fixed and must stay that way. A "false" string or a flag
        // on the level itself is play-anywhere too (AI sheet rows 37, 41).
        is_location_fixed: [isFixedLocation(savedPointAreas, levelRecord ?? level)],
        ar_config: this.createArConfigGroup(savedArType, savedPointAreas?.ar_config || {}),
        ar_points: this.fb.array(
          (Array.isArray(savedPointAreas?.points) && savedPointAreas.points.length
            ? savedPointAreas.points
            : [{}]
          ).map((p: any, i: number) => this.createPointGroup(p, i))
        ),
        // Receipt (the get returns receipt_rules flattened onto the level; a copy keeps the rules)
        receipt_rules: this.createReceiptRulesGroup(
          levelRecord?.receipt_rules ?? level?.receipt_rules ?? null
        ),
      }, { validators: this.levelDateOrderValidator });
      if (
        levelGroup.get('type')?.value === 'qr' &&
        levelGroup.get('qrData')?.value
      ) {
        this.levelQrCodes[this.levels.length] = levelGroup.get('qrData')?.value;
      } else {
        this.levelQrCodes[this.levels.length] = null;
      }
      if (levelGroup.get('type')?.value === 'image' && level.image) {
        // Set image preview for AI-generated or existing image
        const imageUrl = level.image.url || level.image.path || level.image;
        this.levelImagePreviews[this.levels.length] = imageUrl;
        console.log(`Set image preview for level ${this.levels.length}:`, imageUrl ? imageUrl.substring(0, 50) + '...' : 'none');

        // Clear validators since image is already provided
        levelGroup.get('imageFile')?.clearValidators();
        levelGroup.get('imageFile')?.updateValueAndValidity();
      } else if (levelGroup.get('type')?.value === 'quiz') {
        if (!this.quizzesLoaded) {
          this.getQuizzesRecords();
        }
        levelGroup.get('quizzes_id')?.setValidators([Validators.required]);
        levelGroup.get('quizzes_id')?.updateValueAndValidity();
      } else {
        this.levelImagePreviews[this.levels.length] = null;
      }
      this.subscribeToLevelChanges(levelGroup, this.levels.length);
      this.applyInitialLevelTypeValidators(levelGroup);
      this.levels.push(levelGroup);
      this.syncPointLocationValidators(this.levels.length - 1);
      this.syncLevelImageValidator(levelGroup, this.levels.length - 1);
      if (levelGroup.get('type')?.value === 'client_webhook') {
        const variables = levelGroup.get('client_variables') as FormArray;
        if (variables.length === 0) {
          variables.push(this.createClientVariableGroup());
          levelGroup.updateValueAndValidity({ emitEvent: false });
        }
      }
      this.isImageVisible.push(false);
    });
    // Reflect the loaded count in the "number of levels" picker — in edit mode nothing was highlighted.
    const loadedCount = this.levels.length;
    this.selectedLevelCount = loadedCount > 0 && loadedCount <= 5 ? loadedCount : null;
    this.customLevelCount = loadedCount > 5 ? loadedCount : null;
  }
  get prizes(): FormArray {
    return this.addContentForm.get('_contest_prize') as FormArray;
  }
  async setPrizesData(prizesData: any[], isDuplicate: boolean = false) {
    // Categories first, before the list is cleared: while this waited with the list
    // already empty, step 2 rendered nothing, and a failed lookup left it that way for
    // good. A failed lookup now only costs the category paths; the prizes still load.
    if (Array.isArray(prizesData) && prizesData.length > 0 && !this.categoriesLoaded) {
      await this.loadCategoriesAsync().catch(() => undefined);
    }
    this.prizes.clear();
    this.prizeCategoryOptions = [];
    if (!Array.isArray(prizesData) || prizesData.length === 0) {
      // A campaign saved without prizes still needs a prize card: the step renders
      // nothing (not even "Add More Prize") for an empty list, so it could never be fixed.
      this.prizeArray.push(this.createPrize());
      this.prizeCategoryOptions.push(this.cloneCategoriesTree());
      return;
    }
    // A copy owns new prize records. Sending the source's prize ids made the save
    // skip them as another campaign's rows, so the copy was created without prizes.
    const sourcePrizeIds: any[] = [];
    for (const prize of prizesData) {
      const prizeRecord = prize?.prize ?? prize?.prize_details ?? prize?.contest_prize ?? prize;
      const rawCategories =
        prizeRecord?.categories ??
        prizeRecord?.categories_id ??
        prizeRecord?.category_id ??
        prizeRecord?.categoryId ??
        prizeRecord?.category_ids ??
        null;

      const categoryIds = Array.isArray(rawCategories)
        ? rawCategories
          .map((item: any) => {
            if (item && typeof item === 'object') {
              return item.id ?? item.category_id ?? item.categoryId ?? null;
            }
            return item;
          })
          .filter((item: any) => item !== null && item !== undefined)
        : rawCategories;

      const cascaderValue = this.categoryIdsToCascaderValue(categoryIds);

      const prizeName =
        prizeRecord?.name ??
        prizeRecord?.prize_name ??
        prizeRecord?.title ??
        '';
      const prizeValue =
        prizeRecord?.value ??
        prizeRecord?.prize_value ??
        prizeRecord?.amount ??
        prizeRecord?.prizeAmount ??
        0;
      const rawPrizeImage =
        prizeRecord?.image ??
        prizeRecord?.prize_image ??
        prizeRecord?.image_url ??
        prizeRecord?.imageUrl ??
        prizeRecord?.image_path ??
        '';
      const prizeImagePath =
        typeof rawPrizeImage === 'string'
          ? rawPrizeImage
          : rawPrizeImage?.url || rawPrizeImage?.path || '';

      // `||`, not `??`: a read can send arabic_prize_name as "" next to a filled
      // arabic_name, and the empty one used to win, so a copy lost its Arabic name.
      const arabicPrizeName = String(
        [prizeRecord, prize]
          .flatMap((r: any) => [r?.arabic_prize_name, r?.arabic_name, r?.arabicName, r?.name_ar, r?.prize_name_ar])
          .find((v: any) => typeof v === 'string' && v.trim()) ?? ''
      ).trim();
      const prizeCurrency = prizeRecord?.currency ?? prizeRecord?.prize_currency ?? 'USD';
      const prizeId = prizeRecord?.id ?? prize?.id;
      sourcePrizeIds.push(prizeId);
      const prizeGroup = this.fb.group({
        name: [prizeName, Validators.required],
        arabic_name: [arabicPrizeName, Validators.maxLength(100)],
        value: [prizeValue, [Validators.required, Validators.min(0)]],
        currency: [prizeCurrency, Validators.required],
        image_code: [prizeImagePath, Validators.required],
        category_type: [cascaderValue, Validators.required],
        id: [isDuplicate ? null : prizeId],

        //  Fulfilment, as the contest read reports it.
        // 
        //  voucher_code is deliberately absent here — the column is private, so
        //  no contest read returns it. loadPrizePools() fetches it separately
        //  from the one endpoint that may.
        prize_kind: [prizeRecord?.prize_kind || 'item'],
        code_mode: [prizeRecord?.code_mode || 'none'],
        quantity: [this.toPoolQuantity(prizeRecord?.quantity), [Validators.min(1)]],
        voucher_code: [''],
        redeem_instructions: [prizeRecord?.redeem_instructions || ''],
        arabic_redeem_instructions: [prizeRecord?.arabic_redeem_instructions || ''],
        expires_at: [prizeRecord?.expires_at ? new Date(prizeRecord.expires_at) : null],
        // Write-only: a pool can be topped up but never read back.
        codes: [''],
        code_pool_total: [0],
        code_pool_claimed: [0],
        ...this.generatedVoucherControls(prizeRecord?.voucher_config),
      });
      this.prizes.push(prizeGroup);
      this.prizeCategoryOptions.push(this.cloneCategoriesTree());
      const index = this.prizes.length - 1;
      if (!Array.isArray(this.imagePreview)) {
        this.imagePreview = [];
      }
      let prizeImageUrl = prizeImagePath || '';
      if (prizeImageUrl.includes('https://xi5f-5y62-kbyr.n7e.xano.iohttps://')) {
        prizeImageUrl = prizeImageUrl.replace(
          'https://xi5f-5y62-kbyr.n7e.xano.iohttps://',
          'https://'
        );
      }
      this.imagePreview[index] = {
        image_code: prizeImageUrl,
      };
    }

    this.loadPrizePools(sourcePrizeIds);
  }

  /**
   * A saved prize quantity as the form holds it. The API stores "unlimited" as 0, but the
   * control means unlimited by blank and requires at least 1, so a read 0 made every
   * copied or edited prize invalid behind a field that only shared codes show — step 2
   * then said "Prize details are incomplete" with every visible field filled.
   */
  private toPoolQuantity(value: any): number | null {
    const quantity = Number(value);
    return value !== null && value !== '' && Number.isFinite(quantity) && quantity > 0 ? quantity : null;
  }

  /**
   * Fill in the parts of a prize's fulfilment that no contest read carries.
   *
   * `voucher_code` is a private column — the prize addons every contest read
   * goes through strip it, because several of them are open to participants and
   * a shared code IS the prize. This endpoint is the one place the owner may
   * read it back, so an edit that does not re-fetch here would show a blank box
   * and quietly clear the code on save.
   *
   * Best-effort: a failure leaves the boxes empty rather than blocking the edit,
   * and nothing is overwritten server-side unless the client actually saves.
   */
  private loadPrizePools(sourcePrizeIds: any[] = []): void {
    if (!this.contestID || !this.prizes.length) return;

    this.pagesService.getById('CONTEST_PRIZE_POOL', this.contestID).subscribe({
      next: (res: any) => {
        const pools: any[] = Array.isArray(res?.prizes) ? res.prizes : [];
        if (!pools.length) return;

        this.prizes.controls.forEach((group, index) => {
          // A copy's prizes carry no id of their own; match them to the source's.
          const groupId = String(group.get('id')?.value ?? sourcePrizeIds[index] ?? '');
          if (!groupId || groupId === 'null' || groupId === 'undefined') return;
          const pool = pools.find((x) => String(x.id) === groupId);
          if (!pool) return;

          group.patchValue({
            prize_kind: pool.prize_kind || 'item',
            code_mode: pool.code_mode || 'none',
            quantity: this.toPoolQuantity(pool.quantity),
            voucher_code: pool.voucher_code || '',
            // A field the pool does not report keeps what the contest read gave it;
            // blanking it here would clear it server-side on the next save.
            redeem_instructions: pool.redeem_instructions ?? group.get('redeem_instructions')?.value ?? '',
            arabic_redeem_instructions: pool.arabic_redeem_instructions ?? group.get('arabic_redeem_instructions')?.value ?? '',
            expires_at: pool.expires_at ? new Date(pool.expires_at) : null,
            code_pool_total: pool.code_pool?.total ?? 0,
            code_pool_claimed: pool.code_pool?.claimed ?? 0,
          }, { emitEvent: false });

          // The codes already in the pool, listed read-only under the box for new
          // ones. Only on an edit: a copy does not own the source's codes.
          if (pool.code_mode === 'unique' && group.get('id')?.value) {
            this.uploadedCodesByPrize.set(group, readGiftCardPool(pool).codes);
          }

          // A generated-voucher prize carries its batch; show the rules it was saved with.
          const vc = pool.voucher_config;
          if (pool.code_mode === 'generated' && vc) {
            group.patchValue({
              voucher_value: vc.value ?? null,
              voucher_currency: vc.currency || 'USD',
              voucher_expires_at: parseServerDate(vc.expires_at),
              usage_type: vc.usage_type || 'SINGLE_USE',
              carry_over_balance: vc.carry_over_balance === true,
              max_uses: vc.max_uses ?? null,
              voucher_config: vc,
              // The batch size, which is the number of vouchers.
              quantity: this.toPoolQuantity(vc.quantity ?? pool.quantity),
            }, { emitEvent: false });
          }
        });
        if (pools.some((x) => x.code_mode === 'generated')) this.ensureVoucherCurrencies();
      },
      error: () => {
        // Older backend, or a contest this account cannot read the pool for.
        // The rest of the prize edit still works.
      },
    });
  }

  async getContestDetails(id: string, isDuplicate: boolean = false) {
    if (!isDuplicate) this.editDetailsLoaded = false;
    this.sharedService.showLoader();
    try {
      const response: any = await firstValueFrom(
        this.pagesService.getById('GET_SINGLE_CONTEST', id)
      );
      if (response) {
        const rawContest = Array.isArray(response) ? response[0] : response;
        const responseData = rawContest?.data ?? rawContest;
        const normalizedResponse = Array.isArray(responseData) ? responseData[0] : responseData;
        const baseContest = normalizedResponse?.contest ? normalizedResponse.contest : normalizedResponse;
        const contestData = { ...(baseContest || {}) };

        const extractArray = (value: any): any[] | null => {
          if (Array.isArray(value)) return value;
          if (Array.isArray(value?.items)) return value.items;
          if (Array.isArray(value?.data)) return value.data;
          return null;
        };

        const pickFirstArray = (values: any[]): any[] => {
          for (const value of values) {
            const extracted = extractArray(value);
            if (extracted && extracted.length > 0) return extracted;
          }
          for (const value of values) {
            const extracted = extractArray(value);
            if (extracted) return extracted;
          }
          return [];
        };

        let prizesData = pickFirstArray([
          normalizedResponse?._contest_prize,
          normalizedResponse?.prizes,
          normalizedResponse?._contest_prize_of_contest_list,
          normalizedResponse?.contest_prize,
          normalizedResponse?.contest_prizes,
          normalizedResponse?._contest_prize_of_contest,
          normalizedResponse?.prize,
          baseContest?._contest_prize,
          baseContest?.prizes,
          baseContest?._contest_prize_of_contest_list,
          baseContest?.contest_prize,
          baseContest?.contest_prizes,
          baseContest?._contest_prize_of_contest,
          baseContest?.prize,
        ]);

        let levelsData = pickFirstArray([
          normalizedResponse?._levels_of_contest,
          normalizedResponse?.levels,
          normalizedResponse?._levels_of_contest_list,
          normalizedResponse?.levels_of_contest,
          normalizedResponse?.levels_of_contest_list,
          normalizedResponse?.contest_levels,
          normalizedResponse?.level_details,
          baseContest?._levels_of_contest,
          baseContest?.levels,
          baseContest?._levels_of_contest_list,
          baseContest?.levels_of_contest,
          baseContest?.levels_of_contest_list,
          baseContest?.contest_levels,
          baseContest?.level_details,
        ]);

        if (Array.isArray(prizesData)) {
          contestData._contest_prize = prizesData;
        }

        if (Array.isArray(levelsData)) {
          contestData._levels_of_contest = levelsData;
        }

        const mergedBanners = pickFirstArray([
          normalizedResponse?.banners,
          normalizedResponse?.banner,
          baseContest?.banners,
          baseContest?.banner,
        ]);

        if (Array.isArray(mergedBanners)) {
          contestData.banners = mergedBanners;
        }

        // Fallback: if prizes/levels missing in single contest response, try pulling from list endpoint
        if ((prizesData.length === 0 || levelsData.length === 0) && id) {
          try {
            const listResponse: any = await firstValueFrom(
              this.pagesService.getAll('GET_ALL_CONTESTS')
            );
            const listItems = Array.isArray(listResponse?.data)
              ? listResponse.data
              : Array.isArray(listResponse?.items)
                ? listResponse.items
                : Array.isArray(listResponse)
                  ? listResponse
                  : [];

            const fallbackContest = listItems.find(
              (item: any) => String(item?.id) === String(id)
            );

            if (fallbackContest) {
              if (prizesData.length === 0) {
                prizesData = pickFirstArray([
                  fallbackContest?._contest_prize,
                  fallbackContest?.prizes,
                  fallbackContest?._contest_prize_of_contest_list,
                  fallbackContest?.contest_prize,
                  fallbackContest?.contest_prizes,
                  fallbackContest?._contest_prize_of_contest,
                  fallbackContest?.prize,
                ]);
                if (Array.isArray(prizesData)) {
                  contestData._contest_prize = prizesData;
                }
              }

              if (levelsData.length === 0) {
                levelsData = pickFirstArray([
                  fallbackContest?._levels_of_contest,
                  fallbackContest?.levels,
                  fallbackContest?._levels_of_contest_list,
                  fallbackContest?.levels_of_contest,
                  fallbackContest?.levels_of_contest_list,
                  fallbackContest?.contest_levels,
                  fallbackContest?.level_details,
                ]);
                if (Array.isArray(levelsData)) {
                  contestData._levels_of_contest = levelsData;
                }
              }

              if (!Array.isArray(contestData.banners) || contestData.banners.length === 0) {
                const fallbackBanners = pickFirstArray([
                  fallbackContest?.banners,
                  fallbackContest?.banner,
                ]);
                if (Array.isArray(fallbackBanners)) {
                  contestData.banners = fallbackBanners;
                }
              }
            }
          } catch (error) {
            // Ignore fallback failure; main data is already loaded
          }
        }

        this.singleContestData = contestData;
        // A copy is a new campaign; a sent campaign with AR levels already used its AR slot.
        this.campaignAlreadyCountsForAr = !isDuplicate && countsTowardArUsage(contestData, null);
        this.checkContestLimit();

        const ageValue = this.singleContestData.age;
        const ageArray = Array.isArray(ageValue) ? ageValue : [ageValue];

        const locationValue = this.singleContestData.location;
        const locationArray = Array.isArray(locationValue)
          ? locationValue
          : locationValue
            ? String(locationValue).split(',').map((item: string) => item.trim()).filter(Boolean)
            : [];

        let startDate = this.singleContestData.start_date
          ? new Date(this.singleContestData.start_date)
          : null;

        let endDate = this.singleContestData.end_date
          ? new Date(this.singleContestData.end_date)
          : null;

        let prizeAnnounceDate = this.singleContestData.prize_announce_date
          ? new Date(this.singleContestData.prize_announce_date)
          : null;

        // A copy of a campaign that has ended would be over before it starts, so its whole
        // schedule moves forward by one offset: campaign, prize announcement and every level,
        // the way Duplicate as Draft shifts it on the server. Rebuilding only start and end
        // put the prize announcement at end + 24h and left the levels at the old dates.
        // A campaign that has not ended keeps its dates.
        let duplicateShiftMs = 0;
        if (isDuplicate) {
          const now2 = new Date();
          if (!endDate || endDate.getTime() < now2.getTime()) {
            const newStart = new Date(now2.getTime() + 60 * 60 * 1000);
            if (startDate && endDate && endDate.getTime() > startDate.getTime()) {
              duplicateShiftMs = newStart.getTime() - startDate.getTime();
              const sourceEnd = endDate.getTime();
              startDate = newStart;
              endDate = new Date(sourceEnd + duplicateShiftMs);
              prizeAnnounceDate = prizeAnnounceDate && prizeAnnounceDate.getTime() > sourceEnd
                ? new Date(prizeAnnounceDate.getTime() + duplicateShiftMs)
                : new Date(endDate.getTime() + 24 * 60 * 60 * 1000);
            } else {
              startDate = newStart;
              endDate = new Date(startDate.getTime() + 7 * 24 * 60 * 60 * 1000);
              prizeAnnounceDate = new Date(endDate.getTime() + 24 * 60 * 60 * 1000);
            }
          }
        }

        const originalName = this.singleContestData.name || '';
        const originalArabicName = (this.singleContestData.arabic_name || '').trim();
        const duplicateName = isDuplicate
          ? originalName ? `${originalName} (Copy)` : 'Campaign (Copy)'
          : originalName;
        const duplicateArabicName = isDuplicate
          ? originalArabicName ? `${originalArabicName} (نسخة)` : ''
          : originalArabicName;

        // The API drops the flag on save and always returns false, but it keeps the age
        // groups. A saved age group is the restriction, so that is what turns it on.
        const isAgeRestricted = !!(this.singleContestData.is_age_restricted
          ?? this.singleContestData.isAgeRestricted) || hasAgeGroup(ageArray);

        this.addContentForm.patchValue({
          name: duplicateName,
          arabic_name: duplicateArabicName,
          details: this.singleContestData.details,
          arabic_details: this.singleContestData.arabic_details || this.singleContestData.arabicDetails,
          gender: this.singleContestData.gender,
          is_age_restricted: isAgeRestricted,
          age: ageArray,
          location: locationArray,
          number_of_winners: this.singleContestData.number_of_winners ?? '',
          type: this.singleContestData.type,
          client_allow_list: Array.isArray(this.singleContestData.client_allow_list)
            ? this.singleContestData.client_allow_list
            : (this.singleContestData.client_allow_list || '')
              .split(',')
              .map((entry: string) => entry.trim())
              .filter(Boolean),
          // Saved as a word, returned as a number (Medium comes back as 1), which matched
          // neither option, so a Medium campaign reopened with no tier selected.
          kyc_tier: toKycTier(this.singleContestData.kyc_tier ?? this.singleContestData.kycTier),
          sharingSetting: this.toSharingSetting(
            this.singleContestData.sharingSetting || this.singleContestData.sharing_setting
          ),
          format_of_contest: this.singleContestData.format_of_contest || this.singleContestData.formatOfContest,
          totallevels: Array.isArray(levelsData) ? levelsData.length : 0,
          business_objective: this.singleContestData.business_objective || [],
          draw_type: this.singleContestData.draw_type || '',
          all_completers: this.singleContestData.all_completers === true,
          no_of_users: this.singleContestData.no_of_users || '',
          points_pool_per_contest: this.singleContestData.points_pool_per_contest ?? '',
        });
        // Number of Winners is disabled and not required while All Completers
        // is on, so the control has to be reconciled once the contest is in.
        this.syncWinnersControl();
        this.syncLocationCascaderFromForm();
        // "All Countries" can only be recognised against the country list, which otherwise
        // loads on first dropdown open — leaving a saved all-countries campaign looking unset.
        if (locationArray?.length) {
          this.getCountryRecords();
        }
        this.syncInterestCascaderFromForm();
        this.addContentForm.patchValue({
          start_date: startDate,
          end_date: endDate,
        }, { emitEvent: false });

        setTimeout(() => {
          this.addContentForm.patchValue({
            prize_announce_date: prizeAnnounceDate
          });
          // A campaign saved with its prize announcement before its end shows that straight away.
          const prizeDateControl = this.addContentForm.get('prize_announce_date');
          if (prizeDateControl?.hasError('beforeEndDate')) {
            prizeDateControl.markAsTouched();
          }
        }, 0);
        if (duplicateShiftMs > 0) {
          this.notifyDuplicateDatesMoved(duplicateShiftMs);
        }

        // Rest of your code...
        this.bannerPreviews = [];
        this.originalBannerObjects = [];
        const bannersArray = this.addContentForm.get('banners_code') as FormArray;
        bannersArray.clear();
        const bannersData = Array.isArray(this.singleContestData.banners)
          ? this.singleContestData.banners
          : this.singleContestData.banners
            ? [this.singleContestData.banners]
            : this.singleContestData.banner
              ? [this.singleContestData.banner]
              : [];

        if (Array.isArray(bannersData) && bannersData.length > 0) {
          bannersData.forEach((banner: any) => {
            let imageUrl = banner.url || banner.path || '';

            if (imageUrl.includes('https://xi5f-5y62-kbyr.n7e.xano.iohttps://')) {
              imageUrl = imageUrl.replace(
                'https://xi5f-5y62-kbyr.n7e.xano.iohttps://',
                'https://'
              );
            }

            this.bannerPreviews.push(imageUrl);
            this.originalBannerObjects.push({ ...banner });
            bannersArray.push(this.fb.control(imageUrl));
          });
        }

        this.imagePreview = [];
        this.originalPrizeImageObjects = [];
        if (Array.isArray(prizesData)) {
          prizesData.forEach(
            (prize: any, index: number) => {
              let prizeImageUrl =
                prize?.image?.url ||
                prize?.image?.path ||
                prize?.image_url ||
                prize?.imageUrl ||
                prize?.image_path ||
                '';

              if (prizeImageUrl.includes('https://xi5f-5y62-kbyr.n7e.xano.iohttps://')) {
                prizeImageUrl = prizeImageUrl.replace(
                  'https://xi5f-5y62-kbyr.n7e.xano.iohttps://',
                  'https://'
                );
              }

              this.imagePreview[index] = {
                image_code: prizeImageUrl,
              };
              if (prize?.image && typeof prize.image === 'object') {
                this.originalPrizeImageObjects[index] = { ...prize.image };
              }
            }
          );
        }

        // ✅ Sort levels by their timeline or id to maintain order
        if (Array.isArray(levelsData) && levelsData.length > 0) {
          const sortedLevels = [...levelsData].sort((a, b) => {
            // If there's a sequence/order field in your data, use it
            if (a.order !== undefined && b.order !== undefined) {
              return a.order - b.order;
            }
            // Otherwise, sort by timeline
            if (a.level_timeline && b.level_timeline) {
              return new Date(a.level_timeline).getTime() - new Date(b.level_timeline).getTime();
            }
            // Fallback to id if available
            if (a.id && b.id) {
              return a.id - b.id;
            }
            return 0;
          });
          // A copy owns new level/question records; do not hydrate source IDs into the form.
          this.setLevelsData(sortedLevels, isDuplicate, duplicateShiftMs);
        } else {
          this.setLevelsData([]);
        }

        await this.setPrizesData(Array.isArray(prizesData) ? prizesData : [], isDuplicate);

        if (isDuplicate) {
          this.contestID = 0;
          this.isEditMode = false;
          this.isDuplicateMode = true;
          this.singleContestData.id = 0;
          this.singleContestData.status = 'draft';
          if (Array.isArray(this.singleContestData._levels_of_contest)) {
            this.singleContestData._levels_of_contest.forEach((l2: any) => {
              delete l2.id;
              delete l2.level_id;
            });
          }
          if (Array.isArray(this.singleContestData._contest_prize)) {
            this.singleContestData._contest_prize.forEach((p2: any) => {
              delete p2.id;
              delete p2.prize_id;
            });
          }
        }

        // Contest Category — resolve the saved leaf id back into a cascader path once categories are loaded.
        if (!this.contestCategoriesLoaded) {
          await this.loadContestCategoriesAsync();
        }
        const existingCategoryId = this.singleContestData.contest_category
          ?? this.singleContestData.contest_category_id
          ?? this.singleContestData._contest_category?.id
          ?? this.singleContestData.category_id
          ?? null;
        if (existingCategoryId) {
          this.addContentForm.get('category_id')?.setValue(this.buildContestCategoryPath(existingCategoryId));
        }
        if (!isDuplicate) this.editDetailsLoaded = true;
      }
    } catch (error) {
      this.sharedService.showError(error, 'Failed to fetch campaign details.');
    } finally {
      this.sharedService.hideLoader();
    }
  }

  /** Tells the client a copy's dates were moved, so a new end date is not a surprise. */
  private notifyDuplicateDatesMoved(shiftMs: number): void {
    const days = Math.round(shiftMs / (24 * 60 * 60 * 1000));
    this.sharedService.createNotification('info', days >= 1
      ? this.translate.instant('common.duplicateDatesMovedDays', { days })
      : this.translate.instant('common.duplicateDatesMoved'));
  }
  imgPrizeSrc(index: number): string | undefined {
    return this.imagePreview[index]?.image_code || '';
  }
  convertImageUrlToBase64(imageUrl: string): Promise<string> {
    if (imageUrl === '') {
      return Promise.resolve('');
    } else {
      return new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.onload = function () {
          const reader = new FileReader();
          reader.onloadend = function () {
            resolve(reader.result as string);
          };
          reader.readAsDataURL(xhr.response);
        };
        xhr.onerror = function () {
          reject(new Error('Failed to convert image to base64'));
        };
        xhr.open('GET', imageUrl);
        xhr.responseType = 'blob';
        xhr.send();
      });
    }
  }
  addPrize(): void {
    const lastIndex = this.prizeArray.length - 1;
    if (lastIndex >= 0) {
      const lastPrizeGroup = this.prizeArray.at(lastIndex) as FormGroup;
      // Mark all controls in the group as touched, then the group itself
      Object.values(lastPrizeGroup.controls).forEach((control) => {
        control.markAsTouched();
        control.updateValueAndValidity({ emitEvent: false }); // Avoid multiple events if not needed
      });
      lastPrizeGroup.markAsTouched();
      lastPrizeGroup.updateValueAndValidity({ emitEvent: true });
      if (lastPrizeGroup.invalid) {
        this.sharedService.createNotification(
          'error',
          'Please fill in the previous prize details before adding a new one.'
        );
        return;
      }
    }
    this.prizeArray.push(this.createPrize());
    this.prizeCategoryOptions.push(this.cloneCategoriesTree());
    this.imagePreview.push({ image_code: null }); // Initialize empty preview for the new prize
  }
  removePrize(index: number): void {
    if (this.prizeArray.length <= 1) {
      this.sharedService.createNotification(
        'error',
        'At least one prize is required. Cannot remove the last prize.'
      );
      return;
    }
    const prizeGroup = this.prizeArray.at(index) as FormGroup;
    const hasData = Object.keys(prizeGroup.controls).some((key) => {
      const control = prizeGroup.get(key);
      if (!control) return false;
      // Name and image_code are primary data points.
      if (key === 'name' && control.value) return true;
      if (key === 'value' && control.value) return true;
      if (key === 'image_code' && control.value) return true;
      return false;
    });
    if (hasData) {
      this.showPrizeRemoveConfirm(index);
    } else {
      // If no data, remove directly
      this.prizeArray.removeAt(index);
      this.imagePreview.splice(index, 1);
      this.prizeCategoryOptions.splice(index, 1);
    }
  }
  private showPrizeRemoveConfirm(index: number): void {
    this.modal.confirm({
      nzTitle: 'Confirm Removal',
      nzContent:
        'This prize contains data. Are you sure you want to remove it?',
      nzOnOk: () => {
        this.prizeArray.removeAt(index);
        this.imagePreview.splice(index, 1);
        this.prizeCategoryOptions.splice(index, 1);
      },
      nzClassName: 'contest-styled-modal',
      nzCentered: true,
      nzOnCancel: () => { },
    });
  }
  get banners(): FormArray {
    return this.addContentForm.get('banners_code') as FormArray;
  }
  private fileToBase64(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  }
  showQRModal() {
    this.isQrVisible = true;
  }
  handleQROk(): void {
    this.isQrVisible = false;
    this.qrModalIndex = null;
  }
  handleQRCancel(): void {
    this.isQrVisible = false;
    this.qrModalIndex = null;
  }
  onDownloadQR(qrElement: ElementRef | HTMLElement): void {
    const canvas = (qrElement as HTMLElement).querySelector('canvas');
    if (canvas instanceof HTMLCanvasElement) {
      const imgData = canvas.toDataURL('image/png');
      const a = document.createElement('a');
      a.href = imgData;
      a.download = `qr-code.png`;
      a.click();
    } else {
    }
  }

  private subscribeToPointsChanges(): void {
    // Subscribe to changes in the levels array
    this.levels.valueChanges.subscribe(() => {
      // this.calculateTotalPoints();
    });
  }

  private getLevelBaseBounds(levelIndex: number, forStartDate: boolean = false): { min: Date | null; max: Date | null } {
    const contestStartDate = this.addContentForm.get('start_date')?.value;
    const contestEndDate = this.addContentForm.get('end_date')?.value;

    if (!contestStartDate || !contestEndDate) {
      return { min: null, max: null };
    }

    if (levelIndex === 0) {
      return { min: new Date(contestStartDate), max: new Date(contestEndDate) };
    }

    if (forStartDate) {
      // For start date: min = previous level's start date (allows same start time)
      const previousLevelStartDate = this.getPreviousLevelStartDate(levelIndex);
      const min = previousLevelStartDate || new Date(contestStartDate);
      return { min, max: new Date(contestEndDate) };
    }

    // For expiry date: min = previous level's expiry date (cannot end before previous ends)
    const previousLevelDate = this.getPreviousLevelDate(levelIndex);
    if (!previousLevelDate) {
      return { min: null, max: null };
    }

    return { min: new Date(previousLevelDate), max: new Date(contestEndDate) };
  }

  getDisabledLevelStartDate = (levelIndex: number) => {
    return (current: Date): boolean => {
      if (!current) return false;

      const { min, max } = this.getLevelBaseBounds(levelIndex, true);
      if (!min || !max) return true;

      const currentDateOnly = new Date(current);
      currentDateOnly.setHours(0, 0, 0, 0);

      const minDateOnly = new Date(min);
      minDateOnly.setHours(0, 0, 0, 0);

      const maxDateOnly = new Date(max);
      maxDateOnly.setHours(0, 0, 0, 0);

      if (currentDateOnly.getTime() < minDateOnly.getTime() || currentDateOnly.getTime() > maxDateOnly.getTime()) {
        return true;
      }

      const levelExpiry = this.levels.at(levelIndex)?.get('level_timeline')?.value;
      if (levelExpiry) {
        const expiryDateOnly = new Date(levelExpiry);
        expiryDateOnly.setHours(0, 0, 0, 0);
        if (currentDateOnly.getTime() > expiryDateOnly.getTime()) {
          return true;
        }
      }

      return false;
    };
  };

  getDisabledLevelStartDateTime = (levelIndex: number) => {
    return (current: Date | Date[]) => {
      const currentDate = Array.isArray(current) ? current[0] : current;
      const { min, max } = this.getLevelBaseBounds(levelIndex, true);

      if (!currentDate || !min || !max) {
        return {
          nzDisabledHours: () => this.range(0, 24),
          nzDisabledMinutes: () => this.range(0, 60),
          nzDisabledSeconds: () => this.range(0, 60)
        };
      }

      const currentDateOnly = new Date(currentDate);
      currentDateOnly.setHours(0, 0, 0, 0);
      const minDateOnly = new Date(min);
      minDateOnly.setHours(0, 0, 0, 0);
      const maxDateOnly = new Date(max);
      maxDateOnly.setHours(0, 0, 0, 0);

      const levelExpiryRaw = this.levels.at(levelIndex)?.get('level_timeline')?.value;
      const levelExpiry = levelExpiryRaw ? new Date(levelExpiryRaw) : null;
      const levelExpiryDateOnly = levelExpiry ? new Date(levelExpiry) : null;
      levelExpiryDateOnly?.setHours(0, 0, 0, 0);

      return {
        nzDisabledHours: () => {
          const disabled: number[] = [];

          if (currentDateOnly.getTime() === minDateOnly.getTime()) {
            disabled.push(...this.range(0, min.getHours()));
          }
          if (currentDateOnly.getTime() === maxDateOnly.getTime()) {
            disabled.push(...this.range(max.getHours() + 1, 24));
          }
          if (levelExpiry && levelExpiryDateOnly && currentDateOnly.getTime() === levelExpiryDateOnly.getTime()) {
            disabled.push(...this.range(levelExpiry.getHours() + 1, 24));
          }

          return [...new Set(disabled)];
        },
        nzDisabledMinutes: (hour: number) => {
          const disabled: number[] = [];

          if (currentDateOnly.getTime() === minDateOnly.getTime() && hour === min.getHours()) {
            disabled.push(...this.range(0, min.getMinutes()));
          }
          if (currentDateOnly.getTime() === maxDateOnly.getTime() && hour === max.getHours()) {
            disabled.push(...this.range(max.getMinutes() + 1, 60));
          }
          if (levelExpiry && levelExpiryDateOnly && currentDateOnly.getTime() === levelExpiryDateOnly.getTime() && hour === levelExpiry.getHours()) {
            disabled.push(...this.range(levelExpiry.getMinutes(), 60));
          }

          return [...new Set(disabled)];
        },
        nzDisabledSeconds: (hour: number, minute: number) => {
          const disabled: number[] = [];

          if (currentDateOnly.getTime() === minDateOnly.getTime() && hour === min.getHours() && minute === min.getMinutes()) {
            disabled.push(...this.range(0, min.getSeconds()));
          }
          if (currentDateOnly.getTime() === maxDateOnly.getTime() && hour === max.getHours() && minute === max.getMinutes()) {
            disabled.push(...this.range(max.getSeconds() + 1, 60));
          }
          if (levelExpiry && levelExpiryDateOnly &&
            currentDateOnly.getTime() === levelExpiryDateOnly.getTime() &&
            hour === levelExpiry.getHours() &&
            minute === levelExpiry.getMinutes()) {
            disabled.push(...this.range(levelExpiry.getSeconds(), 60));
          }

          return [...new Set(disabled)];
        }
      };
    };
  };

  // Method to get disabled dates for a specific level
  getDisabledLevelDate = (levelIndex: number) => {
    return (current: Date): boolean => {
      if (!current) return false;

      const { min, max } = this.getLevelBaseBounds(levelIndex);
      if (!min || !max) return true;

      const levelStartRaw = this.levels.at(levelIndex)?.get('level_start_date')?.value;
      const levelStart = levelStartRaw ? new Date(levelStartRaw) : null;
      const effectiveMin = levelStart && levelStart.getTime() > min.getTime() ? levelStart : min;

      const currentDateOnly = new Date(current);
      currentDateOnly.setHours(0, 0, 0, 0);

      const minDateOnly = new Date(effectiveMin);
      minDateOnly.setHours(0, 0, 0, 0);

      const maxDateOnly = new Date(max);
      maxDateOnly.setHours(0, 0, 0, 0);

      return currentDateOnly.getTime() < minDateOnly.getTime() ||
        currentDateOnly.getTime() > maxDateOnly.getTime();
    };
  };

  // Method to get disabled times for a specific level timeline
  getDisabledLevelDateTime = (levelIndex: number) => {
    return (current: Date | Date[]) => {
      const currentDate = Array.isArray(current) ? current[0] : current;
      const { min, max } = this.getLevelBaseBounds(levelIndex);

      if (!currentDate || !min || !max) {
        return {
          nzDisabledHours: () => this.range(0, 24),
          nzDisabledMinutes: () => this.range(0, 60),
          nzDisabledSeconds: () => this.range(0, 60)
        };
      }

      const levelStartRaw = this.levels.at(levelIndex)?.get('level_start_date')?.value;
      const levelStart = levelStartRaw ? new Date(levelStartRaw) : null;
      const strictAfterStart = !!levelStart;
      const effectiveMin = levelStart && levelStart.getTime() > min.getTime() ? levelStart : min;

      const currentDateOnly = new Date(currentDate);
      currentDateOnly.setHours(0, 0, 0, 0);
      const minDateOnly = new Date(effectiveMin);
      minDateOnly.setHours(0, 0, 0, 0);
      const endDate = new Date(max);
      const endDateOnly = new Date(endDate);
      endDateOnly.setHours(0, 0, 0, 0);

      return {
        nzDisabledHours: () => {
          const disabled: number[] = [];

          if (currentDateOnly.getTime() === minDateOnly.getTime()) {
            disabled.push(...this.range(0, effectiveMin.getHours()));
          }

          if (currentDateOnly.getTime() === endDateOnly.getTime()) {
            disabled.push(...this.range(endDate.getHours() + 1, 24));
          }

          return disabled;
        },
        nzDisabledMinutes: (hour: number) => {
          const disabled: number[] = [];

          if (currentDateOnly.getTime() === minDateOnly.getTime() &&
            hour === effectiveMin.getHours()) {
            disabled.push(...this.range(0, strictAfterStart ? effectiveMin.getMinutes() + 1 : effectiveMin.getMinutes()));
          }

          if (currentDateOnly.getTime() === endDateOnly.getTime() &&
            hour === endDate.getHours()) {
            disabled.push(...this.range(endDate.getMinutes() + 1, 60));
          }

          return disabled;
        },
        nzDisabledSeconds: (hour: number, minute: number) => {
          const disabled: number[] = [];

          if (currentDateOnly.getTime() === minDateOnly.getTime() &&
            hour === effectiveMin.getHours() &&
            minute === effectiveMin.getMinutes()) {
            disabled.push(...this.range(0, strictAfterStart ? effectiveMin.getSeconds() + 1 : effectiveMin.getSeconds()));
          }

          if (currentDateOnly.getTime() === endDateOnly.getTime() &&
            hour === endDate.getHours() &&
            minute === endDate.getMinutes()) {
            disabled.push(...this.range(endDate.getSeconds() + 1, 60));
          }

          return disabled;
        }
      };
    };
  };
  // Helper method to get the expiry date from the previous level
  getPreviousLevelDate(currentLevelIndex: number): Date | null {
    if (currentLevelIndex <= 0) return null;
    const previousLevel = this.levels.at(currentLevelIndex - 1);
    const previousLevelTimeline = previousLevel?.get('level_timeline')?.value;
    return previousLevelTimeline ? new Date(previousLevelTimeline) : null;
  }

  // Helper method to get the start date from the previous level
  getPreviousLevelStartDate(currentLevelIndex: number): Date | null {
    if (currentLevelIndex <= 0) return null;
    const previousLevel = this.levels.at(currentLevelIndex - 1);
    const previousLevelStart = previousLevel?.get('level_start_date')?.value;
    return previousLevelStart ? new Date(previousLevelStart) : null;
  }
  // Helper method to provide user-friendly date hints
  getLevelDateHint(levelIndex: number): string {
    const contestStartDate = this.addContentForm.get('start_date')?.value;
    const contestEndDate = this.addContentForm.get('end_date')?.value;
    if (!contestStartDate || !contestEndDate) {
      return 'Set campaign dates first';
    }
    if (levelIndex === 0) {
      return `Between campaign start and end dates`;
    }
    const previousLevelDate = this.getPreviousLevelDate(levelIndex);
    if (!previousLevelDate) {
      return `Select Level ${levelIndex} timeline first`;
    }
    return `From Level ${levelIndex} date onwards`;
  }
  // Optional: Add a method to clear all level timelines when contest dates change
  private clearAllLevelTimelines(): void {
    this.levels.controls.forEach((level, index) => {
      level.get('level_timeline')?.setValue(null);
    });
    this.sharedService.createNotification(
      'info',
      'All level timelines have been cleared due to campaign date changes.'
    );
  }
  private subscribeToContestDateChanges(): void {
    // Watch start date changes
    this.addContentForm.get('start_date')?.valueChanges.subscribe(() => {
    });
    // Watch end date changes
    this.addContentForm.get('end_date')?.valueChanges.subscribe(() => {
    });
  }
  private subscribeToLevelChanges(levelGroup: FormGroup, index: number): void {
    levelGroup.get('level_timeline')?.valueChanges.subscribe((value) => {
      if (value) {
      }
    });
    this.subscribeToLevelTypeChanges(levelGroup, index);
    // The receipt purchase window is checked against the level's own dates.
    for (const field of ['level_start_date', 'level_timeline']) {
      levelGroup.get(field)?.valueChanges.subscribe(() => {
        levelGroup.get('receipt_rules')?.updateValueAndValidity();
      });
    }
    levelGroup.get('accept_any_answer')?.valueChanges.subscribe((acceptAny) => {
      this.updateTextAnswerControls(levelGroup, !!acceptAny);
    });
    this.updateTextAnswerControls(levelGroup, !!levelGroup.get('accept_any_answer')?.value);

  }
  deletePrizeImage(index: number) {
    if (this.imagePreview[index]) {
      this.imagePreview[index].image_code = '';
    }
    const prizeControl = this.prizes.at(index);
    if (prizeControl) {
      prizeControl.get('image_code')?.setValue('');
    }
    const fileInput = document.getElementById(`prizeFileInput${index}`) as HTMLInputElement;
    if (fileInput) {
      fileInput.value = ''; // Important
    }
  }

  // Removal only touches the form. This used to delete a saved prize on the server the moment
  // the confirm was accepted, so leaving the wizard without saving still lost it, and it could
  // remove the last prize. The save diffs prizes by id and drops the missing ones itself.
  confirmRemovePrize(index: number): void {
    this.removePrize(index);
  }
  onFormatOptionSelect(format: 'integrated' | 'non-integrated'): void {
    this.addContentForm.patchValue({ format_of_contest: format });
    this.addContentForm.controls['format_of_contest'].markAsTouched();
  }

  /** 1-based numbers of the levels using a webhook type, which only an integrated campaign may have. */
  integratedLevelNumbers(): number[] {
    return this.levels.controls
      .map((level, index) => (INTEGRATED_LEVEL_TYPES.includes(level.get('type')?.value) ? index + 1 : 0))
      .filter(Boolean);
  }

  // Switching to non-integrated empties the type of every webhook level, so the admin has to pick
  // a type the format allows before submitting. This used to be skipped for every saved campaign
  // (edit mode returned early, meant only for the load), so an edited copy of an integrated
  // campaign could go out as non-integrated with its webhook levels intact. The load patches the
  // format before it builds the levels, so it never reaches a webhook level here.
  onFormatChange(value: string) {
    if (value !== 'non-integrated') {
      return;
    }
    const cleared = this.integratedLevelNumbers();
    if (!cleared.length) {
      return;
    }
    cleared.forEach((number) => {
      // Through the type control, so its subscription also drops the webhook fields and validators.
      const typeControl = this.levels.at(number - 1).get('type');
      typeControl?.setValue(null);
      typeControl?.markAsTouched();
    });
    this.sharedService.createNotification(
      'warning',
      this.translate.instant('Levels_Array.Level_type_integrated_cleared', { levels: cleared.join(', ') })
    );
  }

  // Level count management methods
  setLevelCount(count: number): void {
    if (this.isFirstToComplete() && count < 2) {
      this.sharedService.createNotification('warning', '"First To Complete" draw type requires at least 2 levels.');
      return;
    }
    this.selectedLevelCount = count;
    this.customLevelCount = null; // Clear custom input when button is selected
    this.updateLevels(count);
  }

  onCustomLevelInput(value: number): void {
    if (value && value > 0) {
      if (this.isFirstToComplete() && value < 2) {
        this.sharedService.createNotification('warning', '"First To Complete" draw type requires at least 2 levels.');
        // Keep the levels already there (typing "1" over 3 levels used to silently delete level 3).
        const keep = Math.max(2, this.levels.length);
        this.selectedLevelCount = keep <= 5 ? keep : null;
        this.customLevelCount = keep > 5 ? keep : null;
        this.updateLevels(keep);
        return;
      }
      // Limit to maximum 20 levels
      if (value > 20) {
        this.customLevelCount = 20;
        this.sharedService.createNotification(
          'warning',
          'Maximum 20 levels allowed'
        );
        this.updateLevels(20);
      } else {
        this.selectedLevelCount = null; // Clear button selection when custom input is used
        this.customLevelCount = value;
        this.updateLevels(value);
      }
    }
  }

  addLevel(): void {
    const newCount = this.levels.length + 1;
    if (newCount > 20) {
      this.sharedService.createNotification('warning', 'Maximum 20 levels allowed');
      return;
    }
    this.updateLevels(newCount);
    if (newCount <= 5) {
      this.selectedLevelCount = newCount;
      this.customLevelCount = null;
    } else {
      this.selectedLevelCount = null;
      this.customLevelCount = newCount;
    }
  }

  /**
   * Whether the "All Completers" toggle applies at all.
   *
   * It is a property of Instant-Result specifically: every other draw type
   * picks winners out of the completers, so "everyone wins" has no meaning
   * there and the toggle is not shown.
   */
  get isInstantResult(): boolean {
    return this.addContentForm?.get('draw_type')?.value === 'instant_result';
  }

  get isNoDraw(): boolean {
    return this.addContentForm?.get('draw_type')?.value === 'no_draw';
  }

  /** True only when the toggle is both applicable and on. */
  get isAllCompleters(): boolean {
    return this.isInstantResult && this.addContentForm?.get('all_completers')?.value === true;
  }

  /**
   * Keep "Number of Winners" in step with the toggle.
   *
   * With every completer winning there is no cap to state, so the field is
   * disabled and its required validator dropped — otherwise the form sits
   * invalid on a value the backend will ignore.
   *
   * The VALUE is deliberately left alone. Turning the toggle back off restores
   * the number the client last chose instead of making them retype it, and the
   * save keeps sending it: `number_of_winners` is NOT NULL on the contest table,
   * so a blank would be refused by the API whatever the toggle says.
   */
  private syncWinnersControl(): void {
    const winners = this.addContentForm.get('number_of_winners');
    if (!winners) return;

    if (this.isAllCompleters || this.isNoDraw) {
      winners.clearValidators();
      winners.disable({ emitEvent: false });
    } else {
      winners.setValidators([
        Validators.required, Validators.min(1), wholeNumberValidator,
        ...(this.hasAdvancedFields ? [winnersWithinParticipants] : []),
      ]);
      winners.enable({ emitEvent: false });
    }
    winners.updateValueAndValidity({ emitEvent: false });
    this.syncPrizeDateControl();
  }

  /**
   * A No Draw campaign announces no prize, so its Prize Announcement Date is hidden and disabled:
   * a disabled control is neither validated nor sent (see the payload's prize_announce_date).
   */
  private syncPrizeDateControl(): void {
    const prizeDate = this.addContentForm.get('prize_announce_date');
    if (!prizeDate) return;
    if (this.isNoDraw) {
      prizeDate.disable({ emitEvent: false });
    } else {
      prizeDate.enable({ emitEvent: false });
    }
  }

  updateNoOfUsersValidators(): void {
    const noOfUsersCtrl = this.addContentForm.get('no_of_users');
    if (!this.hasAdvancedFields) {
      noOfUsersCtrl?.clearValidators();
      noOfUsersCtrl?.updateValueAndValidity({ emitEvent: false });
      return;
    }
    const validators = [Validators.required, Validators.min(1), wholeNumberValidator];
    const pool = Number(this.addContentForm.get('points_pool_per_contest')?.value);
    let maxVal: number | null = null;
    if (this.maxParticipants > 0 && pool > 0) {
      maxVal = Math.min(this.maxParticipants, pool);
    } else if (this.maxParticipants > 0) {
      maxVal = this.maxParticipants;
    } else if (pool > 0) {
      maxVal = pool;
    }
    if (maxVal !== null) {
      validators.push(Validators.max(maxVal));
    }
    noOfUsersCtrl?.setValidators(validators);
    noOfUsersCtrl?.updateValueAndValidity({ emitEvent: false });
  }

  onAllCompletersChange(enabled: boolean): void {
    this.addContentForm.patchValue({ all_completers: enabled });
    this.syncWinnersControl();
  }

  onDrawTypeChange(drawType: string): void {
    this.addContentForm.patchValue({ draw_type: drawType });
    this.addContentForm.controls['draw_type'].markAsTouched();

    // Leaving Instant-Result takes the toggle with it — a random draw with
    // "everyone wins" set is a contradiction waiting to be saved.
    if (drawType !== 'instant_result') {
      this.addContentForm.patchValue({ all_completers: false }, { emitEvent: false });
    }
    this.syncWinnersControl();

    if (drawType === 'first_to_complete') {
      // The form's own level count: a new campaign starts with one level before any count
      // button is picked, so selectedLevelCount/customLevelCount are still null then.
      if (this.levels.length < 2) {
        this.sharedService.createNotification('warning', '"First To Complete" draw type requires at least 2 levels. Level count has been updated to 2.');
        this.selectedLevelCount = 2;
        this.customLevelCount = null;
        this.updateLevels(2);
      }
    }
  }

  isFirstToComplete(): boolean {
    return this.addContentForm?.get('draw_type')?.value === 'first_to_complete';
  }

  // Draw Type / Number of Participants are shown to SDK plans and, since Web QR,
  // to web_access plans too — both self-publish and so need the same controls.
  get hasAdvancedFields(): boolean {
    return this.hasSdkAccess || this.hasWebAccess;
  }

  /* ------------------------------ Allow-list ------------------------------ */

  // Standalone control for the phone widget — it never ships with the form.
  // Confirmed numbers are moved into addContentForm.client_allow_list as E.164
  // strings; ngx-intl-tel-input validates each entry against the country the
  // admin picks, the same widget and validation used on the signup screen.
  allowListPhoneControl = new FormControl<any>(null);
  allowListCountryISO: CountryISO = CountryISO.SaudiArabia;
  SearchCountryField = SearchCountryField;

  private async initializeAllowListCountry(): Promise<void> {
    const user = this.authService.getUserDetails() || {};
    const company = user.isCompany?.[0] || {};
    const country = user.country_code || user.country || user._user?.country
      || company.country || company._user?.country;
    const rawCountry = typeof country === 'object'
      ? country?.code || country?.iso2 || country?.name
      : country;
    try {
      let code = String(rawCountry || '').toLowerCase();
      if (!Object.values(CountryISO).includes(code as CountryISO)) {
        const currentCountry = rawCountry || await this.countryService.getUserCountry();
        const countries = await this.sharedService.getCountryRecords();
        code = countries.find(record => record.name.toLowerCase() === String(currentCountry).toLowerCase())?.code || '';
      }
      if (Object.values(CountryISO).includes(code as CountryISO)
        && !this.allowListPhoneControl.dirty && !this.allowListPhoneControl.touched) {
        this.allowListCountryISO = code as CountryISO;
        this.cdr.markForCheck();
      }
    } catch {
      // Keep the selectable fallback if country detection is unavailable.
    }
  }

  get allowListNumbers(): string[] {
    return this.addContentForm.get('client_allow_list')?.value || [];
  }

  // google-libphonenumber rejects Egyptian mobiles that are in fact valid, so the
  // signup screen accepts any 10-digit +20 national number. The allow-list has to
  // agree with it — a client who registered with such a number must be addable.
  private resolveAllowListNumber(): string | null {
    const value = this.allowListPhoneControl.value;
    if (!value) {
      return null;
    }

    if (value.e164Number && !this.allowListPhoneControl.errors?.['validatePhoneNumber']) {
      return value.e164Number;
    }

    const national = String(value.nationalNumber || value.number || '').replace(/\D/g, '');
    if (value.dialCode === '+20' && national.length === 10) {
      return `+20${national}`;
    }

    return null;
  }

  autoAddAllowListNumber(): void {
    const number = this.resolveAllowListNumber();
    if (!number) {
      return;
    }
    if (this.allowListNumbers.includes(number)) {
      return;
    }
    const control = this.addContentForm.get('client_allow_list');
    control?.setValue([...this.allowListNumbers, number]);
    control?.markAsTouched();
    this.allowListPhoneControl.reset(null, { emitEvent: false });
  }

  addAllowListNumber(): void {
    const number = this.resolveAllowListNumber();

    if (!number) {
      this.sharedService.createNotification(
        'error',
        this.translate.instant('addContests.clientAllowList.invalid')
      );
      return;
    }

    if (this.allowListNumbers.includes(number)) {
      this.sharedService.createNotification(
        'error',
        this.translate.instant('addContests.clientAllowList.duplicate')
      );
      return;
    }

    const control = this.addContentForm.get('client_allow_list');
    control?.setValue([...this.allowListNumbers, number]);
    control?.markAsTouched();
    this.allowListPhoneControl.reset(null);
  }

  removeAllowListNumber(number: string): void {
    const control = this.addContentForm.get('client_allow_list');
    control?.setValue(this.allowListNumbers.filter((entry) => entry !== number));
    control?.markAsTouched();
  }

  editAllowListNumber(number: string): void {
    if (this.allowListPhoneControl.value) {
      this.autoAddAllowListNumber();
    }
    this.removeAllowListNumber(number);
    this.allowListPhoneControl.setValue(number);
  }

  // The client allow-list only applies to Private access mode.
  private syncAllowListValidator(accessMode: string): void {
    const control = this.addContentForm.get('client_allow_list');
    if (!control) {
      return;
    }
    control.clearValidators();
    if (accessMode !== 'private') {
      control.setValue([], { emitEvent: false });
      this.allowListPhoneControl.reset(null, { emitEvent: false });
    }
    control.updateValueAndValidity({ emitEvent: false });
  }

  // Stepper navigation methods
  nextStep(): void {
    if (this.currentStep < 3) {
      // Check if participants exceed plan limit - do not allow client to continue
      if (this.currentStep === 1 && this.hasAdvancedFields && this.maxParticipants > 0) {
        const noOfUsers = Number(this.addContentForm.get('no_of_users')?.value);
        if (noOfUsers && noOfUsers > this.maxParticipants) {
          this.addContentForm.get('no_of_users')?.markAsTouched();
          this.modal.warning({
            nzTitle: this.isArabic ? 'تنبيه حد المشاركين' : 'Participant Limit Warning',
            nzContent: this.isArabic
              ? `خطتك تسمح بحد أقصى <b>${this.maxParticipants}</b> مشارك، لكنك أدخلت <b>${noOfUsers}</b>. لا يمكنك المتابعة بما يتجاوز حد خطتك. يرجى تعديل عدد المشاركين.`
              : `Your plan allows a maximum of <b>${this.maxParticipants}</b> participants, but you have entered <b>${noOfUsers}</b>. You cannot continue beyond your plan limit. Please reduce the number of participants.`,
            nzOkText: this.isArabic ? 'موافق' : 'OK',
            nzCentered: true,
            nzClassName: 'contest-styled-modal contest-modal-warning',
          });
          return;
        }
      }

      if (this.validateCurrentStep()) {
        this.currentStep++;
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
    }
  }

  previousStep(): void {
    if (this.currentStep > 1) {
      this.currentStep--;
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }

  // Calculate time duration for levels preview
  calculateTimeFromNow(timeline: Date | string | null): string {
    if (!timeline) return 'Timeline not set';

    const contestStartDate = this.addContentForm.get('start_date')?.value;
    const timelineDate = new Date(timeline);

    if (!contestStartDate) {
      // If no contest start date, calculate from now
      const now = new Date();
      const diff = timelineDate.getTime() - now.getTime();
      return this.formatTimeDuration(diff);
    }

    // Calculate from contest start date
    const startDate = new Date(contestStartDate);
    const diff = timelineDate.getTime() - startDate.getTime();
    return this.formatTimeDuration(diff);
  }

  // Format time duration into readable format (days, hours, minutes)
  formatTimeDuration(milliseconds: number): string {
    if (milliseconds < 0) return '0d 0h 0m 0s';

    const days = Math.floor(milliseconds / (1000 * 60 * 60 * 24));
    const hours = Math.floor((milliseconds % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    const minutes = Math.floor((milliseconds % (1000 * 60 * 60)) / (1000 * 60));
    const seconds = Math.floor((milliseconds % (1000 * 60)) / 1000);

    return `${days}d ${hours}h ${minutes}m ${seconds}s`;
  }

  private startContestCountdown(): void {
    this.updateContestTimeLeft();

    if (this.contestCountdownIntervalId) {
      clearInterval(this.contestCountdownIntervalId);
    }

    this.contestCountdownIntervalId = setInterval(() => {
      this.updateContestTimeLeft();
    }, 1000);
  }

  private stopContestCountdown(): void {
    if (this.contestCountdownIntervalId) {
      clearInterval(this.contestCountdownIntervalId);
      this.contestCountdownIntervalId = null;
    }
  }

  private updateContestTimeLeft(): void {
    const endDateValue = this.addContentForm.get('end_date')?.value;
    if (!endDateValue) {
      this.contestTimeLeft = '0d 0h 0m 0s';
      return;
    }

    const endDateMs = new Date(endDateValue).getTime();
    if (Number.isNaN(endDateMs)) {
      this.contestTimeLeft = '0d 0h 0m 0s';
      return;
    }

    this.contestTimeLeft = this.formatTimeDuration(endDateMs - Date.now());
  }

  validateCurrentStep(): boolean {
    if (this.addContentForm.get('type')?.value === 'private') {
      this.autoAddAllowListNumber();
    }

    // For Step 1: Validate contest information fields
    if (this.currentStep === 1) {
      const requiredFields = [
        'name',
        'arabic_name',
        'details',
        'arabic_details',
        'gender',
        'location',
        'type',
        'kyc_tier',
        'sharingSetting',
        'number_of_winners',
        'format_of_contest',
        'start_date',
        'end_date',
        'prize_announce_date',
        'business_objective'
      ];

      if (this.addContentForm.get('is_age_restricted')?.value) {
        requiredFields.push('age');
      }

      // Add SDK fields to validation if user has SDK access
      if (this.hasAdvancedFields) {
        requiredFields.push('draw_type', 'no_of_users');
      }

      let isValid = true;
      requiredFields.forEach(field => {
        const control = this.addContentForm.get(field);
        if (control && !control.disabled) {
          if (typeof control.value === 'string' && !control.value.trim()) {
            control.setErrors({ required: true, whitespace: true });
            control.markAsTouched();
            isValid = false;
          } else if (control.invalid) {
            control.markAsTouched();
            isValid = false;
          }
        }
      });

      return isValid;
    }

    // For Step 2: Validate prizes
    if (this.currentStep === 2) {
      let isValid = true;
      this.prizes.controls.forEach((prize) => {
        const prizeGroup = prize as FormGroup;
        // Prizes loaded from a saved, copied or AI campaign carry only `required`,
        // so a name of spaces got through; hold every prize to the same rule.
        const name = prizeGroup.get('name');
        if (name && typeof name.value === 'string' && !name.value.trim()) {
          name.setErrors({ ...(name.errors || {}), required: true, whitespace: true });
        }
        if (prizeGroup.invalid) {
          prizeGroup.markAllAsTouched();
          isValid = false;
        }
      });

      // The code fields of a voucher or gift card are required too, but carry no
      // validators of their own (they depend on the code type), so they are only
      // caught here. Left to the publish step, the client got all the way to the
      // end before hearing about a field on this page.
      const poolProblems = this.prizePoolProblems();
      if (poolProblems.length) {
        this.prizes.controls.forEach((prize) => (prize as FormGroup).markAllAsTouched());
      }

      if (!isValid || poolProblems.length) {
        const lines = [
          ...(isValid ? [] : [this.translate.instant('addContests.prizeStep.required')]),
          ...poolProblems,
        ];
        this.modal.error({
          nzTitle: this.translate.instant('addContests.prizeStep.title'),
          // nzContent renders as HTML, so the lines are escaped and joined with <br>.
          nzContent: lines.map((l) => this.escapeHtml(l)).join('<br>'),
          nzCentered: true,
          nzClassName: 'contest-styled-modal contest-modal-error',
          nzOkText: this.translate.instant('common.ok'),
        });
        return false;
      }

      return true;
    }

    return true;
  }

  /** Prize names are typed by the client and go into modal HTML. */
  private escapeHtml(text: string): string {
    return String(text)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  hasUnsavedChanges(): boolean {
    return this.addContentForm?.dirty || false;
  }

  private markFormAsSaved(): void {
    this.campaignSaved = true;
    this.levelTypeSwitchPrompt?.destroy();
    this.addContentForm.markAsPristine();
    this.addContentForm.markAsUntouched();

    Object.values(this.addContentForm.controls).forEach((control) => {
      control.markAsPristine();
      control.markAsUntouched();
    });
  }

  // Set once the user has already confirmed a refresh in our own modal, so the browser's
  // "Reload site?" prompt doesn't ask the same question a second time.
  private reloadConfirmed = false;

  @HostListener('window:beforeunload', ['$event'])
  onBeforeUnload(event: BeforeUnloadEvent): void {
    if (!this.reloadConfirmed && this.hasUnsavedChanges()) {
      event.preventDefault();
      event.returnValue = '';
    }
  }

  @HostListener('window:keydown', ['$event'])
  onKeyDown(event: KeyboardEvent): void {
    const isRefresh = event.key === 'F5' || ((event.ctrlKey || event.metaKey) && event.key === 'r');
    if (isRefresh && this.hasUnsavedChanges()) {
      event.preventDefault();
      this.modal.confirm({
        nzTitle: 'Unsaved Changes',
        nzContent: 'You have unsaved changes. Are you sure you want to refresh this page?',
        nzOkText: 'Yes, Refresh',
        nzCancelText: 'Cancel',
        nzCentered: true,
        nzClassName: 'contest-styled-modal',
        nzOnOk: () => {
          this.reloadConfirmed = true;
          window.location.reload();
        },
        nzOnCancel: () => { }
      });
    }
  }

  confirmUnsavedChanges(): Promise<boolean> {
    return new Promise((resolve) => {
      this.modal.confirm({
        nzTitle: 'Unsaved Changes',
        nzContent: this.leavePageWarningMessage,
        nzOkText: 'Yes, Leave',
        nzCancelText: 'Cancel',
        nzCentered: true,
        nzClassName: 'contest-styled-modal',
        nzOnOk: () => resolve(true),
        nzOnCancel: () => resolve(false)
      });
    });
  }

}

